/**
 * Engraving component.
 *
 * 1. Checks if current page is a product page.
 * 2. Checks if current product is engravable.
 * 3. Inserts modal button trigger in the main product form buttons container.
 */
customElements.get('yo-engraving') ??
  customElements.define(
    'yo-engraving',
    class YO_Engraving extends HTMLElement {
      /** When true, preview auto-expands on mobile after typing. Default false. */
      static PREVIEW_AUTO_EXPAND_ON_MOBILE = false;

      static fonts = {
        'Arial Regular': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/01-Arial-Regular.woff2?v=1758098286',
        'Lucida Calligraphy':
          'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/02-Lucida-Calligraphy.woff2?v=1758098284',
        'Raleway Regular':
          'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/03-Raleway-Regular.woff2?v=1758098285',
        'Raleway Italic': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/04-Raleway-Italic.woff2?v=1758098285',
        'Noto Serif Regular':
          'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/05-Noto-Serif-Regular.woff2?v=1758098285',
        'Noto Serif Italic':
          'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/06-Noto-Serif-Italic.woff2?v=1758098285',
        'Mission Script': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/07-Mission-Script.woff2?v=1758098285',
        'Edwardian Script':
          'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/08-Edwardian-Script.woff2?v=1758098284',
        Angelina: 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/Angelina.TTF?v=1774771182',
        Lydian: 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/Lydian.TTF?v=1774771181',
        'DW Script': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/DWScript.otf?v=1774771181',
        Arial: 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/Arial.ttf?v=1774771182',
        'Zapf Chan': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/ZapfChan.TTF?v=1774771182',
        'Monotype Corsiva': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/MonotypeCorsiva.TTF?v=1774771182',
        Corsiva: 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/MonotypeCorsiva.TTF?v=1774771182',
        'Times NR': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/TNRI.ttf?v=1774771182',
        'Murray Hill': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/MurrayHill.TTF?v=1774771182',
        'Old English': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/09-Old-English.woff2?v=1776307107',
        'Park Avenue': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/10-Park-Avenue.woff2?v=1776307107',
        Souvenir: 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/12-Souvenir.woff2?v=1776307106',
        'Times New Roman Italic':
          'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/13-Times-New-Roman-Italic.woff2?v=1776307108',
        'Victorian Script':
          'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/14-Victorian-Script.woff2?v=1776307107',
        'Shelly Allegro': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/11-Shelly-Allegro.woff2?v=1776308208',
        Goudy: 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/15-Goudy.woff2?v=1776549685',
        'Times New Roman':
          'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/16-Times-New-Roman.woff2?v=1776549686',
        Block: 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/16-Times-New-Roman.woff2?v=1776549686',
        Script: 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/17-Snell-Roundhand-Black.woff2?v=1777843685',
        'Black Chancery': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/18-Black-Chancery-Regular.woff2?v=1783560438',
        'Copperplate Gothic': 'https://cdn.shopify.com/s/files/1/0749/6892/4471/files/19-Copperplate-Gothic-Std-31-BC.woff2?v=1783560431',
      };

      /**
       * 1. Initialize by moving the widget up the DOM tree
       * 2. Read engraving settings
       * 3. Declare some properties
       * 4. Make sure only one opener button exists
       * 5. Attach INPUT event listeners to text fields
       * 6. Attach CHANGE event listener to artwork radios
       * 7. Attach SUBMIT event listener to form
       */
      constructor() {
        super();

        this.ns = '[YO/Engraving]';

        // [1] Move up the DOM
        if (!this.classList.contains('initialized')) {
          document.body.append(this);
          this.classList.add('initialized');
        }

        // [2] Take engraving form settings from dataset
        const settings = JSON.parse(this.dataset.settings);

        // [3] Properties
        /** @type {{ vendor: string}} */
        this.product = JSON.parse(document.querySelector('[data-yo-product]').textContent);
        try {
          this.engravingVariants = JSON.parse(this.dataset.engravingVariants || '[]');
        } catch (_) {
          this.engravingVariants = [];
        }
        if (!Array.isArray(this.engravingVariants)) this.engravingVariants = [];
        this.selectedEngravingVariant = null;
        this.textLinesMode = this.dataset?.textLinesMode === 'true';

        this.form = this.querySelector('form');
        this.line1 = settings.line_1_max_length ?? 0;
        this.line2 = settings.line_2_max_length ?? 0;
        this.line3 = settings.line_3_max_length ?? 0;
        this.line4 = settings.line_4_max_length ?? 0;
        const hp = String(this.dataset.hasPrimaryLine || '').toLowerCase();
        this.hasPrimaryLine = hp === 'true' ? true : hp === 'false' ? false : settings.has_primary_line === true;
        let lockedPrimary = -1;
        const lockEl = this.querySelector('input[data-line-input="true"][data-line-is-primary="true"]');
        if (lockEl) {
          const n = parseInt(lockEl.dataset.lineIndex || '', 10);
          if (Number.isFinite(n) && n >= 1) lockedPrimary = n;
        } else {
          const lockedHidden = this.querySelector(
            'input[type="hidden"][name="Primary Line"][data-yo-primary-line-locked]',
          );
          if (lockedHidden) {
            const n = parseInt(lockedHidden.value, 10);
            if (Number.isFinite(n) && n >= 1) lockedPrimary = n;
          }
        }
        this._lockedPrimaryLineIndex = lockedPrimary;
        this._primaryAutoSet = false;
        this.hasArtwork = settings.has_artwork === true;
        this.fontList = settings.font_list;

        console.log(`${this.ns} Settings`, settings.preview_config);

        /** @type {HTMLElement} */
        this.overlay = this.querySelector('.yo-engraving__overlay');
        this.engravingQuote = this.querySelector('.yo-engraving-form__quote');
        this.engravingPrice = this.querySelector('.yo-engraving-form__quote__engraving');

        /** @type {HTMLButtonElement} */
        this.atcButton = this.querySelector('.yo-engraving__atc');
        /** @type {HTMLElement} */
        this.preview = this.querySelector('yo-engraving-preview');

        // [4] There must be only one opener
        document.querySelector('.yo-engraving-opener')?.remove();

        /** @type {HTMLButtonElement} */
        this.opener = document.getElementById('YOEngravingButton').content.firstElementChild;
        this.opener.addEventListener('click', this.open.bind(this));
        const params = typeof URLSearchParams !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const debugMode = params && params.get('debug') === '1';
        this.opener.hidden = this.product.vendor !== 'Terry Bear'; // Only TB uses the old button now
        console.log(`${this.ns} opener hidden check`, {
          vendor: this.product.vendor,
          debugMode,
          openerHidden: this.opener.hidden,
        });
        document.querySelector('.product-block product-form .product-form__buttons').appendChild(this.opener);

        // [5] Text inputs
        /** @type {HTMLInputElement[]} */
        this.textLines = this.querySelectorAll('input[data-line-input="true"], input[name^="Line "]');
        this.textLines.forEach((el) => el.addEventListener('input', this.textInput.bind(this)));
        this.textLines.forEach((el) => el.addEventListener('focus', this._onTextFocus.bind(this)));
        this.textLines.forEach((el) => el.addEventListener('blur', this._onTextBlur.bind(this)));

        // [6] Artwork + font change
        this.addEventListener('change', (event) => {
          if (event.target.matches('[name="Artwork"]')) {
            this.updateEngravingVariant();
          }
          if (
            event.target.matches('[name="Artwork"], [name="Font"], [name="Primary Line"], [name="Engraving color"]')
          ) {
            this.preview?.updatePreview();
          }
        });

        // [7] Form submit
        this.form.addEventListener('submit', this.formSubmit.bind(this));

        // Prevent pinch-zoom on page when widget is open (does not affect zoom lightbox pinch-to-zoom)
        this._syncWidgetOpenBodyClass();
        this._widgetOpenObserver = new MutationObserver(() => this._syncWidgetOpenBodyClass());
        this._widgetOpenObserver.observe(this, { attributes: true, attributeFilter: ['class'] });

        console.log(`${this.ns} Init`, this);
      }

      connectedCallback() {
        queueMicrotask(() => this._wireCustomerBackgroundInput());
      }

      _wireCustomerBackgroundInput() {
        const input = this.querySelector('.yo-engraving-form__bg-input');
        if (!input) return;
        const clear = this.querySelector('.yo-engraving-form__bg-clear');
        const bind = () => {
          const preview = this.querySelector('yo-engraving-preview');
          this.preview = preview;
          preview?.attachCustomerBackgroundInput?.(input, clear);
        };
        const preview = this.querySelector('yo-engraving-preview');
        if (preview && typeof preview.attachCustomerBackgroundInput === 'function') {
          bind();
          return;
        }
        customElements.whenDefined('yo-engraving-preview').then(bind);
      }

      _syncWidgetOpenBodyClass() {
        if (this.classList.contains('is-open')) {
          document.body.classList.add('yo-engraving-widget-open');
        } else {
          document.body.classList.remove('yo-engraving-widget-open');
        }
      }

      _onTextFocus() {
        if (!this.preview || !window.matchMedia('(max-width: 1023px)').matches) return;
        this.preview.classList.add('is-text-focused');
        this.preview.setTextFocusState?.(true);
        this._centerPreviewForTypingZoom();
        if (this.preview.classList.contains('is-collapsed')) return;
        this.preview.classList.add('is-collapsed');
        this.preview.dataset.userCollapsed = 'true';
      }

      _onTextBlur() {
        if (!this.preview) return;
        const self = this;
        setTimeout(function () {
          const stillFocused = self.textLines && Array.from(self.textLines).includes(document.activeElement);
          if (!stillFocused) {
            self.preview.classList.remove('is-text-focused');
            self.preview.setTextFocusState?.(false);
          }
        }, 0);
      }

      _centerPreviewForTypingZoom() {
        if (!this.preview || !window.matchMedia('(max-width: 1023px)').matches) return;
        const active = document.activeElement;
        if (!active || !Array.from(this.textLines || []).includes(active)) return;

        const rect = this.preview.getBoundingClientRect();
        const vv = window.visualViewport;
        const viewportTop = vv ? vv.offsetTop : 0;
        const viewportHeight = vv ? vv.height : window.innerHeight;
        const targetCenterY = viewportTop + viewportHeight / 2;
        const currentCenterY = rect.top + rect.height / 2;
        const deltaY = currentCenterY - targetCenterY;
        if (Math.abs(deltaY) < 10) return;
        window.scrollBy({ top: deltaY, behavior: 'smooth' });
      }

      disconnectedCallback() {
        this._widgetOpenObserver?.disconnect();
        document.body.classList.remove('yo-engraving-widget-open');
      }

      /**
       * Opens the form modal.
       *
       * @param {MouseEvent} event
       */
      open(event) {
        event?.preventDefault();

        document.documentElement.classList.add('overlay-open');
        this.classList.add('is-open');

        this.preview?.updatePreview();
        this.updateEngravingVariant();
        // Update variant ID
        /** @type {{ id: number }} */
        const selectedVariant = JSON.parse(document.querySelector('[data-selected-variant]')?.textContent ?? null);
        if (selectedVariant) this.form.querySelector('[name=id]').value = selectedVariant.id;

        // Update quantity
        this.querySelector('[name="quantity"]').value = document.querySelector(
          '.block--buy-buttons input[name="quantity"]',
        ).value;

        console.log(`${this.ns} Opened`, this);
      }

      /**
       * Closes the form modal.
       *
       * @param {MouseEvent} event
       */
      close(event) {
        event?.preventDefault();

        this.classList.remove('is-open');
        document.documentElement.classList.remove('overlay-open');

        console.log(`${this.ns} Closed`, this);
      }

      /**
       * Handles text line inputs.
       *
       * 1. Update text field character count
       * 2. Update engraving add-on variant
       *
       * @param {KeyboardEvent} event
       */
      textInput(event) {
        /** @type {HTMLInputElement} */
        const input = event.target;

        // [1] Update counter
        /** @type {HTMLElement} */
        const counter = input.parentElement.querySelector('.yo-engraving-form__counter');
        counter.textContent =
          input.value.length > 0
            ? `${input.maxLength - input.value.length} characters left`
            : counter.dataset.defaultText;
        // console.log(`${this.ns} Updated`, counter);

        // [2] Update variant
        this.updateEngravingVariant();
        // [3] Live preview
        if (this._previewDebounce) clearTimeout(this._previewDebounce);
        this.preview?.updatePreview({ immediate: true });
        clearTimeout(this._typingZoomCenterDebounce);
        if (window.matchMedia('(max-width: 1023px)').matches) {
          this._typingZoomCenterDebounce = setTimeout(() => {
            this.preview?.setTextFocusState?.(true);
            this._centerPreviewForTypingZoom();
          }, 80);
        }

        if (this.constructor.PREVIEW_AUTO_EXPAND_ON_MOBILE) {
          clearTimeout(this._previewExpandDebounce);
          this._previewExpandDebounce = setTimeout(() => {
            if (this.preview?.dataset.userCollapsed === 'true') return;
            if (this.preview && window.matchMedia('(max-width: 1023px)').matches) {
              this.preview.classList.remove('is-collapsed');
            }
          }, 1000);
        }

        if (
          this.hasPrimaryLine &&
          this._lockedPrimaryLineIndex < 1 &&
          !this._primaryAutoSet &&
          input.value.trim().length > 0
        ) {
          const othersEmpty = Array.from(this.textLines).every((el) => el === input || el.value.trim().length === 0);
          if (othersEmpty) {
            const lineNum = parseInt(input.dataset.lineIndex || input.name.replace(/^Line\s*/, ''), 10);
            if (!isNaN(lineNum)) {
              const radio = this.form?.querySelector(`[name="Primary Line"][type="radio"][value="${lineNum}"]`);
              if (radio) {
                radio.checked = true;
                this._primaryAutoSet = true;
                this.preview?.updatePreview();
              }
            }
          }
        }
      }

      /**
       * Updates associated engraving product variant.
       */
      updateEngravingVariant() {
        clearTimeout(this._variantUpdater);
        this._variantUpdater = setTimeout(() => {
          const textLineCount = Array.from(this.textLines).filter((el) => el.value.trim() !== '').length;
          const artwork = this.querySelector('[name="Artwork"]:checked')?.value ?? 'No Artwork';
          const wantsArtwork = artwork !== 'No Artwork';

          let variantTitle = textLineCount > 1 ? `${textLineCount} Text Lines / ` : `${textLineCount} Text Line / `;
          variantTitle += artwork === 'No Artwork' ? 'No Artwork' : 'With Artwork';
          variantTitle = variantTitle.replace('0 Text Line', 'No Text Line');

          this.selectedEngravingVariant = this.engravingVariants.find((v) => v.title === variantTitle);
          if (
            !this.selectedEngravingVariant &&
            Array.isArray(this.engravingVariants) &&
            this.engravingVariants.length
          ) {
            const parsed = this.engravingVariants
              .map((v) => {
                const title = String(v?.title || '');
                const withArtwork = title.includes('/ With Artwork');
                const noArtwork = title.includes('/ No Artwork');
                let lines = -1;
                if (title.startsWith('No Text Line /')) {
                  lines = 0;
                } else {
                  const m = title.match(/^(\d+)\s+Text Line(?:s)?\s+\//);
                  if (m) lines = parseInt(m[1], 10);
                }
                if (!Number.isFinite(lines) || lines < 0) return null;
                if (wantsArtwork && !withArtwork) return null;
                if (!wantsArtwork && !noArtwork) return null;
                return { v, lines };
              })
              .filter(Boolean);

            if (parsed.length) {
              if (this.textLinesMode) {
                parsed.sort((a, b) => b.lines - a.lines);
              } else {
                parsed.sort((a, b) => {
                  const da = Math.abs(a.lines - textLineCount);
                  const db = Math.abs(b.lines - textLineCount);
                  if (da !== db) return da - db;
                  return b.lines - a.lines;
                });
              }
              this.selectedEngravingVariant = parsed[0].v;
            }
          }
          if (!this.selectedEngravingVariant && this.textLinesMode && this.engravingVariants.length) {
            this.selectedEngravingVariant = this.engravingVariants[0];
          }

          if (this.product.vendor === 'Forever Pets') {
            if (this.engravingQuote) this.engravingQuote.hidden = true;
          } else if (this.engravingPrice) {
            this.engravingPrice.textContent =
              this.selectedEngravingVariant && this.product.vendor !== 'Forever Pets'
                ? this.selectedEngravingVariant.price
                : '$0.00';
          }

          if (this.atcButton) this.atcButton.disabled = this.selectedEngravingVariant === undefined;

          console.log(`${this.ns} Selected engraving variant:`, this.selectedEngravingVariant);
        }, 500);
      }

      /**
       * Handles ATC form submission.
       *
       * @param {FormEvent} event
       */
      async formSubmit(event) {
        event.preventDefault();

        this.classList.add('is-busy');
        this.atcButton.classList.add('loading');

        const bundleId = `${Math.floor(Math.random() * 9999)}`.padStart(4, '0');
        const formData = new FormData(event.target);
        const uploadUrl = (this.dataset.uploadUrl || '').trim();
        const uploadFileToUrl = async (file, filename) => {
          if (!uploadUrl || !file) return '';
          try {
            const fd = new FormData();
            fd.append('file', file, filename || 'upload.bin');
            const res = await fetch(uploadUrl, { method: 'POST', body: fd });
            if (!res.ok) throw new Error(res.statusText);
            const data = await res.json();
            return (data && (data.url ?? data.image_url ?? data.file_url)) || '';
          } catch (err) {
            console.warn(`${this.ns} Upload failed`, err);
            return '';
          }
        };

        let imageUrl = '';
        if (uploadUrl && this.preview && typeof this.preview.getExportBlob === 'function') {
          try {
            const blob = await this.preview.getExportBlob();
            if (blob) {
              imageUrl = await uploadFileToUrl(blob, 'engraving-preview.png');
            }
          } catch (err) {
            console.warn(`${this.ns} Upload failed`, err);
          }
        }
        let uploadedSourceImageUrl = '';
        const uploadedSourceFile = this.querySelector('.yo-engraving-form__bg-input')?.files?.[0];
        if (uploadedSourceFile) {
          uploadedSourceImageUrl = await uploadFileToUrl(
            uploadedSourceFile,
            uploadedSourceFile.name || 'engraving-source',
          );
        }

        const payload = { items: [] };

        // Only TB uses the personalization add-on product now
        if (this.product.vendor === 'Terry Bear') {
          payload.items.push({
            id: this.selectedEngravingVariant.id,
            quantity: formData.get('quantity'),
            properties: { _bundleId: bundleId },
          });
        }

        const mainProduct = {
          id: formData.get('id'),
          quantity: formData.get('quantity'),
          properties: {
            _bundleId: bundleId,
            _agreement: new Date().toISOString(),
          },
        };

        formData.delete('id');
        formData.delete('quantity');
        const textLinePropertyNames = new Set(
          Array.from(this.textLines || [])
            .map((input) => input.name || input.getAttribute('name'))
            .filter(Boolean),
        );
        for (const [k, v] of formData.entries()) {
          if (this.textLinesMode && textLinePropertyNames.has(String(k))) continue;
          mainProduct.properties[k] = k === 'Artwork' && v === 'No Artwork' ? '' : v;
        }
        this.textLines.forEach((input) => {
          const name = input.name || input.getAttribute('name');
          if (!name) return;
          const value = (input.value || '').trim();
          if (!this.textLinesMode) {
            mainProduct.properties[name] = value;
            return;
          }
          const rawLabel = input
            .closest('.yo-engraving-form__field')
            ?.querySelector('.yo-engraving-form__label')?.textContent;
          const label = (rawLabel || '').trim();
          const position = (input.dataset?.linePositionProperty || '').trim();
          const key = [position, label || name].filter(Boolean).join(' ');
          mainProduct.properties[key] = value;
        });
        const specialInstructions = (mainProduct.properties['Special instructions'] || '').trim();
        if (specialInstructions) {
          mainProduct.properties['Special instructions'] = specialInstructions;
        } else {
          delete mainProduct.properties['Special instructions'];
        }
        if (imageUrl) {
          mainProduct.properties['_engraving-image-url'] = imageUrl;
        }
        if (uploadedSourceImageUrl) {
          mainProduct.properties['View uploaded image'] = uploadedSourceImageUrl;
        }

        payload.items.push(mainProduct);

        try {
          const response = await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await response.json();
          this.close();
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          console.log(`${this.ns} Response`, data);
        } catch (error) {
          console.log(`${this.ns} ERROR`, error);
        } finally {
          this.classList.remove('is-busy');
          this.atcButton.classList.remove('loading');
          this.form.reset();
        }
      }
    },
  );

/**
 * Live Preview Config is defined in assets/yo-engraving-config.js.
 */
/**
 * Engraving preview component.
 */
customElements.get('yo-engraving-preview') ??
  customElements.define(
    'yo-engraving-preview',
    class YO_EngravingPreview extends HTMLElement {
      static get fonts() {
        const Ctor = customElements.get('yo-engraving');
        return (Ctor && Ctor.fonts) || {};
      }

      constructor() {
        super();

        this.ns = '[YO/Engraving/Preview]';
        const params = typeof URLSearchParams !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const liveDebug = params && (params.get('debug') === '1' || params.get('live-debug') === '1');
        this.liveDebug = liveDebug;

        /** @type {HTMLButtonElement} */
        this.toggle = this.querySelector('.yo-engraving-preview__toggle');
        this._updateToggleLabel();
        this.toggle.addEventListener('click', (event) => {
          event.preventDefault();
          this.classList.toggle('is-collapsed');
          if (this.classList.contains('is-collapsed')) this.dataset.userCollapsed = 'true';
          this._updateToggleLabel();
        });
        this._toggleLabelObserver = new MutationObserver(() => this._updateToggleLabel());
        this._toggleLabelObserver.observe(this, { attributes: true, attributeFilter: ['class'] });

        this._initZoomLightbox();

        /** @type {HTMLButtonElement} */
        this.downloadBtn = this.querySelector('.yo-engraving-preview__download');
        if (this.downloadBtn) {
          this.downloadBtn.addEventListener('click', () => this._downloadImage());
          if (!liveDebug) this.downloadBtn.hidden = true;
        }
        /** @type {HTMLButtonElement} */
        this.testUploadBtn = this.querySelector('.yo-engraving-preview__test-upload');
        if (this.testUploadBtn) {
          this.testUploadBtn.addEventListener('click', () => this._testUpload());
          this.testUploadBtn.hidden = true;
        }

        this.config = this._buildConfig();

        if (liveDebug) setTimeout(() => this._loadConfigDebug(), 0);
        this._syncTextFillToForm();
        this._fontCache = new Set();
        this._artworkImageCache = new Map();
        this._backgroundImageCache = new Map();
        this._backgroundUserObjectUrl = null;
        this._drawScheduled = null;
        this._resizeObserver = null;
        this._previewRequestId = 0;
        this._fontLoadPromises = new Map();
        const hasLiveImages = !!this.dataset.livePreviewImages;
        this._livePreviewEnabled = this.dataset.enableLivePreview === 'true' || this.liveDebug;

        if (!this._livePreviewEnabled) this.classList.add('live-preview-disabled');
        if (this.liveDebug && hasLiveImages) this._applyLivePreviewImagesForDebug();
        if (this._livePreviewEnabled) this._initCanvases();

        console.log(`${this.ns} Init`, this);
      }

      _loadConfigDebug() {
        if (!this.liveDebug) return;
        const init = () => {
          if (typeof window !== 'undefined' && window.YO_ENGRAVING_CONFIG_DEBUG?.init) {
            window.YO_ENGRAVING_CONFIG_DEBUG.init(this);
          }
        };
        if (typeof window !== 'undefined' && window.YO_ENGRAVING_CONFIG_DEBUG?.init) {
          init();
          return;
        }
        const src = this.dataset.configDebugSrc || this.closest('yo-engraving')?.dataset?.configDebugSrc;
        if (!src || typeof document === 'undefined') return;
        if (!window.YO_ENGRAVING_CONFIG_DEBUG_LOADING) {
          window.YO_ENGRAVING_CONFIG_DEBUG_LOADING = new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[data-yo-engraving-config-debug="${src}"]`);
            if (existing) {
              existing.addEventListener('load', resolve, { once: true });
              existing.addEventListener('error', reject, { once: true });
              return;
            }
            const script = document.createElement('script');
            script.src = src;
            script.defer = true;
            script.dataset.yoEngravingConfigDebug = src;
            script.addEventListener('load', resolve, { once: true });
            script.addEventListener('error', reject, { once: true });
            document.head.appendChild(script);
          });
        }
        window.YO_ENGRAVING_CONFIG_DEBUG_LOADING.then(init).catch((err) => {
          console.warn(`${this.ns} Could not load debug config generator`, err);
        });
      }

      /**
       * Large Image
       * @param {HTMLInputElement | null} input
       * @param {HTMLButtonElement | null} clearBtn
       */
      attachCustomerBackgroundInput(input, clearBtn) {
        if (!input || this._customerBgInputBound) return;
        this._customerBgInputBound = true;
        this._customerBgClearBtn = clearBtn;
        input.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (this._backgroundUserObjectUrl) {
            const prev = this._backgroundUserObjectUrl;
            this._backgroundImageCache.delete(prev);
            URL.revokeObjectURL(prev);
            this._backgroundUserObjectUrl = null;
          }
          if (file) {
            this._backgroundUserObjectUrl = URL.createObjectURL(file);
            this.classList.remove('live-preview-disabled');
          } else if (!this._livePreviewEnabled) {
            this.classList.add('live-preview-disabled');
          }
          if (this._customerBgClearBtn) this._customerBgClearBtn.hidden = !file;
          this.updatePreview();
        });
        clearBtn?.addEventListener('click', () => {
          input.value = '';
          if (this._backgroundUserObjectUrl) {
            const prev = this._backgroundUserObjectUrl;
            this._backgroundImageCache.delete(prev);
            URL.revokeObjectURL(prev);
            this._backgroundUserObjectUrl = null;
          }
          if (this._customerBgClearBtn) this._customerBgClearBtn.hidden = true;
          if (!this._livePreviewEnabled) this.classList.add('live-preview-disabled');
          this.updatePreview();
        });
      }

      _applyLivePreviewImagesForDebug() {
        try {
          const urls = JSON.parse(this.dataset.livePreviewImages || '[]');
          if (!Array.isArray(urls) || urls.length === 0) return;
          const items = this.querySelectorAll('.yo-engraving-preview__item');
          items.forEach((item, i) => {
            const url = urls[i];
            if (!url) return;
            item.querySelectorAll('picture > source').forEach((s) => s.remove());
            const img = item.querySelector('img');
            if (img) {
              img.removeAttribute('srcset');
              img.src = url;
            }
          });
        } catch (e) {
          console.warn(`${this.ns} Could not apply live preview images for debug`, e);
        }
      }

      _syncTextFillToForm() {
        const fill = this.config?.text?.fillColor ?? '#1a1a1a';
        this.closest('yo-engraving')?.style.setProperty('--yo-engraving-text-fill', fill);
      }

      _getSelectedEngravingColor() {
        const parent = this.closest('yo-engraving');
        const selected = parent?.querySelector('[name="Engraving color"]:checked');
        const color = (selected?.value || '').trim();
        return color || '';
      }

      _updateToggleLabel() {
        if (!this.toggle) return;
        const collapsed = this.classList.contains('is-collapsed');
        this.toggle.textContent = collapsed ? 'Enlarge Preview' : 'Collapse Preview';
        this.toggle.setAttribute('aria-label', collapsed ? 'Enlarge Preview' : 'Collapse Preview');
      }

      /**
       * Keep mobile text-focus zoom anchored around engraving zone center.
       * @param {boolean} enabled
       */
      setTextFocusState(enabled) {
        if (!window.matchMedia('(max-width: 1023px)').matches) return;
        const slider = this.querySelector('.gridy-slider');
        if (!slider) return;
        if (!enabled) {
          slider.style.removeProperty('transform');
          return;
        }

        const zone = this.config?.zone || {};
        const zoneX = Number(zone.x);
        const zoneY = Number(zone.y);
        const zoneW = Number(zone.width);
        const zoneH = Number(zone.height);
        const centerX = Number.isFinite(zoneX) && Number.isFinite(zoneW) ? zoneX + zoneW / 2 : 0.5;
        const centerY = Number.isFinite(zoneY) && Number.isFinite(zoneH) ? zoneY + zoneH / 2 : 0.5;
        const shiftX = (0.5 - centerX) * 100;
        const shiftY = (0.5 - centerY) * 100;
        const maxScaleByZoneW = Number.isFinite(zoneW) && zoneW > 0 ? 1 / zoneW : 2.2;
        const maxScaleByZoneH = Number.isFinite(zoneH) && zoneH > 0 ? 1 / zoneH : 2.2;
        const focusScale = Math.max(1, Math.min(2.2, maxScaleByZoneW * 0.92, maxScaleByZoneH * 0.92));
        slider.style.transform = `translate(calc(-50% + ${shiftX.toFixed(2)}%), calc(-50% + ${shiftY.toFixed(2)}%)) scale(${focusScale.toFixed(3)})`;
      }

      /** @returns {HTMLCanvasElement|null} Canvas inside the currently active item. */
      get canvas() {
        return this.querySelector('.yo-engraving-preview__item.active .yo-engraving-preview__canvas');
      }

      _initCanvases() {
        this.querySelectorAll('.yo-engraving-preview__canvas').forEach((c) => {
          this._setupCanvasEl(c);
        });
        this._observeResize();
      }

      _initZoomLightbox() {
        const parent = this.closest('yo-engraving');
        const zoomModalId = parent?.dataset?.zoomModalId;
        if (!zoomModalId) return;
        const zoomModal = document.getElementById(zoomModalId);
        if (!zoomModal) return;
        const wrap = zoomModal.querySelector('.yo-engraving-zoom__wrap');
        const zoomImg = zoomModal.querySelector('.yo-engraving-zoom__img');
        const zoomBody = zoomModal.querySelector('.yo-engraving-zoom__body');
        if (!wrap || !zoomImg || !zoomBody) return;

        let zoomObjectUrl = null;
        this.addEventListener('click', async (e) => {
          if (e.target.closest('button')) return;
          if (!this.contains(e.target)) return;
          const isMobile = window.matchMedia('(max-width: 1023px)').matches;
          if (isMobile && this.classList.contains('is-collapsed')) {
            this.classList.remove('is-collapsed');
            delete this.dataset.userCollapsed;
            return;
          }
          try {
            const blob = await this._getExportBlob();
            if (!blob) {
              console.warn(`${this.ns} Zoom: no export blob (background + engraving)`);
              return;
            }
            if (zoomObjectUrl) URL.revokeObjectURL(zoomObjectUrl);
            zoomObjectUrl = URL.createObjectURL(blob);
            zoomImg.src = zoomObjectUrl;
            zoomImg.alt = 'Engraving preview';
            wrap.dataset.zoomScale = '1';
            wrap.style.transform = 'scale(1)';
            document.body.classList.add('yo-engraving-zoom-open');
            if (typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers?.showModalOrDrawer) {
              Woolman.ModalsAndDrawers.showModalOrDrawer(zoomModalId);
            } else {
              zoomModal.classList.add('is-open');
              document.body.classList.add('overflow-hidden');
              document.documentElement.classList.add('overlay-open');
            }
          } catch (err) {
            console.warn(`${this.ns} Zoom: could not export preview`, err);
          }
        });

        const redrawPreviewAfterZoomClose = () => {
          const wasZoomOpen = document.body.classList.contains('yo-engraving-zoom-open');
          document.body.classList.remove('yo-engraving-zoom-open');
          if (!wasZoomOpen) return;
          if (zoomObjectUrl) {
            URL.revokeObjectURL(zoomObjectUrl);
            zoomObjectUrl = null;
          }
          requestAnimationFrame(() => {
            this._setupCanvas();
            this.updatePreview();
            requestAnimationFrame(() => {
              this._setupCanvas();
              this.updatePreview();
            });
          });
        };
        zoomModal.addEventListener('click', (e) => {
          // When the theme's modal helper is used, `is-open` may not be applied.
          // We always toggle `yo-engraving-zoom-open` in our own code, so use that as the source of truth.
          if (!document.body.classList.contains('yo-engraving-zoom-open')) return;
          if (wrap.contains(e.target)) return;
          redrawPreviewAfterZoomClose();
          if (typeof Woolman !== 'undefined' && Woolman.ModalsAndDrawers?.closeModalOrDrawer) {
            Woolman.ModalsAndDrawers.closeModalOrDrawer(zoomModalId);
          } else {
            zoomModal.classList.remove('is-open');
            document.body.classList.remove('overflow-hidden');
            document.documentElement.classList.remove('overlay-open');
          }
        });
        const zoomOpenObserver = new MutationObserver((mutations) => {
          if (!zoomModal.classList.contains('is-open')) redrawPreviewAfterZoomClose();
        });
        zoomOpenObserver.observe(zoomModal, { attributes: true, attributeFilter: ['class'] });

        const zoomMin = 1;
        const zoomMax = 4;
        const zoomStep = 0.25;
        const setZoomScale = (scale) => {
          const s = Math.min(zoomMax, Math.max(zoomMin, scale));
          wrap.dataset.zoomScale = s;
          wrap.style.transform = `scale(${s})`;
        };

        zoomBody.addEventListener(
          'wheel',
          (e) => {
            if (!zoomModal.classList.contains('is-open')) return;
            e.preventDefault();
            const current = parseFloat(wrap.dataset.zoomScale) || 1;
            const next = e.deltaY > 0 ? current - zoomStep : current + zoomStep;
            setZoomScale(next);
          },
          { passive: false },
        );

        let pinchStartDistance = 0;
        let pinchStartScale = 1;
        const getTouchDistance = (touches) => {
          if (touches.length < 2) return 0;
          const a = touches[0];
          const b = touches[1];
          return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        };
        zoomBody.addEventListener(
          'touchstart',
          (e) => {
            if (!zoomModal.classList.contains('is-open') || e.touches.length !== 2) return;
            e.preventDefault();
            pinchStartDistance = getTouchDistance(e.touches);
            pinchStartScale = parseFloat(wrap.dataset.zoomScale) || 1;
          },
          { passive: false },
        );
        zoomBody.addEventListener(
          'touchmove',
          (e) => {
            if (!zoomModal.classList.contains('is-open') || e.touches.length !== 2) return;
            e.preventDefault();
            const d = getTouchDistance(e.touches);
            if (pinchStartDistance > 0) setZoomScale((d / pinchStartDistance) * pinchStartScale);
          },
          { passive: false },
        );
        zoomBody.addEventListener('touchend', (e) => {
          if (e.touches.length < 2) pinchStartDistance = 0;
        });
      }

      _getConfigOverrideForEditor() {
        const out = {};
        try {
          if (typeof localStorage !== 'undefined') {
            const stored = localStorage.getItem('yo-engraving-preview-config');
            if (stored) this._deepMerge(out, JSON.parse(stored));
          }
        } catch (_) {}
        try {
          if (typeof window !== 'undefined' && window.YO_ENGRAVING_PREVIEW_OVERRIDE) {
            this._deepMerge(out, window.YO_ENGRAVING_PREVIEW_OVERRIDE);
          }
        } catch (_) {}
        return out;
      }

      _setConfigOverrideFromEditor(obj) {
        try {
          if (typeof localStorage !== 'undefined') {
            if (obj == null || Object.keys(obj).length === 0) {
              localStorage.removeItem('yo-engraving-preview-config');
            } else {
              localStorage.setItem('yo-engraving-preview-config', JSON.stringify(obj));
            }
          }
        } catch (_) {}
        if (typeof window !== 'undefined') {
          window.YO_ENGRAVING_PREVIEW_OVERRIDE = obj && Object.keys(obj).length > 0 ? obj : undefined;
        }
      }

      _getValueAtPath(obj, path) {
        const parts = path.split('.');
        let v = obj;
        for (const p of parts) {
          if (v == null) return undefined;
          v = v[p];
        }
        return v;
      }

      _setValueAtPath(obj, path, value) {
        const parts = path.split('.');
        let cur = obj;
        for (let i = 0; i < parts.length - 1; i++) {
          const p = parts[i];
          const next = parts[i + 1];
          const nextIsArrayIndex = /^\d+$/.test(next);
          if (!(p in cur) || cur[p] === null || typeof cur[p] !== 'object') {
            cur[p] = nextIsArrayIndex ? [] : {};
          }
          cur = cur[p];
        }
        cur[parts[parts.length - 1]] = value;
      }

      /**
       * How many Line N inputs exist for this product (metafields). Uses `data-line-field-count` from Liquid, else DOM.
       */
      _getEngravingLineFieldCount() {
        const parent = this.closest('yo-engraving');
        if (!parent) return 6;
        const textLinesMode = parent.dataset?.textLinesMode === 'true';
        const raw = parent.dataset?.lineFieldCount;
        if (raw != null && raw !== '') {
          const n = parseInt(raw, 10);
          if (Number.isFinite(n) && n >= 1) return textLinesMode ? n : Math.min(6, n);
        }
        const inputs = parent.querySelectorAll('input[data-line-input="true"], input[name^="Line "]');
        if (inputs.length) return textLinesMode ? inputs.length : Math.min(6, inputs.length);
        return textLinesMode ? 0 : 6;
      }

      /** Matches Liquid `has_primary_line` / shopper “Primary” line UI. */
      _hasEngravingPrimaryLine() {
        const parent = this.closest('yo-engraving');
        if (!parent) return false;
        if (
          parent.querySelector(
            'input[data-line-input="true"][data-line-is-primary="true"], input[type="hidden"][name="Primary Line"][data-yo-primary-line-locked], .yo-engraving-form__primary-fixed',
          )
        ) {
          return true;
        }
        const ds = String(parent.dataset?.hasPrimaryLine || '').toLowerCase();
        if (ds === 'true') return true;
        if (ds === 'false') return false;
        if (typeof parent.hasPrimaryLine === 'boolean') return parent.hasPrimaryLine;
        try {
          const s = JSON.parse(parent.dataset?.settings || '{}');
          return s.has_primary_line === true;
        } catch (_) {
          return false;
        }
      }

      _isPrimarySlot(primarySlotIndex, slotIndex) {
        if (Array.isArray(primarySlotIndex)) return primarySlotIndex.includes(slotIndex);
        const n = Number(primarySlotIndex);
        if (!Number.isFinite(n) || n < 0) return slotIndex === 0;
        return slotIndex === n;
      }

      /**
       * `data-preview-config` JSON from engraving preview metaobject, or legacy flat preview config only.
       */
      _parseDataPreviewConfig() {
        try {
          const raw = this.dataset.previewConfig;
          if (!raw) return { mergeSource: null };
          const parsed = JSON.parse(raw);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return { mergeSource: null };
          }
          if (
            parsed.preview_config != null &&
            typeof parsed.preview_config === 'object' &&
            !Array.isArray(parsed.preview_config)
          ) {
            return { mergeSource: parsed.preview_config };
          }
          const rest = { ...parsed };
          delete rest.enable_image_upload;
          delete rest.enableImageUpload;
          return { mergeSource: rest };
        } catch (_) {
          return { mergeSource: null };
        }
      }

      _normalizePreviewConfigVersion(config) {
        if (!config || typeof config !== 'object' || Array.isArray(config)) return config;
        const out = structuredClone(config);
        const getConfigVersion =
          typeof window !== 'undefined' && window.YO_ENGRAVING_CONFIG?.getConfigVersion
            ? window.YO_ENGRAVING_CONFIG.getConfigVersion
            : (cfg) =>
                cfg && typeof cfg === 'object' && typeof cfg.version === 'string' && cfg.version.trim()
                  ? cfg.version.trim()
                  : 'v0';
        out.version = getConfigVersion(out);
        return out;
      }

      _buildConfig() {
        let base = structuredClone(YO_ENGRAVING_PREVIEW_DEFAULTS);
        const parent = this.closest('yo-engraving');
        if (parent && parent.dataset.settings) {
          try {
            const s = JSON.parse(parent.dataset.settings);
            if (s.preview_config && typeof s.preview_config === 'object')
              this._deepMerge(base, this._normalizePreviewConfigVersion(s.preview_config));
          } catch (_) {}
        }
        try {
          const { mergeSource } = this._parseDataPreviewConfig();
          if (mergeSource) this._deepMerge(base, this._normalizePreviewConfigVersion(mergeSource));
        } catch (e) {
          console.warn(`${this.ns} Bad preview config`, e);
        }
        if (this.liveDebug) {
          try {
            if (typeof localStorage !== 'undefined') {
              const stored = localStorage.getItem('yo-engraving-preview-config');
              if (stored) this._deepMerge(base, this._normalizePreviewConfigVersion(JSON.parse(stored)));
            }
          } catch (e) {
            console.warn(`${this.ns} Bad localStorage yo-engraving-preview-config`, e);
          }
          try {
            if (typeof window !== 'undefined' && window.YO_ENGRAVING_PREVIEW_OVERRIDE) {
              this._deepMerge(base, this._normalizePreviewConfigVersion(window.YO_ENGRAVING_PREVIEW_OVERRIDE));
            }
          } catch (e) {
            console.warn(`${this.ns} Bad YO_ENGRAVING_PREVIEW_OVERRIDE`, e);
          }
        }
        this._normalizeConfigPer1000(base);
        if (base.corners && typeof base.corners === 'object') {
          base.corners = {
            topLeft: { x: Number(base.corners.topLeft?.x) || 0, y: Number(base.corners.topLeft?.y) || 0 },
            topRight: { x: Number(base.corners.topRight?.x) || 0, y: Number(base.corners.topRight?.y) || 0 },
            bottomRight: { x: Number(base.corners.bottomRight?.x) || 0, y: Number(base.corners.bottomRight?.y) || 0 },
            bottomLeft: { x: Number(base.corners.bottomLeft?.x) || 0, y: Number(base.corners.bottomLeft?.y) || 0 },
          };
        }
        {
          const d = YO_ENGRAVING_PREVIEW_DEFAULTS.circularText;
          const ct = base.circularText && typeof base.circularText === 'object' ? base.circularText : {};
          base.circularText = {
            enabled: Boolean(ct.enabled),
            showDebugLines: Boolean(ct.showDebugLines),
            centerX: Number.isFinite(Number(ct.centerX)) ? Number(ct.centerX) : d.centerX,
            centerY: Number.isFinite(Number(ct.centerY)) ? Number(ct.centerY) : d.centerY,
            radius: Number.isFinite(Number(ct.radius)) ? Number(ct.radius) : d.radius,
            fontSize: Number.isFinite(Number(ct.fontSize)) ? Number(ct.fontSize) : d.fontSize,
          };
        }
        {
          const d = YO_ENGRAVING_PREVIEW_DEFAULTS.fieldPlacements;
          const fp = base.fieldPlacements && typeof base.fieldPlacements === 'object' ? base.fieldPlacements : {};
          const defSlots = Array.isArray(d.slots) ? d.slots : [];
          const slotsIn = Array.isArray(fp.slots) ? fp.slots : [];
          const lineN = this._getEngravingLineFieldCount();
          const textLinesModeBuild = this.closest('yo-engraving')?.dataset?.textLinesMode === 'true';
          const slotCap =
            typeof window !== 'undefined' && window.YO_ENGRAVING_CONFIG?.fieldPlacementSlotCap
              ? window.YO_ENGRAVING_CONFIG.fieldPlacementSlotCap
              : typeof YO_ENGRAVING_FIELD_PLACEMENT_SLOT_CAP !== 'undefined'
                ? YO_ENGRAVING_FIELD_PLACEMENT_SLOT_CAP
                : 40;
          const maxSlots = textLinesModeBuild
            ? Math.max(lineN, slotsIn.length, 1)
            : Math.min(slotCap, Math.max(lineN, slotsIn.length, 1));
          const slotFallback = (i) => ({
            x: 0.5,
            y: Math.min(0.9, 0.22 + i * 0.035),
            maxWidth: 0.4,
            skewX: 0,
            skewY: 0,
          });
          const slots = [];
          for (let i = 0; i < maxSlots; i++) {
            const def = defSlots[i] || slotFallback(i);
            const o = slotsIn[i] && typeof slotsIn[i] === 'object' ? slotsIn[i] : {};
            const normalizeFraction = (value, fallback) => {
              let n = Number(value);
              if (!Number.isFinite(n)) n = Number(fallback) || 0;
              return Math.abs(n) > 1 ? n / 1000 : n;
            };
            slots.push({
              x: normalizeFraction(o.x, def.x),
              y: normalizeFraction(o.y, def.y),
              maxWidth: normalizeFraction(o.maxWidth, def.maxWidth),
              skewX: normalizeFraction(o.skewX, def.skewX),
              skewY: normalizeFraction(o.skewY, def.skewY),
            });
          }
          base.fieldPlacements = {
            enabled: Boolean(fp.enabled),
            showDebugLines: Boolean(fp.showDebugLines),
            fontSize: Number.isFinite(Number(fp.fontSize)) ? Number(fp.fontSize) : d.fontSize,
            primaryFontSize: Number.isFinite(Number(fp.primaryFontSize))
              ? Number(fp.primaryFontSize)
              : d.primaryFontSize,
            slots,
          };
        }
        if (typeof window !== 'undefined' && typeof window.YO_ENGRAVING_COMPANION_normalizeBuild === 'function') {
          window.YO_ENGRAVING_COMPANION_normalizeBuild(base);
        }
        {
          const parentForCompanion = this.closest('yo-engraving');
          const textLinesModeBuild = parentForCompanion?.dataset?.textLinesMode === 'true';
          const hasPositionedTextLines =
            typeof window !== 'undefined' &&
            typeof window.YO_ENGRAVING_COMPANION_hasNonDefaultLineLocation === 'function'
              ? window.YO_ENGRAVING_COMPANION_hasNonDefaultLineLocation(parentForCompanion)
              : false;
          if (textLinesModeBuild && hasPositionedTextLines && base.companion && typeof base.companion === 'object') {
            base.companion.enabled = true;
          }
        }
        if (base.backgroundImage && typeof base.backgroundImage === 'object') {
          const bg = base.backgroundImage;
          const cornersComplete = (cr) =>
            cr &&
            [cr.topLeft, cr.topRight, cr.bottomRight, cr.bottomLeft].every(
              (p) => p && typeof p === 'object' && Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)),
            );

          if (!cornersComplete(bg.corners) && bg.placement && typeof bg.placement === 'object') {
            const p = bg.placement;
            const x = Number(p.x) || 0;
            const y = Number(p.y) || 0;
            const pw = Number.isFinite(Number(p.width)) ? Number(p.width) : 1;
            const ph = Number.isFinite(Number(p.height)) ? Number(p.height) : 1;
            bg.corners = {
              topLeft: { x, y },
              topRight: { x: x + pw, y },
              bottomRight: { x: x + pw, y: y + ph },
              bottomLeft: { x, y: y + ph },
            };
          }
          if (!cornersComplete(bg.corners)) {
            bg.corners = {
              topLeft: { x: 0, y: 0 },
              topRight: { x: 1, y: 0 },
              bottomRight: { x: 1, y: 1 },
              bottomLeft: { x: 0, y: 1 },
            };
          }
          const c = bg.corners;
          bg.corners = {
            topLeft: { x: Number(c.topLeft?.x) || 0, y: Number(c.topLeft?.y) || 0 },
            topRight: { x: Number(c.topRight?.x) || 0, y: Number(c.topRight?.y) || 0 },
            bottomRight: { x: Number(c.bottomRight?.x) || 0, y: Number(c.bottomRight?.y) || 0 },
            bottomLeft: { x: Number(c.bottomLeft?.x) || 0, y: Number(c.bottomLeft?.y) || 0 },
          };
          const brad = Number(bg.borderRadius);
          bg.borderRadius = Number.isFinite(brad) ? Math.max(0, brad) : 0;
          bg.blendMode = typeof bg.blendMode === 'string' && bg.blendMode.trim() ? bg.blendMode.trim() : 'source-over';
          bg.backgroundColor =
            typeof bg.backgroundColor === 'string' && bg.backgroundColor.trim() ? bg.backgroundColor.trim() : '';
          const border = bg.border && typeof bg.border === 'object' ? bg.border : {};
          const borderWidth = Number(border.width);
          const borderGap = Number(border.gap);
          const borderDoubleWidth = Number(border.doubleWidth);
          const borderOpacity = Number(border.opacity);
          bg.border = {
            enabled: Boolean(border.enabled),
            oval: Boolean(border.oval),
            double: Boolean(border.double),
            gap: Number.isFinite(borderGap)
              ? Math.max(0, Math.abs(borderGap) > 1 ? borderGap / 1000 : borderGap)
              : 0.012,
            doubleWidth: Number.isFinite(borderDoubleWidth)
              ? Math.max(0, Math.abs(borderDoubleWidth) > 1 ? borderDoubleWidth / 1000 : borderDoubleWidth)
              : 0,
            color: typeof border.color === 'string' && border.color.trim() ? border.color.trim() : '#ffffff',
            width: Number.isFinite(borderWidth)
              ? Math.max(0, Math.abs(borderWidth) > 1 ? borderWidth / 1000 : borderWidth)
              : 0,
            opacity: Number.isFinite(borderOpacity) ? Math.min(1, Math.max(0, borderOpacity)) : 1,
          };
          const filters = bg.filters && typeof bg.filters === 'object' ? bg.filters : {};
          const contrast = Number(filters.contrast);
          const sepiaIntensity = Number(filters.sepiaIntensity);
          const burntBrownIntensity = Number(filters.burntBrownIntensity);
          bg.filters = {
            blackWhiteTransparent: Boolean(filters.blackWhiteTransparent),
            contrast: Number.isFinite(contrast) ? Math.min(2, Math.max(0, contrast)) : 1,
            sepia: Boolean(filters.sepia),
            sepiaIntensity: Number.isFinite(sepiaIntensity) ? Math.min(1, Math.max(0, sepiaIntensity)) : 1,
            burntBrown: Boolean(filters.burntBrown),
            burntBrownIntensity: Number.isFinite(burntBrownIntensity)
              ? Math.min(1, Math.max(0, burntBrownIntensity))
              : 0.65,
          };
          const feather = Number(bg.edgeFeather);
          bg.edgeFeather = Number.isFinite(feather) ? Math.max(0, feather) : 0;
        }
        const selectedEngravingColor = this._getSelectedEngravingColor();
        if (selectedEngravingColor) {
          if (!base.text || typeof base.text !== 'object') base.text = {};
          base.text.fillColor = selectedEngravingColor;
        }
        return base;
      }

      /** If stored config uses per-1000 values (e.g. 400 instead of 0.4), convert to 0–1 so drawing code works. */
      _normalizeConfigPer1000(config) {
        if (typeof window !== 'undefined' && window.YO_ENGRAVING_CONFIG?.normalizeConfigPer1000) {
          window.YO_ENGRAVING_CONFIG.normalizeConfigPer1000(config);
        }
      }

      _deepMerge(target, source) {
        if (!source || typeof source !== 'object' || Array.isArray(source)) return target;
        for (const key of Object.keys(source)) {
          const sv = source[key];
          if (sv === undefined || sv === null) continue;
          if (typeof sv === 'object' && !Array.isArray(sv)) {
            if (!target[key] || typeof target[key] !== 'object' || Array.isArray(target[key])) {
              target[key] = {};
            }
            this._deepMerge(target[key], sv);
          } else {
            target[key] = sv;
          }
        }
        return target;
      }

      /** Minimum canvas draw size (px) so text/curve render sharply; displayed size can be smaller via CSS. */
      static get MIN_PREVIEW_SIZE() {
        return 700;
      }

      _getPreviewDrawDimensions(container) {
        const minSize = this.constructor.MIN_PREVIEW_SIZE;
        if (!container) {
          return { drawW: minSize, drawH: minSize };
        }
        const dw = Math.max(1, container.clientWidth);
        const dh = Math.max(1, container.clientHeight);
        const scale = Math.max(1, minSize / dw, minSize / dh);
        return { drawW: Math.round(dw * scale), drawH: Math.round(dh * scale) };
      }

      _setupCanvasEl(canvas) {
        const container = canvas.parentElement;
        if (!container) return false;
        const displayW = container.clientWidth;
        const displayH = container.clientHeight;
        if (displayW <= 0 || displayH <= 0) return false;
        const { drawW, drawH } = this._getPreviewDrawDimensions(container);
        const dpr = Math.min(window.devicePixelRatio ?? 1, 3);
        canvas.width = drawW * dpr;
        canvas.height = drawH * dpr;
        canvas.style.width = `${displayW}px`;
        canvas.style.height = `${displayH}px`;
        return true;
      }

      _setupCanvas() {
        const c = this.canvas;
        if (!c) return;
        if (!this._setupCanvasEl(c)) return;
        const container = c.parentElement;
        if (!container) return;
        const { drawW, drawH } = this._getPreviewDrawDimensions(container);
        this._canvasW = drawW;
        this._canvasH = drawH;
        this._dpr = Math.min(window.devicePixelRatio ?? 1, 3);
      }

      _observeResize() {
        this._resizeObserver = new ResizeObserver(() => {
          let didSetup = false;
          this.querySelectorAll('.yo-engraving-preview__canvas').forEach((c) => {
            didSetup = this._setupCanvasEl(c) || didSetup;
          });
          if (!didSetup) return;
          this._setupCanvas();
          this.updatePreview();
        });
        this._resizeObserver.observe(this);
      }

      disconnectedCallback() {
        this._toggleLabelObserver?.disconnect();
        this._resizeObserver?.disconnect();
        this._drawScheduled && cancelAnimationFrame(this._drawScheduled);
        if (this._backgroundUserObjectUrl) {
          URL.revokeObjectURL(this._backgroundUserObjectUrl);
          this._backgroundUserObjectUrl = null;
        }
      }

      async _loadFont(fontName) {
        const name = String(fontName ?? '')
          .replace(/\u00a0/g, ' ')
          .trim();
        if (!name) return;
        if (this._fontCache.has(name)) return;
        const fontMap = YO_EngravingPreview.fonts;
        const url = typeof fontMap === 'object' && fontMap[name];
        if (!url) return;
        const existingLoad = this._fontLoadPromises.get(name);
        if (existingLoad) return existingLoad;
        const loadPromise = (async () => {
          try {
            const font = new FontFace(name, `url(${url})`);
            await font.load();
            document.fonts.add(font);
            this._fontCache.add(name);
          } catch (e) {
            console.warn(`${this.ns} Font load failed:`, name, e);
          } finally {
            this._fontLoadPromises.delete(name);
          }
        })();
        this._fontLoadPromises.set(name, loadPromise);
        return loadPromise;
      }

      _setLoadingState(isLoading) {
        this.classList.toggle('is-loading', Boolean(isLoading));
        this.querySelector('.yo-engraving-preview__loading')?.setAttribute('aria-busy', String(Boolean(isLoading)));
      }

      async _preparePreviewAssets(font, artworkImageUrl, bgUrl) {
        try {
          if (font) await this._loadFont(font);
          await this._ensureArtworkImage(artworkImageUrl);
          if (bgUrl) await this._ensureBackgroundImage(bgUrl);
        } catch (err) {
          console.warn(`${this.ns} Preview asset load failed`, err);
        }
      }

      _getFormState() {
        const parent = this.closest('yo-engraving');
        if (!parent)
          return {
            lines: [],
            primaryIndex: -1,
            font: '',
            artworkImageUrl: null,
            lineTextsBySlot: [],
            primarySlotIndex: -1,
            primarySlotIndices: [],
          };
        const form = parent.querySelector('form');
        const lineInputs = Array.from(parent.querySelectorAll('input[data-line-input="true"], input[name^="Line "]'));
        const filledIndices = [];
        const lines = [];
        lineInputs.forEach((el, i) => {
          const val = (el.value || '').trim();
          if (val) {
            lines.push(val);
            filledIndices.push(i);
          }
        });
        const primaryFixedInputIndices = Array.from(form?.querySelectorAll('.yo-engraving-form__primary-fixed') || [])
          .map((marker) =>
            marker
              ?.closest('.yo-engraving-form__field')
              ?.querySelector('input[data-line-input="true"], input[name^="Line "]'),
          )
          .map((input) => lineInputs.indexOf(input))
          .filter((i) => i >= 0);
        const primaryDataInputIndices = lineInputs
          .map((el, i) => (el.dataset?.lineIsPrimary === 'true' ? i : -1))
          .filter((i) => i >= 0);
        const primaryLockedInputIndices = [...new Set([...primaryFixedInputIndices, ...primaryDataInputIndices])];
        const primaryLockedHidden = form?.querySelector(
          'input[type="hidden"][name="Primary Line"][data-yo-primary-line-locked]',
        );
        const hasLockedPrimary = primaryLockedInputIndices.length > 0 || Boolean(primaryLockedHidden);
        const primaryRadio = form?.querySelector('[name="Primary Line"]:checked');
        let primaryIndex = -1;
        let primarySlotIndex = -1;
        let primarySlotIndices = [];
        if (primaryLockedInputIndices.length > 0) {
          primarySlotIndices = primaryLockedInputIndices;
          primarySlotIndex = primaryLockedInputIndices[0];
          const firstFilledPrimarySlot = primaryLockedInputIndices.find((i) => filledIndices.includes(i));
          if (firstFilledPrimarySlot != null) primaryIndex = filledIndices.indexOf(firstFilledPrimarySlot);
        } else if (primaryLockedHidden) {
          const pv = parseInt(primaryLockedHidden.value, 10);
          if (Number.isFinite(pv) && pv >= 1) {
            primarySlotIndex = pv - 1;
            primaryIndex = filledIndices.indexOf(primarySlotIndex);
          }
        } else if (primaryRadio) {
          const primaryOrigIdx = parseInt(primaryRadio.value, 10) - 1;
          const mapped = filledIndices.indexOf(primaryOrigIdx);
          if (mapped >= 0) primaryIndex = mapped;
          const n = parseInt(primaryRadio.value, 10);
          if (Number.isFinite(n) && n >= 1) primarySlotIndex = n - 1;
        }
        if (primarySlotIndices.length === 0 && primarySlotIndex >= 0) primarySlotIndices = [primarySlotIndex];
        if (primaryIndex === -1 && lines.length > 0 && !hasLockedPrimary) primaryIndex = 0;
        const lineTextsBySlot = lineInputs.map((el) => (el.value || '').trim());
        if (primarySlotIndex < 0 && lineTextsBySlot.some((s) => s) && !hasLockedPrimary) {
          primarySlotIndex = lineTextsBySlot.findIndex((s) => s);
          primarySlotIndices = [primarySlotIndex];
        }
        const fontRadio = form?.querySelector('[name="Font"]:checked');
        const font = (fontRadio?.value ?? '').replace(/\u00a0/g, ' ').trim();
        const artworkRadio = form?.querySelector('[name="Artwork"]:checked');
        let artworkImageUrl = null;
        if (artworkRadio && artworkRadio.value && artworkRadio.value !== 'No Artwork') {
          const label = artworkRadio.closest('label');
          const previewUrl = label?.querySelector('[data-artwork-preview-url]')?.dataset?.artworkPreviewUrl;
          const img = label?.querySelector('img');
          artworkImageUrl = previewUrl || img?.src || null;
        }
        return { lines, primaryIndex, font, artworkImageUrl, lineTextsBySlot, primarySlotIndex, primarySlotIndices };
      }

      /**
       * @param {Object} [exportOpts] - When set, render to exportOpts.ctx at exportOpts.w x exportOpts.h (dpr 1). Use noClear: true if caller drew background first.
       */
      _draw(lines, primaryIndex, fontName, artworkImageUrl, exportOpts) {
        const isExport = Boolean(exportOpts?.ctx);
        const canvas = isExport ? null : this.canvas;
        if (!isExport && !canvas) return;
        if (!isExport) this._setupCanvas();
        this.config = this._buildConfig();
        if (!isExport) this._syncTextFillToForm();
        const ctx = isExport ? exportOpts.ctx : canvas.getContext('2d');
        if (!ctx) return;
        const w = exportOpts?.w ?? this._canvasW ?? canvas.width / (this._dpr ?? 1);
        const h = exportOpts?.h ?? this._canvasH ?? canvas.height / (this._dpr ?? 1);
        const dpr = exportOpts?.dpr ?? this._dpr ?? 1;
        if (!isExport && (canvas.width === 0 || canvas.height === 0)) return;
        if (!exportOpts?.noClear) ctx.clearRect(0, 0, w, h);

        const cfg = this.config;
        const {
          lineTextsBySlot: _slotTexts = [],
          primarySlotIndex: primarySlotIndexFromForm = -1,
          primarySlotIndices: primarySlotIndicesFromForm = [],
        } = this._getFormState();
        const lineTextsBySlot = Array.isArray(_slotTexts) ? _slotTexts : [];
        const companionPrimarySlotIndex =
          Array.isArray(primarySlotIndicesFromForm) && primarySlotIndicesFromForm.length
            ? primarySlotIndicesFromForm
            : primarySlotIndexFromForm;
        const smallCanvas = w < 200 || h < 200;
        let zoneX = cfg.zone.x * w;
        let zoneY = cfg.zone.y * h;
        let zoneW = cfg.zone.width * w;
        let zoneH = cfg.zone.height * h;
        if (smallCanvas) {
          zoneX = Math.round(zoneX);
          zoneY = Math.round(zoneY);
          zoneW = Math.max(1, Math.round(zoneW));
          zoneH = Math.max(1, Math.round(zoneH));
        }

        const roundTxt = (v) => (smallCanvas ? Math.max(1, Math.round(v)) : v);
        const textIn = cfg.text != null && typeof cfg.text === 'object' && !Array.isArray(cfg.text) ? cfg.text : {};
        const effectiveConfig = {
          ...cfg,
          artworkTextGap: roundTxt(h * (cfg.artworkTextGap ?? 0.067)),
          text: {
            ...textIn,
            primaryMaxFontSize: roundTxt(h * (textIn.primaryMaxFontSize ?? 0.11)),
            oneLineTextOnlyFontSize: roundTxt(h * (textIn.oneLineTextOnlyFontSize ?? 0)),
            oneLineTextOnlyMaxWidth: Number(textIn.oneLineTextOnlyMaxWidth) || 0,
            primaryMinFontSize: roundTxt(h * (textIn.primaryMinFontSize ?? 0.033)),
            secondaryMaxFontSize: roundTxt(h * (textIn.secondaryMaxFontSize ?? 0.067)),
            secondaryMinFontSize: roundTxt(h * (textIn.secondaryMinFontSize ?? 0.033)),
            gap: roundTxt(h * (textIn.gap ?? 0.02)),
            primaryGap: roundTxt(h * (textIn.primaryGap ?? 0.047)),
          },
        };

        ctx.save();
        if (!isExport) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        this._drawBackgroundImage(ctx, w, h, { useDomBlendBackdrop: !isExport, renderScale: dpr, isExport });
        this._drawImageUploadDebugLines(ctx, w, h, isExport);

        if (cfg.showZoneDebugLines && this.liveDebug && !isExport) {
          ctx.save();
          ctx.strokeStyle = 'rgba(255, 0, 0, 1)';
          ctx.setLineDash([4, 4]);
          ctx.lineWidth = 2;
          ctx.strokeRect(zoneX, zoneY, zoneW, zoneH);
          ctx.restore();
        }

        if (cfg.circularText?.showDebugLines && this.liveDebug && !isExport) {
          ctx.save();
          ctx.globalAlpha = 1;
          this._strokeCircularTextDebugCircle(ctx, w, h, dpr, cfg.circularText);
          ctx.restore();
        }

        if (cfg.fieldPlacements?.enabled && cfg.fieldPlacements?.showDebugLines && this.liveDebug && !isExport) {
          ctx.save();
          ctx.globalAlpha = 1;
          this._strokeFieldPlacementsDebug(ctx, w, h, dpr, cfg.fieldPlacements, lineTextsBySlot);
          ctx.restore();
        }

        if (cfg.companion?.showDebugLines && this.liveDebug && !isExport) {
          ctx.save();
          ctx.globalAlpha = 1;
          this._strokeCompanionDebug(ctx, w, h, dpr, cfg.companion, 0, 0, zoneW, zoneY + zoneH / 2);
          ctx.restore();
        }

        const hasArtwork = Boolean(artworkImageUrl);
        const hasText = lines.length > 0;
        const hasFieldLineText = lineTextsBySlot.some((s) => String(s || '').trim());
        const parentEngraving = this.closest('yo-engraving');
        const textLinesModeDraw = parentEngraving?.dataset?.textLinesMode === 'true';
        const compDraw = cfg.companion && typeof cfg.companion === 'object' ? cfg.companion : {};
        const lineLocationsBySlot =
          typeof window !== 'undefined' && typeof window.YO_ENGRAVING_COMPANION_getLineLocationsBySlot === 'function'
            ? window.YO_ENGRAVING_COMPANION_getLineLocationsBySlot(parentEngraving)
            : [];
        const hasPositionedTextLines =
          typeof window !== 'undefined' && typeof window.YO_ENGRAVING_COMPANION_hasNonDefaultLineLocation === 'function'
            ? window.YO_ENGRAVING_COMPANION_hasNonDefaultLineLocation(parentEngraving)
            : lineLocationsBySlot.some((loc) => loc && loc !== 'center');
        const useCompanion =
          textLinesModeDraw &&
          (Boolean(compDraw.enabled) || hasPositionedTextLines) &&
          (hasFieldLineText || hasArtwork);
        const fpCfg = cfg.fieldPlacements;
        const useFieldPlacements =
          Boolean(fpCfg?.enabled && (hasFieldLineText || (this.liveDebug && fpCfg?.showDebugLines))) && !useCompanion;
        const useCircularText = Boolean(cfg.circularText?.enabled && hasText) && !useFieldPlacements;
        const zoneStacksText = hasText && !useCircularText && !useFieldPlacements && !useCompanion;
        /** Companion still lays out art in the zone with text — use zone artwork max W/H, not “art only” caps. */
        const artworkMaxUsesZoneStacking = zoneStacksText || useCompanion;
        const bgSrc = this._getBackgroundImageSourceFromConfig(cfg);
        const showFieldAnchorsOnly =
          this.liveDebug && fpCfg?.enabled && fpCfg?.showDebugLines && lineTextsBySlot.length > 0;
        if (!hasArtwork && !hasText && !hasFieldLineText && !bgSrc && !showFieldAnchorsOnly) {
          ctx.restore();
          return;
        }

        const artMaxW = artworkMaxUsesZoneStacking
          ? w * (cfg.artworkMaxWidth ?? 1)
          : w * (cfg.artworkOnlyMaxWidth ?? 1);
        const artMaxH = artworkMaxUsesZoneStacking
          ? h * (cfg.artworkMaxHeight ?? 0.4)
          : h * (cfg.artworkOnlyMaxHeight ?? 1);
        const artImg = hasArtwork ? this._getArtworkImage(artworkImageUrl) : null;
        let artDrawW = 0;
        let artDrawH = 0;
        if (artImg) {
          const scale = Math.min(artMaxW / artImg.naturalWidth, artMaxH / artImg.naturalHeight, 1);
          artDrawW = artImg.naturalWidth * scale;
          artDrawH = artImg.naturalHeight * scale;
        }

        const textCfg = effectiveConfig.text;
        const gapPx = textCfg.gap ?? 0;
        const primaryGapPx = textCfg.primaryGap ?? 0;
        const primaryGapCount =
          zoneStacksText && primaryIndex >= 0
            ? Math.min(primaryIndex > 0 ? 1 : 0, 1) + Math.min(primaryIndex < lines.length - 1 ? 1 : 0, 1)
            : 0;
        const n = lines.length;
        const verticalText = textCfg?.vertical === true;
        const maxSegLine =
          zoneStacksText && verticalText
            ? Math.max(
                0,
                ...lines.map((ln) => {
                  const g = this._segmentGlyphs(ln);
                  return g.length;
                }),
              )
            : 0;
        const textBlockH = zoneStacksText
          ? verticalText
            ? maxSegLine * textCfg.primaryMaxFontSize +
              Math.max(0, maxSegLine - 1) * (Number(textCfg.verticalLetterSpacing) || 0) +
              textCfg.primaryMaxFontSize * 0.35
            : (() => {
                const pSlot = primaryIndex >= 0 ? primaryIndex : 0;
                let slotSum = 0;
                for (let i = 0; i < n; i++) {
                  slotSum += i === pSlot ? textCfg.primaryMaxFontSize : textCfg.secondaryMaxFontSize;
                }
                return slotSum + (n - 1) * 2 * gapPx + primaryGapCount * primaryGapPx;
              })()
          : 0;
        const gap = hasArtwork && artImg && zoneStacksText ? effectiveConfig.artworkTextGap : 0;
        const hasStackableArtworkText =
          hasArtwork && hasText && artImg && !useCircularText && !useFieldPlacements && !useCompanion;
        const artworkPositionRaw = String(cfg.artworkPosition || 'top').toLowerCase();
        const artworkPosition =
          artworkPositionRaw === 'left' || artworkPositionRaw === 'right' || artworkPositionRaw === 'bottom'
            ? artworkPositionRaw
            : 'top';
        const stackArtworkAtBottom = Boolean(cfg.artworkAtBottom) && hasStackableArtworkText;
        const sideArtworkLeft = hasStackableArtworkText && !stackArtworkAtBottom && artworkPosition === 'left';
        const sideArtworkRight = hasStackableArtworkText && !stackArtworkAtBottom && artworkPosition === 'right';
        const stackArtworkBottom = hasStackableArtworkText && (stackArtworkAtBottom || artworkPosition === 'bottom');
        const stackArtworkTop = hasStackableArtworkText && !sideArtworkLeft && !sideArtworkRight && !stackArtworkBottom;
        const curveLayoutW = sideArtworkLeft || sideArtworkRight ? Math.max(0, zoneW - artDrawW - gap) : zoneW;
        const curvePad =
          zoneStacksText && !verticalText && Math.abs(cfg.textCurve ?? 0) > 1e-6
            ? this._estimateTextArcLayoutPad(curveLayoutW, cfg.textCurve, textCfg.primaryMaxFontSize)
            : 0;
        const textBlockForFit = textBlockH + curvePad;
        let totalContentH = artDrawH + gap + textBlockForFit;
        let scaledTextBlockH = textBlockH;
        let textCfgForDraw = effectiveConfig.text;
        let nominalTextCfgForDraw = effectiveConfig.text;
        const oneLineTextOnlyFs = Number(textCfg.oneLineTextOnlyFontSize);
        const oneLineTextOnlyMaxWidth = Number(textCfg.oneLineTextOnlyMaxWidth);
        const useOneLineTextOnlyFs =
          !hasArtwork &&
          hasText &&
          lines.length === 1 &&
          !useCircularText &&
          Number.isFinite(oneLineTextOnlyFs) &&
          oneLineTextOnlyFs > 0;
        const useOneLineTextOnlyMaxWidth =
          useOneLineTextOnlyFs && Number.isFinite(oneLineTextOnlyMaxWidth) && oneLineTextOnlyMaxWidth > 0;
        const artPlusTextH = artDrawH + textBlockForFit;
        if (totalContentH > zoneH && artPlusTextH > 0) {
          const t0 = effectiveConfig.text;
          const applyTextFitScale = (fitScale) => {
            if (!Number.isFinite(fitScale) || fitScale >= 1 - 1e-9) return;
            scaledTextBlockH = textBlockH * fitScale;
            totalContentH = artDrawH + gap + textBlockForFit * fitScale;
            textCfgForDraw = {
              ...t0,
              primaryMaxFontSize: t0.primaryMaxFontSize * fitScale,
              primaryMinFontSize: t0.primaryMinFontSize * fitScale,
              secondaryMaxFontSize: t0.secondaryMaxFontSize * fitScale,
              secondaryMinFontSize: t0.secondaryMinFontSize * fitScale,
              gap: t0.gap * fitScale,
              primaryGap: t0.primaryGap * fitScale,
            };
          };

          if (hasArtwork && artImg && zoneStacksText) {
            let textForFitEff = textBlockForFit;
            let textAvail = zoneH - artDrawH - gap;
            if (textAvail <= 0 && artDrawH > 0) {
              const maxArtH = Math.max(0, zoneH - gap - textBlockForFit);
              if (maxArtH > 0) {
                const artScale = Math.min(1, maxArtH / artDrawH);
                artDrawW *= artScale;
                artDrawH *= artScale;
                totalContentH = artDrawH + gap + textBlockForFit;
                textAvail = zoneH - artDrawH - gap;
              }
            }
            if (textBlockForFit > 0 && textAvail > 0 && textAvail + 1e-6 < textBlockForFit) {
              const fs = textAvail / textBlockForFit;
              applyTextFitScale(fs);
              textForFitEff = textBlockForFit * fs;
            }
            if (artDrawH + gap + textForFitEff > zoneH + 0.5 && artDrawH > 0) {
              const maxArtH2 = Math.max(0, zoneH - gap - textForFitEff);
              if (maxArtH2 > 0) {
                const artScale2 = Math.min(1, maxArtH2 / artDrawH);
                artDrawW *= artScale2;
                artDrawH *= artScale2;
                totalContentH = artDrawH + gap + textForFitEff;
              }
            }
          } else {
            const maxArtH = Math.max(0, zoneH - gap - textBlockForFit);
            if (hasArtwork && artImg && artDrawH > 0 && maxArtH > 0) {
              const artScale = Math.min(1, maxArtH / artDrawH);
              artDrawW *= artScale;
              artDrawH *= artScale;
              totalContentH = artDrawH + gap + textBlockForFit;
            }
            if (totalContentH > zoneH && textBlockForFit > 0) {
              applyTextFitScale(zoneH / textBlockForFit);
              totalContentH = zoneH;
            }
          }
        }
        if (useOneLineTextOnlyFs) {
          const forcedPx = oneLineTextOnlyFs;
          textCfgForDraw = {
            ...textCfgForDraw,
            primaryMaxFontSize: forcedPx,
            primaryMinFontSize: Math.min(Number(textCfgForDraw.primaryMinFontSize) || forcedPx, forcedPx),
            ...(useOneLineTextOnlyMaxWidth ? { primaryMaxWidth: oneLineTextOnlyMaxWidth } : {}),
          };
          nominalTextCfgForDraw = {
            ...nominalTextCfgForDraw,
            primaryMaxFontSize: forcedPx,
            primaryMinFontSize: Math.min(Number(nominalTextCfgForDraw.primaryMinFontSize) || forcedPx, forcedPx),
            ...(useOneLineTextOnlyMaxWidth ? { primaryMaxWidth: oneLineTextOnlyMaxWidth } : {}),
          };
        }

        const lineLayoutZoneW = sideArtworkLeft || sideArtworkRight ? Math.max(0, zoneW - artDrawW - gap) : zoneW;
        let layoutTextBlockH = scaledTextBlockH;
        let lineSizesForTextDraw = null;
        if (zoneStacksText) {
          lineSizesForTextDraw = this._computeTextLineSizesForLayout(
            ctx,
            lines,
            primaryIndex,
            fontName,
            lineLayoutZoneW,
            scaledTextBlockH,
            textCfgForDraw,
            w,
            this.config.textCurve,
            nominalTextCfgForDraw,
          );
          layoutTextBlockH = this._textStackHeightFromLineSizes(
            lines,
            primaryIndex,
            lineSizesForTextDraw,
            textCfgForDraw,
            ctx,
            fontName,
          );
        } else if (useCircularText || useFieldPlacements) {
          layoutTextBlockH = 0;
        }
        const straightNoCurve = zoneStacksText && !verticalText && Math.abs(cfg.textCurve ?? 0) < 1e-6;
        const textDrawBandH = zoneStacksText
          ? straightNoCurve || verticalText
            ? layoutTextBlockH
            : scaledTextBlockH
          : scaledTextBlockH;
        let visibleContentH = artDrawH + gap + layoutTextBlockH;
        if (sideArtworkLeft || sideArtworkRight) {
          visibleContentH = Math.max(artDrawH, layoutTextBlockH);
        }
        const startY = zoneY + (zoneH - visibleContentH) / 2;
        let artX = zoneX + (zoneW - artDrawW) / 2;
        let artY;
        let textY;
        let textZoneX = zoneX;
        let textZoneW = zoneW;
        if (sideArtworkLeft || sideArtworkRight) {
          const sideGap = gap;
          if (sideArtworkLeft) {
            artX = zoneX;
            textZoneX = zoneX + artDrawW + sideGap;
            textZoneW = Math.max(0, zoneX + zoneW - textZoneX);
          } else {
            textZoneX = zoneX;
            textZoneW = Math.max(0, zoneW - artDrawW - sideGap);
            artX = textZoneX + textZoneW + sideGap;
          }
          artY = zoneY + (zoneH - artDrawH) / 2;
          textY = zoneY + (zoneH - layoutTextBlockH) / 2;
        } else if (stackArtworkBottom) {
          textY = startY;
          artY = startY + layoutTextBlockH + gap;
        } else if (stackArtworkTop) {
          artY = startY;
          textY = startY + artDrawH + gap;
        } else {
          artY = startY;
          textY = startY + artDrawH + gap;
        }
        if (!hasText && artImg) {
          const offX = Number(cfg.artworkOnlyOffsetX);
          const offY = Number(cfg.artworkOnlyOffsetY);
          if (Number.isFinite(offX)) artX += zoneW * (Math.abs(offX) > 1 ? offX / 1000 : offX);
          if (Number.isFinite(offY)) artY += zoneH * (Math.abs(offY) > 1 ? offY / 1000 : offY);
        }

        const wantsLayoutDebug = !isExport && this._wantsArtworkDebugOutline(cfg);
        let artMaxDbg = null;
        if (wantsLayoutDebug && artImg) {
          const amw = artworkMaxUsesZoneStacking ? w * (cfg.artworkMaxWidth ?? 1) : w * (cfg.artworkOnlyMaxWidth ?? 1);
          const amh = artworkMaxUsesZoneStacking
            ? h * (cfg.artworkMaxHeight ?? 0.4)
            : h * (cfg.artworkOnlyMaxHeight ?? 1);
          let amx;
          let amy;
          if (zoneStacksText && sideArtworkLeft) {
            amx = zoneX;
            amy = zoneY + (zoneH - amh) / 2;
          } else if (zoneStacksText && sideArtworkRight) {
            amx = zoneX + zoneW - amw;
            amy = zoneY + (zoneH - amh) / 2;
          } else if (zoneStacksText && (stackArtworkBottom || stackArtworkTop)) {
            amx = zoneX + (zoneW - amw) / 2;
            amy = artY;
          } else {
            amx = zoneX + (zoneW - amw) / 2;
            amy = zoneY + (zoneH - amh) / 2;
          }
          if (amw > 0 && amh > 0) artMaxDbg = { amx, amy, amw, amh };
        }

        const zoneRotDeg = Number(cfg.zoneRotation);
        const zoneRotRad = Number.isFinite(zoneRotDeg) ? (zoneRotDeg * Math.PI) / 180 : 0;
        const hasZoneRotate = Math.abs(zoneRotRad) > 1e-6;

        const skewX = cfg.skewX ?? 0;
        const skewY = cfg.skewY ?? 0;
        const hasSkew = skewX !== 0 || skewY !== 0;
        const pyN = Number(cfg.perspectiveY ?? cfg.perspective_y ?? 0);
        const pxN = Number(cfg.perspectiveX ?? cfg.perspective_x ?? 0);
        const perspectiveY = Number.isFinite(pyN) ? (Math.abs(pyN) > 1 ? pyN / 1000 : pyN) : 0;
        const perspectiveX = Number.isFinite(pxN) ? (Math.abs(pxN) > 1 ? pxN / 1000 : pxN) : 0;
        const hasPerspective = Math.abs(perspectiveY) > 1e-6 || Math.abs(perspectiveX) > 1e-6;
        const corners = cfg.corners;
        const hasCornerWarp =
          corners &&
          [corners.topLeft, corners.topRight, corners.bottomRight, corners.bottomLeft].some(
            (c) => c && (c.x !== 0 || c.y !== 0),
          );
        const useOffscreen = hasCornerWarp || hasPerspective;

        const wrapZoneDraw = !useOffscreen && (hasZoneRotate || hasSkew);
        if (wrapZoneDraw) ctx.save();
        if (hasZoneRotate && !useOffscreen) {
          const zrx = zoneX + zoneW / 2;
          const zry = zoneY + zoneH / 2;
          ctx.translate(zrx, zry);
          ctx.rotate(zoneRotRad);
          ctx.translate(-zrx, -zry);
        }
        if (hasSkew && !useOffscreen) {
          const cx = zoneX + zoneW / 2;
          const cy = zoneY + zoneH / 2;
          ctx.translate(cx, cy);
          if (skewY !== 0 && skewX !== 0) {
            ctx.transform(1, skewY, skewX, 1, 0, 0);
          } else {
            if (skewY !== 0) ctx.transform(1, skewY, 0, 1, 0, 0);
            if (skewX !== 0) ctx.transform(1, 0, skewX, 1, 0, 0);
          }
          ctx.translate(-cx, -cy);
        }

        ctx.globalAlpha = cfg.opacity;
        if (useOffscreen) {
          /**
           * Offscreen must be dense enough that perspective/warp never upsamples strips past device
           * pixels (dest strip width ≈ cellW×scaleX×dpr px; source strip has cellW×osScale px → need
           * osScale ≥ scaleX×dpr). Extra margin for corner warp shears.
           */
          const persMag = Math.max(Math.abs(perspectiveY), Math.abs(perspectiveX));
          const stretch = hasPerspective ? (1 + persMag) * 1.4 + 0.35 : hasCornerWarp ? 1.85 : 1;
          // Use the same base DPR for export as preview so skew/perspective stays aligned.
          const baseDpr = isExport ? (this._dpr ?? dpr ?? 1) : dpr;
          const osScale = Math.min(12, Math.max(2, Math.ceil(baseDpr * stretch)));
          const warpedLayoutDebugAlreadyDrawn = wantsLayoutDebug && hasPerspective && !hasCornerWarp;
          /**
           * Companion anchors (cy) sit on a guide line; text uses ascenders above cy and artwork is
           * vertically centered on cy. Zone-relative Y can go negative. Without extra padding the
           * offscreen bitmap clips at y=0, which looks like a hard cut along the horizontal guide.
           */
          const companionOffscreenPad = useCompanion
            ? Math.min(
                360,
                Math.max(
                  56,
                  Math.ceil(0.12 * Math.max(zoneW, zoneH)) +
                    Math.ceil((Number(artDrawH) || 0) * 0.45) +
                    Math.ceil(h * 0.04),
                ),
              )
            : 0;
          const debugPad = Math.max(warpedLayoutDebugAlreadyDrawn ? 48 : 0, companionOffscreenPad);
          const offW = Math.ceil((zoneW + 2 * debugPad) * osScale);
          const offH = Math.ceil((zoneH + 2 * debugPad) * osScale);
          const offscreen = document.createElement('canvas');
          offscreen.width = offW;
          offscreen.height = offH;
          const octx = offscreen.getContext('2d');
          if (octx) {
            octx.imageSmoothingEnabled = true;
            octx.imageSmoothingQuality = 'high';
            octx.setTransform(osScale, 0, 0, osScale, debugPad * osScale, debugPad * osScale);
            if (hasZoneRotate) {
              const rrx = zoneW / 2;
              const rry = zoneH / 2;
              octx.translate(rrx, rry);
              octx.rotate(zoneRotRad);
              octx.translate(-rrx, -rry);
            }
            if (hasSkew) {
              const cx = zoneW / 2;
              const cy = zoneH / 2;
              octx.translate(cx, cy);
              if (skewY !== 0 && skewX !== 0) {
                octx.transform(1, skewY, skewX, 1, 0, 0);
              } else {
                if (skewY !== 0) octx.transform(1, skewY, 0, 1, 0, 0);
                if (skewX !== 0) octx.transform(1, 0, skewX, 1, 0, 0);
              }
              octx.translate(-cx, -cy);
            }
            if (artImg && !useCompanion) {
              const artRelX = artX - zoneX;
              const artRelY = artY - zoneY;
              this._drawArtwork(octx, artworkImageUrl, artRelX, artRelY, artDrawW, artDrawH, osScale);
            }
            if (zoneStacksText)
              this._drawText(
                octx,
                lines,
                primaryIndex,
                fontName,
                textZoneX - zoneX,
                textY - zoneY,
                textZoneW,
                textDrawBandH,
                textCfgForDraw,
                w,
                nominalTextCfgForDraw,
                lineSizesForTextDraw,
              );
            if (useFieldPlacements)
              this._drawFieldPlacementLines(
                octx,
                w,
                h,
                lineTextsBySlot,
                primarySlotIndexFromForm,
                fontName,
                fpCfg,
                textCfgForDraw,
                effectiveConfig.text,
                roundTxt,
                zoneX,
                zoneY,
              );
            if (useCompanion) {
              this._drawCompanionLayout(
                octx,
                w,
                h,
                osScale,
                cfg,
                lineTextsBySlot,
                lineLocationsBySlot,
                companionPrimarySlotIndex,
                fontName,
                textCfgForDraw,
                effectiveConfig.text,
                roundTxt,
                artworkImageUrl,
                artDrawW,
                artDrawH,
                zoneX,
                zoneY,
                zoneW,
                zoneH / 2,
              );
            }
            if (useCompanion && wantsLayoutDebug) {
              if (artImg && artDrawW > 0 && artDrawH > 0) {
                const b = this._companionLayoutArtBounds;
                const pos = b
                  ? { ax: b.ax, ay: b.ay }
                  : this._companionArtAllocationRect(w, zoneX, cfg.companion, artDrawW, artDrawH, zoneH / 2);
                this._strokeArtworkDebugRect(octx, pos.ax, pos.ay, artDrawW, artDrawH);
                if (artMaxDbg)
                  this._strokeArtworkMaxSlotDebugRect(
                    octx,
                    artMaxDbg.amx - zoneX,
                    artMaxDbg.amy - zoneY,
                    artMaxDbg.amw,
                    artMaxDbg.amh,
                  );
              }
              if (hasFieldLineText)
                this._strokeCompanionTextColumnBandsDebug(octx, w, h, cfg.companion, zoneW, zoneH, zoneH / 2, zoneX);
            }
            if (warpedLayoutDebugAlreadyDrawn && artImg && !useCompanion) {
              const artRelX = artX - zoneX;
              const artRelY = artY - zoneY;
              this._strokeArtworkDebugRect(octx, artRelX, artRelY, artDrawW, artDrawH);
              if (artMaxDbg)
                this._strokeArtworkMaxSlotDebugRect(
                  octx,
                  artMaxDbg.amx - zoneX,
                  artMaxDbg.amy - zoneY,
                  artMaxDbg.amw,
                  artMaxDbg.amh,
                );
            }
            if (warpedLayoutDebugAlreadyDrawn && hasText && !useCircularText && !useCompanion) {
              this._strokeTextLayoutDebugRects(
                octx,
                textZoneX - zoneX,
                textY - zoneY,
                textZoneW,
                textDrawBandH,
                textBlockForFit,
                layoutTextBlockH,
              );
            }
            let sourceCanvas = offscreen;
            let sourceW = zoneW + 2 * debugPad;
            let sourceH = zoneH + 2 * debugPad;
            let quadBaseX = zoneX - debugPad;
            let quadBaseY = zoneY - debugPad;
            let quadBaseW = sourceW;
            let quadBaseH = sourceH;
            if (hasPerspective) {
              const perspectiveFramePad = debugPad;
              const perspectiveFrameW = zoneW + 2 * perspectiveFramePad;
              const perspectiveFrameH = zoneH + 2 * perspectiveFramePad;
              const zoneCenterY = Math.max(perspectiveFrameH / 2, 1);
              const zoneCenterX = Math.max(perspectiveFrameW / 2, 1);
              const cols = Math.max(16, Math.min(96, Math.floor(offW / 18)));
              const rows = Math.max(16, Math.min(96, Math.floor(offH / 18)));
              const cellW = perspectiveFrameW / cols;
              const cellH = perspectiveFrameH / rows;
              const sx0 = (i, j) => i * cellW * osScale;
              const sy0 = (i, j) => j * cellH * osScale;
              const sw0 = cellW * osScale;
              const sh0 = cellH * osScale;
              const destW = [];
              const destH = Array.from({ length: cols }, () => []);
              for (let j = 0; j < rows; j++) {
                destW[j] = [];
                const ty = (j * cellH + cellH / 2 - perspectiveFrameH / 2) / zoneCenterY;
                const scaleX = Math.max(0.1, 1 + perspectiveY * ty);
                for (let i = 0; i < cols; i++) {
                  const tx = (i * cellW + cellW / 2 - perspectiveFrameW / 2) / zoneCenterX;
                  const scaleY = Math.max(0.1, 1 + perspectiveX * tx);
                  destW[j][i] = cellW * scaleX;
                  destH[i][j] = cellH * scaleY;
                }
              }
              const rowWidths = destW.map((r) => r.reduce((a, w) => a + w, 0));
              const colHeights = destH.map((c) => c.reduce((a, h) => a + h, 0));
              const totalW = Math.max(...rowWidths);
              const totalH = Math.max(...colHeights);
              const frameX = zoneX - perspectiveFramePad;
              const frameY = zoneY - perspectiveFramePad;
              const startX = frameX + (perspectiveFrameW - totalW) / 2;
              const startY = frameY + (perspectiveFrameH - totalH) / 2;
              if (hasCornerWarp) {
                const perspectiveCanvas = document.createElement('canvas');
                perspectiveCanvas.width = Math.ceil(totalW * osScale);
                perspectiveCanvas.height = Math.ceil(totalH * osScale);
                const pctx = perspectiveCanvas.getContext('2d');
                if (pctx) {
                  pctx.imageSmoothingEnabled = true;
                  pctx.imageSmoothingQuality = 'high';
                  pctx.setTransform(osScale, 0, 0, osScale, 0, 0);
                  for (let j = 0; j < rows; j++) {
                    let dx = (totalW - rowWidths[j]) / 2;
                    for (let i = 0; i < cols; i++) {
                      let dy = (totalH - colHeights[i]) / 2;
                      for (let jj = 0; jj < j; jj++) dy += destH[i][jj];
                      pctx.drawImage(offscreen, sx0(i, j), sy0(i, j), sw0, sh0, dx, dy, destW[j][i], destH[i][j]);
                      dx += destW[j][i];
                    }
                  }
                  pctx.setTransform(1, 0, 0, 1, 0, 0);
                  sourceCanvas = perspectiveCanvas;
                  sourceW = totalW;
                  sourceH = totalH;
                  quadBaseX = startX;
                  quadBaseY = startY;
                  quadBaseW = totalW;
                  quadBaseH = totalH;
                }
              } else {
                for (let j = 0; j < rows; j++) {
                  let dx = startX + (totalW - rowWidths[j]) / 2;
                  for (let i = 0; i < cols; i++) {
                    let dy = startY + (totalH - colHeights[i]) / 2;
                    for (let jj = 0; jj < j; jj++) dy += destH[i][jj];
                    ctx.drawImage(offscreen, sx0(i, j), sy0(i, j), sw0, sh0, dx, dy, destW[j][i], destH[i][j]);
                    dx += destW[j][i];
                  }
                }
              }
            }
            if (hasCornerWarp) {
              const c = corners;
              const tlx = c.topLeft?.x ?? 0;
              const tly = c.topLeft?.y ?? 0;
              const trx = c.topRight?.x ?? 0;
              const try_ = c.topRight?.y ?? 0;
              const brx = c.bottomRight?.x ?? 0;
              const bry = c.bottomRight?.y ?? 0;
              const blx = c.bottomLeft?.x ?? 0;
              const bly = c.bottomLeft?.y ?? 0;
              const tl = { x: quadBaseX + quadBaseW * tlx, y: quadBaseY + quadBaseH * tly };
              const tr = { x: quadBaseX + quadBaseW + quadBaseW * trx, y: quadBaseY + quadBaseH * try_ };
              const br = { x: quadBaseX + quadBaseW + quadBaseW * brx, y: quadBaseY + quadBaseH + quadBaseH * bry };
              const bl = { x: quadBaseX + quadBaseW * blx, y: quadBaseY + quadBaseH + quadBaseH * bly };
              const dtl = { x: tl.x - quadBaseX, y: tl.y - quadBaseY };
              const dtr = { x: tr.x - (quadBaseX + quadBaseW), y: tr.y - quadBaseY };
              const dbr = { x: br.x - (quadBaseX + quadBaseW), y: br.y - (quadBaseY + quadBaseH) };
              const dbl = { x: bl.x - quadBaseX, y: bl.y - (quadBaseY + quadBaseH) };
              const srcPxW = sourceCanvas.width;
              const srcPxH = sourceCanvas.height;
              const cols = Math.max(16, Math.min(120, Math.floor(srcPxW / 14)));
              const rows = Math.max(16, Math.min(120, Math.floor(srcPxH / 14)));
              const cellW = srcPxW / cols;
              const cellH = srcPxH / rows;
              const vx = [];
              const vy = [];
              for (let j = 0; j <= rows; j++) {
                vx[j] = [];
                vy[j] = [];
                const v = j / rows;
                const v1 = 1 - v;
                for (let i = 0; i <= cols; i++) {
                  const u = i / cols;
                  const u1 = 1 - u;
                  const wtl = u1 * u1 * v1 * v1;
                  const wtr = u * u * v1 * v1;
                  const wbr = u * u * v * v;
                  const wbl = u1 * u1 * v * v;
                  vx[j][i] = quadBaseX + u * quadBaseW + wtl * dtl.x + wtr * dtr.x + wbr * dbr.x + wbl * dbl.x;
                  vy[j][i] = quadBaseY + v * quadBaseH + wtl * dtl.y + wtr * dtr.y + wbr * dbr.y + wbl * dbl.y;
                }
              }
              for (let j = 0; j < rows; j++) {
                for (let i = 0; i < cols; i++) {
                  const cx = (vx[j][i] + vx[j][i + 1] + vx[j + 1][i] + vx[j + 1][i + 1]) / 4;
                  const cy = (vy[j][i] + vy[j][i + 1] + vy[j + 1][i] + vy[j + 1][i + 1]) / 4;
                  const dw = (vx[j][i + 1] - vx[j][i] + vx[j + 1][i + 1] - vx[j + 1][i]) / 2;
                  const dh = (vy[j + 1][i] - vy[j][i] + vy[j + 1][i + 1] - vy[j][i + 1]) / 2;
                  ctx.drawImage(sourceCanvas, i * cellW, j * cellH, cellW, cellH, cx - dw / 2, cy - dh / 2, dw, dh);
                }
              }
            }
          }
        }
        const warpedLayoutDebugAlreadyDrawn = useOffscreen && wantsLayoutDebug && hasPerspective && !hasCornerWarp;
        if (useOffscreen && wantsLayoutDebug && artImg && !useCompanion) {
          if (!warpedLayoutDebugAlreadyDrawn) {
            this._strokeArtworkDebugRect(ctx, artX, artY, artDrawW, artDrawH);
            if (artMaxDbg)
              this._strokeArtworkMaxSlotDebugRect(ctx, artMaxDbg.amx, artMaxDbg.amy, artMaxDbg.amw, artMaxDbg.amh);
          }
        }
        if (useOffscreen && wantsLayoutDebug && hasText && !useCircularText && !useCompanion) {
          if (!warpedLayoutDebugAlreadyDrawn) {
            this._strokeTextLayoutDebugRects(
              ctx,
              textZoneX,
              textY,
              textZoneW,
              textDrawBandH,
              textBlockForFit,
              layoutTextBlockH,
            );
          }
          if (zoneStacksText && !textCfgForDraw.vertical && !warpedLayoutDebugAlreadyDrawn)
            this._strokeTextMaxWidthConstraintGuides(
              ctx,
              textZoneX,
              textY,
              textZoneW,
              textDrawBandH,
              textCfgForDraw,
              w,
            );
        }
        if (!useOffscreen) {
          if (artImg && !useCompanion) {
            this._drawArtwork(ctx, artworkImageUrl, artX, artY, artDrawW, artDrawH, dpr);
            if (wantsLayoutDebug) {
              this._strokeArtworkDebugRect(ctx, artX, artY, artDrawW, artDrawH);
              if (artMaxDbg)
                this._strokeArtworkMaxSlotDebugRect(ctx, artMaxDbg.amx, artMaxDbg.amy, artMaxDbg.amw, artMaxDbg.amh);
            }
          }
          if (zoneStacksText)
            this._drawText(
              ctx,
              lines,
              primaryIndex,
              fontName,
              textZoneX,
              textY,
              textZoneW,
              textDrawBandH,
              textCfgForDraw,
              w,
              nominalTextCfgForDraw,
              lineSizesForTextDraw,
            );
          if (hasText && !useCircularText && wantsLayoutDebug && !useCompanion) {
            this._strokeTextLayoutDebugRects(
              ctx,
              textZoneX,
              textY,
              textZoneW,
              textDrawBandH,
              textBlockForFit,
              layoutTextBlockH,
            );
            if (zoneStacksText && !textCfgForDraw.vertical)
              this._strokeTextMaxWidthConstraintGuides(
                ctx,
                textZoneX,
                textY,
                textZoneW,
                textDrawBandH,
                textCfgForDraw,
                w,
              );
          }
          if (useFieldPlacements)
            this._drawFieldPlacementLines(
              ctx,
              w,
              h,
              lineTextsBySlot,
              primarySlotIndexFromForm,
              fontName,
              fpCfg,
              textCfgForDraw,
              effectiveConfig.text,
              roundTxt,
            );
          if (useCompanion) {
            this._drawCompanionLayout(
              ctx,
              w,
              h,
              dpr,
              cfg,
              lineTextsBySlot,
              lineLocationsBySlot,
              companionPrimarySlotIndex,
              fontName,
              textCfgForDraw,
              effectiveConfig.text,
              roundTxt,
              artworkImageUrl,
              artDrawW,
              artDrawH,
              0,
              0,
              zoneW,
              zoneY + zoneH / 2,
            );
          }
          if (useCompanion && wantsLayoutDebug) {
            if (artImg && artDrawW > 0 && artDrawH > 0) {
              const anchor = zoneY + zoneH / 2;
              const b = this._companionLayoutArtBounds;
              const pos = b
                ? { ax: b.ax, ay: b.ay }
                : this._companionArtAllocationRect(w, 0, cfg.companion, artDrawW, artDrawH, anchor);
              this._strokeArtworkDebugRect(ctx, pos.ax, pos.ay, artDrawW, artDrawH);
              if (artMaxDbg)
                this._strokeArtworkMaxSlotDebugRect(ctx, artMaxDbg.amx, artMaxDbg.amy, artMaxDbg.amw, artMaxDbg.amh);
            }
            if (hasFieldLineText)
              this._strokeCompanionTextColumnBandsDebug(ctx, w, h, cfg.companion, zoneW, zoneH, zoneY + zoneH / 2, 0);
          }
          if (hasText && !useCircularText && wantsLayoutDebug && !useCompanion)
            this._strokeTextLayoutDebugRects(
              ctx,
              zoneX,
              textY,
              zoneW,
              textDrawBandH,
              textBlockForFit,
              layoutTextBlockH,
            );
        }
        if (wrapZoneDraw) ctx.restore();

        if (useCircularText) {
          ctx.save();
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.globalAlpha = cfg.opacity;
          const circDef = YO_ENGRAVING_PREVIEW_DEFAULTS.circularText;
          const circularFontPx = roundTxt(h * (cfg.circularText.fontSize ?? circDef.fontSize));
          this._drawCircularZoneText(ctx, lines, fontName, w, h, textCfgForDraw, cfg.circularText, circularFontPx);
          ctx.restore();
        }
        ctx.globalAlpha = 1;

        ctx.restore();
      }

      _getArtworkImage(url) {
        let img = this._artworkImageCache.get(url);
        if (!img) {
          img = new Image();
          img.crossOrigin = 'anonymous';
          this._artworkImageCache.set(url, img);
          img.onload = () => this.updatePreview();
          img.src = url;
        }
        return img.complete && img.naturalWidth ? img : null;
      }

      _ensureArtworkImage(url) {
        if (!url) return Promise.resolve();
        let img = this._artworkImageCache.get(url);
        if (!img) {
          img = new Image();
          img.crossOrigin = 'anonymous';
          this._artworkImageCache.set(url, img);
          img.src = url;
        }
        if (img.complete && img.naturalWidth) return Promise.resolve(img);
        return new Promise((resolve, reject) => {
          img.onload = () => resolve(img);
          img.onerror = reject;
        });
      }

      _getBackgroundImageSourceFromConfig(cfg) {
        if (this._backgroundUserObjectUrl) return this._backgroundUserObjectUrl;
        if (!cfg?.backgroundImage?.enabled) return null;
        const u = (cfg?.backgroundImage?.url || '').trim();
        return u || null;
      }

      _isBackgroundImageLocalUrl(url) {
        return typeof url === 'string' && /^(blob:|data:)/i.test(url);
      }

      _getBackgroundImage(url) {
        if (!url) return null;
        let img = this._backgroundImageCache.get(url);
        if (!img) {
          img = new Image();
          if (!this._isBackgroundImageLocalUrl(url)) {
            img.crossOrigin = 'anonymous';
          }
          this._backgroundImageCache.set(url, img);
          img.onload = () => this.updatePreview();
          img.src = url;
        }
        return img.complete && img.naturalWidth ? img : null;
      }

      _ensureBackgroundImage(url) {
        if (!url) return Promise.resolve();
        let img = this._backgroundImageCache.get(url);
        if (!img) {
          img = new Image();
          if (!this._isBackgroundImageLocalUrl(url)) {
            img.crossOrigin = 'anonymous';
          }
          this._backgroundImageCache.set(url, img);
          img.src = url;
        }
        if (img.complete && img.naturalWidth) return Promise.resolve(img);
        return new Promise((resolve, reject) => {
          img.onload = () => resolve(img);
          img.onerror = reject;
        });
      }

      _roundRectPath(ctx, x, y, rw, rh, r) {
        const w = Math.max(0, rw);
        const h = Math.max(0, rh);
        const rr = Math.max(0, Math.min(r, w / 2, h / 2));
        if (rr <= 0) {
          ctx.rect(x, y, w, h);
          return;
        }
        if (typeof ctx.roundRect === 'function') {
          ctx.roundRect(x, y, w, h, rr);
          return;
        }
        ctx.moveTo(x + rr, y);
        ctx.arcTo(x + w, y, x + w, y + h, rr);
        ctx.arcTo(x + w, y + h, x, y + h, rr);
        ctx.arcTo(x, y + h, x, y, rr);
        ctx.arcTo(x, y, x + w, y, rr);
        ctx.closePath();
      }

      _normalizeShapeCornerRadii(w, h, rTopLeft, rTopRight, rBottomRight, rBottomLeft) {
        const maxR = Math.min(w / 2, h / 2);
        return {
          topLeft: Math.max(0, Math.min(Number(rTopLeft) || 0, maxR)),
          topRight: Math.max(0, Math.min(Number(rTopRight) || 0, maxR)),
          bottomRight: Math.max(0, Math.min(Number(rBottomRight) || 0, maxR)),
          bottomLeft: Math.max(0, Math.min(Number(rBottomLeft) || 0, maxR)),
        };
      }

      _roundedRectPerCornerPath(ctx, x, y, rw, rh, radii) {
        const w = Math.max(0, rw);
        const h = Math.max(0, rh);
        if (w <= 0 || h <= 0) return;
        const rs = this._normalizeShapeCornerRadii(
          w,
          h,
          radii?.topLeft,
          radii?.topRight,
          radii?.bottomRight,
          radii?.bottomLeft,
        );
        const rTL = rs.topLeft;
        const rTR = rs.topRight;
        const rBR = rs.bottomRight;
        const rBL = rs.bottomLeft;

        ctx.moveTo(x + rTL, y);
        ctx.lineTo(x + w - rTR, y);
        if (rTR > 0) ctx.quadraticCurveTo(x + w, y, x + w, y + rTR);
        else ctx.lineTo(x + w, y);
        ctx.lineTo(x + w, y + h - rBR);
        if (rBR > 0) ctx.quadraticCurveTo(x + w, y + h, x + w - rBR, y + h);
        else ctx.lineTo(x + w, y + h);
        ctx.lineTo(x + rBL, y + h);
        if (rBL > 0) ctx.quadraticCurveTo(x, y + h, x, y + h - rBL);
        else ctx.lineTo(x, y + h);
        ctx.lineTo(x, y + rTL);
        if (rTL > 0) ctx.quadraticCurveTo(x, y, x + rTL, y);
        else ctx.lineTo(x, y);
        ctx.closePath();
      }

      _buildBackgroundShapePath(ctx, x, y, rw, rh, shapeConfig) {
        const w = Math.max(0, rw);
        const h = Math.max(0, rh);
        if (w <= 0 || h <= 0) {
          ctx.rect(x, y, w, h);
          return;
        }
        if (shapeConfig?.oval) {
          ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
          return;
        }
        const radii = {
          topLeft: Number(shapeConfig?.cornerRadiusTopLeft) || 0,
          topRight: Number(shapeConfig?.cornerRadiusTopRight) || 0,
          bottomRight: Number(shapeConfig?.cornerRadiusBottomRight) || 0,
          bottomLeft: Number(shapeConfig?.cornerRadiusBottomLeft) || 0,
        };
        const hasAnyRadius = Object.values(radii).some((v) => v > 0);
        if (hasAnyRadius) {
          this._roundedRectPerCornerPath(ctx, x, y, w, h, radii);
          return;
        }
        ctx.rect(x, y, w, h);
      }

      /**
       * Draw image into an axis-aligned rect with objectFit (logical px).
       */
      _drawImageFittedInRect(ctx, img, bx, by, bw, bh, fit, smallCanvas) {
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        if (!iw || !ih) return;
        let rx = bx;
        let ry = by;
        let rw = bw;
        let rh = bh;
        if (smallCanvas) {
          rx = Math.round(rx);
          ry = Math.round(ry);
          rw = Math.max(1, Math.round(rw));
          rh = Math.max(1, Math.round(rh));
        }
        if (fit === 'stretch') {
          ctx.drawImage(img, rx, ry, rw, rh);
          return;
        }
        const scale = fit === 'contain' ? Math.min(rw / iw, rh / ih) : Math.max(rw / iw, rh / ih);
        let dw = iw * scale;
        let dh = ih * scale;
        let dx = rx + (rw - dw) / 2;
        let dy = ry + (rh - dh) / 2;
        if (smallCanvas) {
          dx = Math.round(dx);
          dy = Math.round(dy);
        }
        ctx.drawImage(img, dx, dy, dw, dh);
      }

      _applyBackgroundImageFiltersToCanvas(canvas, bg) {
        const filters = bg?.filters && typeof bg.filters === 'object' ? bg.filters : {};
        const useSepia = Boolean(filters.sepia);
        const useBlackWhiteTransparent = Boolean(filters.blackWhiteTransparent);
        const useBurntBrown = Boolean(filters.burntBrown);
        const contrast = Number.isFinite(Number(filters.contrast))
          ? Math.min(2, Math.max(0, Number(filters.contrast)))
          : 1;
        const sepiaIntensity = Number.isFinite(Number(filters.sepiaIntensity))
          ? Math.min(1, Math.max(0, Number(filters.sepiaIntensity)))
          : 1;
        const burntBrownIntensity = Number.isFinite(Number(filters.burntBrownIntensity))
          ? Math.min(1, Math.max(0, Number(filters.burntBrownIntensity)))
          : 0.65;
        const useContrast = Math.abs(contrast - 1) > 0.0001;
        if (!useContrast && !useSepia && !useBlackWhiteTransparent && !useBurntBrown) return;
        const ctx = canvas?.getContext?.('2d');
        if (!ctx) return;
        const w = canvas.width;
        const h = canvas.height;
        if (w < 1 || h < 1) return;
        let imageData;
        try {
          imageData = ctx.getImageData(0, 0, w, h);
        } catch (err) {
          console.warn(`${this.ns} Image upload filter skipped`, err);
          return;
        }
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha === 0) continue;
          let r = data[i];
          let g = data[i + 1];
          let b = data[i + 2];
          if (useContrast) {
            r = (r - 128) * contrast + 128;
            g = (g - 128) * contrast + 128;
            b = (b - 128) * contrast + 128;
          }
          if (useSepia) {
            const sr = r * 0.393 + g * 0.769 + b * 0.189;
            const sg = r * 0.349 + g * 0.686 + b * 0.168;
            const sb = r * 0.272 + g * 0.534 + b * 0.131;
            r = r + (Math.min(255, sr) - r) * sepiaIntensity;
            g = g + (Math.min(255, sg) - g) * sepiaIntensity;
            b = b + (Math.min(255, sb) - b) * sepiaIntensity;
          }
          if (useBlackWhiteTransparent) {
            const lum = r * 0.2126 + g * 0.7152 + b * 0.0722;
            r = lum;
            g = lum;
            b = lum;
          }
          if (useBurntBrown) {
            const lum = r * 0.2126 + g * 0.7152 + b * 0.0722;
            const shadowStrength = Math.max(0, Math.min(1, (210 - lum) / 210)) * burntBrownIntensity;
            const targetR = Math.min(255, Math.max(r, 92));
            const targetG = Math.min(255, Math.max(g * 0.72, 48));
            const targetB = Math.min(255, Math.max(b * 0.38, 22));
            r = r + (targetR - r) * shadowStrength;
            g = g + (targetG - g) * shadowStrength;
            b = b + (targetB - b) * shadowStrength;
          }
          data[i] = r;
          data[i + 1] = g;
          data[i + 2] = b;
        }
        ctx.putImageData(imageData, 0, 0);
      }

      _drawActivePreviewImageBackdrop(ctx, w, h) {
        const canvas = this.canvas;
        const activeItem = canvas?.closest('.yo-engraving-preview__item');
        const img = activeItem?.querySelector('img');
        if (!img || !img.complete || !img.naturalWidth || !img.naturalHeight) return false;
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const scale = Math.min(w / iw, h / ih);
        const dw = iw * scale;
        const dh = ih * scale;
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
        return true;
      }

      _drawWarpedBackgroundWithDomBackdropBlend(ctx, warped, w, h, blendMode, alpha) {
        if (!warped || !w || !h) return false;
        const blended = document.createElement('canvas');
        blended.width = Math.max(1, Math.ceil(w));
        blended.height = Math.max(1, Math.ceil(h));
        const bctx = blended.getContext('2d');
        if (!bctx) return false;
        bctx.imageSmoothingEnabled = true;
        bctx.imageSmoothingQuality = 'high';
        if (!this._drawActivePreviewImageBackdrop(bctx, w, h)) return false;
        bctx.save();
        bctx.globalCompositeOperation = blendMode;
        bctx.globalAlpha = 1;
        bctx.drawImage(warped, 0, 0);
        bctx.restore();
        bctx.save();
        bctx.globalCompositeOperation = 'destination-in';
        bctx.drawImage(warped, 0, 0);
        bctx.restore();
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = alpha;
        ctx.drawImage(blended, 0, 0);
        ctx.restore();
        return true;
      }

      /**
       * Map a rectangular canvas texture onto an arbitrary quad (bilinear UV, mesh draw).
       * @param {CanvasRenderingContext2D} destCtx
       * @param {HTMLCanvasElement} sourceCanvas
       * @param {{x:number,y:number}} tl - top-left in dest space
       * @param {{x:number,y:number}} tr
       * @param {{x:number,y:number}} bl
       * @param {{x:number,y:number}} br
       */
      _drawSourceCanvasBilinearMeshToQuad(destCtx, sourceCanvas, tl, tr, bl, br, qualityScale = 1, seamPadPx = 0.65) {
        const srcPxW = sourceCanvas.width;
        const srcPxH = sourceCanvas.height;
        if (srcPxW < 1 || srcPxH < 1) return;
        const q = Number.isFinite(qualityScale) ? Math.max(1, qualityScale) : 1;
        const cols = Math.max(18, Math.min(220, Math.floor((srcPxW / 12) * q)));
        const rows = Math.max(18, Math.min(220, Math.floor((srcPxH / 12) * q)));
        const cellW = srcPxW / cols;
        const cellH = srcPxH / rows;
        const vx = [];
        const vy = [];
        for (let j = 0; j <= rows; j++) {
          vx[j] = [];
          vy[j] = [];
          const v = j / rows;
          const v1 = 1 - v;
          for (let i = 0; i <= cols; i++) {
            const u = i / cols;
            const u1 = 1 - u;
            vx[j][i] = u1 * v1 * tl.x + u * v1 * tr.x + u1 * v * bl.x + u * v * br.x;
            vy[j][i] = u1 * v1 * tl.y + u * v1 * tr.y + u1 * v * bl.y + u * v * br.y;
          }
        }
        // Slight dest overlap hides 1px gaps on skewed quads; callers can tune it for alpha-sensitive passes.
        const seamPad = Math.max(0, Number(seamPadPx) || 0);
        for (let j = 0; j < rows; j++) {
          for (let i = 0; i < cols; i++) {
            const cx = (vx[j][i] + vx[j][i + 1] + vx[j + 1][i] + vx[j + 1][i + 1]) / 4;
            const cy = (vy[j][i] + vy[j][i + 1] + vy[j + 1][i] + vy[j + 1][i + 1]) / 4;
            const dw = (vx[j][i + 1] - vx[j][i] + vx[j + 1][i + 1] - vx[j + 1][i]) / 2;
            const dh = (vy[j + 1][i] - vy[j][i] + vy[j + 1][i + 1] - vy[j][i + 1]) / 2;
            destCtx.drawImage(
              sourceCanvas,
              i * cellW,
              j * cellH,
              cellW,
              cellH,
              cx - dw / 2 - seamPad,
              cy - dh / 2 - seamPad,
              dw + 2 * seamPad,
              dh + 2 * seamPad,
            );
          }
        }
      }

      _drawFeatheredQuadMask(ctx, tl, tr, br, bl, featherPx) {
        const f = Math.max(0, Number(featherPx) || 0);
        if (f <= 0) {
          ctx.fillStyle = '#fff';
          ctx.beginPath();
          ctx.moveTo(tl.x, tl.y);
          ctx.lineTo(tr.x, tr.y);
          ctx.lineTo(br.x, br.y);
          ctx.lineTo(bl.x, bl.y);
          ctx.closePath();
          ctx.fill();
          return;
        }
        const w = ctx.canvas?.width || 0;
        const h = ctx.canvas?.height || 0;
        if (w < 1 || h < 1) return;
        const pad = Math.ceil(f + 2);
        const minX = Math.max(0, Math.floor(Math.min(tl.x, tr.x, br.x, bl.x) - pad));
        const maxX = Math.min(w, Math.ceil(Math.max(tl.x, tr.x, br.x, bl.x) + pad));
        const minY = Math.max(0, Math.floor(Math.min(tl.y, tr.y, br.y, bl.y) - pad));
        const maxY = Math.min(h, Math.ceil(Math.max(tl.y, tr.y, br.y, bl.y) + pad));
        const maskW = Math.max(0, maxX - minX);
        const maskH = Math.max(0, maxY - minY);
        if (maskW < 1 || maskH < 1) return;
        const img = ctx.createImageData(maskW, maskH);
        const data = img.data;
        const edges = [
          [tl, tr],
          [tr, br],
          [br, bl],
          [bl, tl],
        ].map(([a, b]) => {
          const ex = b.x - a.x;
          const ey = b.y - a.y;
          return { a, ex, ey, len: Math.max(0.0001, Math.hypot(ex, ey)) };
        });
        for (let y = 0; y < maskH; y++) {
          const py = minY + y + 0.5;
          for (let x = 0; x < maskW; x++) {
            const px = minX + x + 0.5;
            let alphaFactor = 1;
            let inside = true;
            for (const edge of edges) {
              const signed = (edge.ex * (py - edge.a.y) - edge.ey * (px - edge.a.x)) / edge.len;
              if (signed < 0) {
                inside = false;
                break;
              }
              const t = Math.max(0, Math.min(1, signed / f));
              alphaFactor *= t * t * (3 - 2 * t);
            }
            if (!inside) continue;
            const alpha = Math.round(255 * alphaFactor);
            const idx = (y * maskW + x) * 4;
            data[idx] = 255;
            data[idx + 1] = 255;
            data[idx + 2] = 255;
            data[idx + 3] = alpha;
          }
        }
        ctx.putImageData(img, minX, minY);
      }

      _drawBackgroundImage(ctx, w, h, opts = {}) {
        const cfg = this.config;
        const bg = cfg?.backgroundImage;
        if (!bg) return;
        const srcUrl = this._getBackgroundImageSourceFromConfig(cfg);
        if (!srcUrl) return;
        const img = this._getBackgroundImage(srcUrl);
        if (!img) return;
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        if (!iw || !ih) return;

        const c = bg.corners || {};
        const tl = { x: (Number(c.topLeft?.x) || 0) * w, y: (Number(c.topLeft?.y) || 0) * h };
        const tr = { x: (Number(c.topRight?.x) || 1) * w, y: (Number(c.topRight?.y) || 0) * h };
        const br = { x: (Number(c.bottomRight?.x) || 1) * w, y: (Number(c.bottomRight?.y) || 1) * h };
        const bl = { x: (Number(c.bottomLeft?.x) || 0) * w, y: (Number(c.bottomLeft?.y) || 1) * h };
        const topLen = Math.hypot(tr.x - tl.x, tr.y - tl.y);
        const bottomLen = Math.hypot(br.x - bl.x, br.y - bl.y);
        const leftLen = Math.hypot(bl.x - tl.x, bl.y - tl.y);
        const rightLen = Math.hypot(br.x - tr.x, br.y - tr.y);

        const op = Number(bg.opacity);
        const alpha = Number.isFinite(op) ? Math.min(1, Math.max(0, op)) : 1;
        const fit = String(bg.objectFit || 'cover').toLowerCase();
        const blendMode = typeof bg.blendMode === 'string' && bg.blendMode.trim() ? bg.blendMode.trim() : 'source-over';
        const usesDomBlendBackdrop = Boolean(opts?.useDomBlendBackdrop && blendMode !== 'source-over');
        const isExportRender = Boolean(opts?.isExport);
        const renderScale = usesDomBlendBackdrop ? 1 : Math.max(1, Number(opts?.renderScale) || 1);
        const borderCfg = bg.border && typeof bg.border === 'object' ? bg.border : {};
        const hasOvalClip = Boolean(borderCfg.oval);
        const smallCanvas = w < 200 || h < 200;

        ctx.save();
        if (smallCanvas) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
        }

        const minX = Math.min(tl.x, tr.x, br.x, bl.x);
        const maxX = Math.max(tl.x, tr.x, br.x, bl.x);
        const minY = Math.min(tl.y, tr.y, br.y, bl.y);
        const maxY = Math.max(tl.y, tr.y, br.y, bl.y);
        const bbW = Math.max(1, maxX - minX);
        const bbH = Math.max(1, maxY - minY);
        const minSide = Math.min(bbW, bbH);
        const brCfg = Number(bg.borderRadius);
        const featherCfg = Number(bg.edgeFeather);
        let featherPx = 0;
        if (Number.isFinite(featherCfg) && featherCfg > 0) {
          featherPx = featherCfg * minSide;
        }
        let clipRadiusPx = 0;
        if (Number.isFinite(brCfg) && brCfg > 0) {
          clipRadiusPx = brCfg * minSide;
          clipRadiusPx = Math.min(clipRadiusPx, bbW / 2, bbH / 2);
        }
        const shapeCfg = bg.shape && typeof bg.shape === 'object' ? bg.shape : {};
        const toCornerPx = (fractionValue) => {
          const fraction = Number(fractionValue);
          if (!Number.isFinite(fraction) || fraction <= 0) return 0;
          return Math.min(fraction * minSide, bbW / 2, bbH / 2);
        };
        const cornerRadiiPx = {
          topLeft: toCornerPx(shapeCfg.cornerRadiusTopLeft),
          topRight: toCornerPx(shapeCfg.cornerRadiusTopRight),
          bottomRight: toCornerPx(shapeCfg.cornerRadiusBottomRight),
          bottomLeft: toCornerPx(shapeCfg.cornerRadiusBottomLeft),
        };
        const hasCustomCornerRadii =
          cornerRadiiPx.topLeft > 0 ||
          cornerRadiiPx.topRight > 0 ||
          cornerRadiiPx.bottomRight > 0 ||
          cornerRadiiPx.bottomLeft > 0;
        if (!hasCustomCornerRadii && clipRadiusPx > 0) {
          cornerRadiiPx.topLeft = clipRadiusPx;
          cornerRadiiPx.topRight = clipRadiusPx;
          cornerRadiiPx.bottomRight = clipRadiusPx;
          cornerRadiiPx.bottomLeft = clipRadiusPx;
        }
        const edgeMaxW = Math.max(topLen, bottomLen);
        const edgeMaxH = Math.max(leftLen, rightLen);
        const stretchW = edgeMaxW / bbW;
        const stretchH = edgeMaxH / bbH;
        const stretch = Math.max(1, stretchW, stretchH);
        const hasRoundedClip =
          hasOvalClip ||
          cornerRadiiPx.topLeft > 0 ||
          cornerRadiiPx.topRight > 0 ||
          cornerRadiiPx.bottomRight > 0 ||
          cornerRadiiPx.bottomLeft > 0;
        const hasEdgeFeather = featherPx > 0;
        const uploadSourceScale = Math.max(1.5, Math.min(2.5, renderScale || 1));
        const ss = hasRoundedClip
          ? Math.min(14, Math.max(3, Math.ceil(stretch * 2 * uploadSourceScale)))
          : Math.min(8, Math.max(2, Math.ceil(stretch * 1.5 * uploadSourceScale)));
        const offW = Math.max(1, Math.ceil(bbW * ss));
        const offH = Math.max(1, Math.ceil(bbH * ss));

        const off = document.createElement('canvas');
        off.width = offW;
        off.height = offH;
        const octx = off.getContext('2d');
        if (!octx) {
          ctx.restore();
          return;
        }
        octx.imageSmoothingEnabled = true;
        octx.imageSmoothingQuality = 'high';
        const offSmall = offW < 200 || offH < 200;
        const bgColor = typeof bg.backgroundColor === 'string' ? bg.backgroundColor.trim() : '';
        if (bgColor) {
          octx.save();
          octx.fillStyle = bgColor;
          octx.fillRect(0, 0, offW, offH);
          octx.restore();
        }
        this._drawImageFittedInRect(octx, img, 0, 0, offW, offH, fit, offSmall);
        this._applyBackgroundImageFiltersToCanvas(off, bg);

        let sourceForMesh = off;
        const radiusScale = offW / bbW;
        const shapeForOffscreen = {
          oval: hasOvalClip,
          cornerRadiusTopLeft: Math.min(cornerRadiiPx.topLeft * radiusScale, offW / 2, offH / 2),
          cornerRadiusTopRight: Math.min(cornerRadiiPx.topRight * radiusScale, offW / 2, offH / 2),
          cornerRadiusBottomRight: Math.min(cornerRadiiPx.bottomRight * radiusScale, offW / 2, offH / 2),
          cornerRadiusBottomLeft: Math.min(cornerRadiiPx.bottomLeft * radiusScale, offW / 2, offH / 2),
        };
        if (hasRoundedClip && !hasEdgeFeather) {
          const masked = document.createElement('canvas');
          masked.width = offW;
          masked.height = offH;
          const mctx = masked.getContext('2d');
          if (mctx) {
            mctx.imageSmoothingEnabled = true;
            mctx.imageSmoothingQuality = 'high';
            mctx.beginPath();
            this._buildBackgroundShapePath(mctx, 0, 0, offW, offH, shapeForOffscreen);
            mctx.clip();
            mctx.drawImage(off, 0, 0);
            sourceForMesh = masked;
          }
        }

        const meshQuality =
          hasRoundedClip || hasEdgeFeather
            ? Math.min(6, Math.max(2.5, stretch * 1.7 * uploadSourceScale))
            : Math.min(3.5, Math.max(1.5, stretch * 1.2 * uploadSourceScale));
        const scalePoint = (point) => ({ x: point.x * renderScale, y: point.y * renderScale });
        const drawWarpMesh = (destCtx) => {
          destCtx.save();
          if (renderScale !== 1) destCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
          this._drawSourceCanvasBilinearMeshToQuad(
            destCtx,
            sourceForMesh,
            tl,
            tr,
            bl,
            br,
            meshQuality,
            isExportRender ? 0.25 : 1.25,
          );
          destCtx.restore();
        };
        const drawWarpedToMain = (warped) => {
          if (
            !usesDomBlendBackdrop ||
            !this._drawWarpedBackgroundWithDomBackdropBlend(ctx, warped, w, h, blendMode, alpha)
          ) {
            ctx.drawImage(warped, 0, 0, w, h);
          }
        };
        const drawImageBorder = () => {
          const borderWidthFraction = Number(borderCfg.width);
          const borderWidthPx = Number.isFinite(borderWidthFraction) ? Math.max(0, borderWidthFraction) * minSide : 0;
          const borderGapFraction = Number(borderCfg.gap);
          const borderGapPx = Number.isFinite(borderGapFraction) ? Math.max(0, borderGapFraction) * minSide : 0;
          const borderDoubleWidthFraction = Number(borderCfg.doubleWidth);
          const borderDoubleWidthPx =
            Number.isFinite(borderDoubleWidthFraction) && borderDoubleWidthFraction > 0
              ? borderDoubleWidthFraction * minSide
              : borderWidthPx;
          const borderOpacity = Number(borderCfg.opacity);
          const borderAlpha = Number.isFinite(borderOpacity) ? Math.min(1, Math.max(0, borderOpacity)) : 1;
          const borderColor =
            typeof borderCfg.color === 'string' && borderCfg.color.trim() ? borderCfg.color.trim() : '#ffffff';
          const borderLines = [{ widthPx: borderWidthPx, outsideOffsetPx: -borderWidthPx / 2, color: borderColor }];
          if (borderCfg.double && borderDoubleWidthPx > 0) {
            borderLines.push({
              widthPx: borderDoubleWidthPx,
              outsideOffsetPx: borderGapPx + borderDoubleWidthPx / 2,
              color: borderColor,
            });
          }
          if (!borderCfg.enabled || borderWidthPx <= 0 || borderAlpha <= 0) return;
          if (shapeForOffscreen.oval) {
            const mapUvToQuad = (u, v) => ({
              x: (1 - u) * (1 - v) * tl.x + u * (1 - v) * tr.x + (1 - u) * v * bl.x + u * v * br.x,
              y: (1 - u) * (1 - v) * tl.y + u * (1 - v) * tr.y + (1 - u) * v * bl.y + u * v * br.y,
            });
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            ctx.globalAlpha *= borderAlpha;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            const segments = 256;
            for (const line of borderLines) {
              const radiusU = 0.5 + line.outsideOffsetPx / Math.max(1, bbW);
              const radiusV = 0.5 + line.outsideOffsetPx / Math.max(1, bbH);
              if (radiusU <= 0 || radiusV <= 0) continue;
              ctx.strokeStyle = line.color;
              ctx.lineWidth = line.widthPx;
              ctx.beginPath();
              for (let i = 0; i <= segments; i++) {
                const theta = (i / segments) * Math.PI * 2;
                const u = 0.5 + Math.cos(theta) * radiusU;
                const v = 0.5 + Math.sin(theta) * radiusV;
                const p = mapUvToQuad(u, v);
                if (i === 0) ctx.moveTo(p.x, p.y);
                else ctx.lineTo(p.x, p.y);
              }
              ctx.closePath();
              ctx.stroke();
            }
            ctx.restore();
            return;
          }
          if (hasRoundedClip) {
            const borderScale = Math.min(4, Math.max(2, renderScale * 2));
            const outsidePadPx = borderCfg.double ? borderGapPx + borderDoubleWidthPx + borderWidthPx : borderWidthPx;
            const outsidePadOff = Math.ceil(outsidePadPx * radiusScale * borderScale + 2);
            const borderOff = document.createElement('canvas');
            borderOff.width = Math.max(1, Math.ceil(offW * borderScale + outsidePadOff * 2));
            borderOff.height = Math.max(1, Math.ceil(offH * borderScale + outsidePadOff * 2));
            const borderOffCtx = borderOff.getContext('2d');
            if (!borderOffCtx) return;
            borderOffCtx.imageSmoothingEnabled = true;
            borderOffCtx.imageSmoothingQuality = 'high';
            const borderShape = {
              oval: shapeForOffscreen.oval,
              cornerRadiusTopLeft: 0,
              cornerRadiusTopRight: 0,
              cornerRadiusBottomRight: 0,
              cornerRadiusBottomLeft: 0,
            };
            borderOffCtx.lineJoin = 'round';
            borderOffCtx.lineCap = 'round';
            for (const line of borderLines) {
              const lineWidthOff = Math.max(1, line.widthPx * radiusScale * borderScale);
              const centerOffsetOff = line.outsideOffsetPx * radiusScale * borderScale;
              const insetHere = outsidePadOff - centerOffsetOff;
              borderShape.cornerRadiusTopLeft = Math.max(
                0,
                shapeForOffscreen.cornerRadiusTopLeft * borderScale - insetHere + outsidePadOff,
              );
              borderShape.cornerRadiusTopRight = Math.max(
                0,
                shapeForOffscreen.cornerRadiusTopRight * borderScale - insetHere + outsidePadOff,
              );
              borderShape.cornerRadiusBottomRight = Math.max(
                0,
                shapeForOffscreen.cornerRadiusBottomRight * borderScale - insetHere + outsidePadOff,
              );
              borderShape.cornerRadiusBottomLeft = Math.max(
                0,
                shapeForOffscreen.cornerRadiusBottomLeft * borderScale - insetHere + outsidePadOff,
              );
              borderOffCtx.strokeStyle = line.color;
              borderOffCtx.lineWidth = lineWidthOff;
              borderOffCtx.beginPath();
              this._buildBackgroundShapePath(
                borderOffCtx,
                insetHere,
                insetHere,
                Math.max(0, borderOff.width - insetHere * 2),
                Math.max(0, borderOff.height - insetHere * 2),
                borderShape,
              );
              borderOffCtx.stroke();
            }

            const warpedBorder = document.createElement('canvas');
            warpedBorder.width = Math.max(1, Math.ceil(w * renderScale));
            warpedBorder.height = Math.max(1, Math.ceil(h * renderScale));
            const warpedBorderCtx = warpedBorder.getContext('2d');
            if (!warpedBorderCtx) return;
            warpedBorderCtx.imageSmoothingEnabled = true;
            warpedBorderCtx.imageSmoothingQuality = 'high';
            warpedBorderCtx.save();
            if (renderScale !== 1) warpedBorderCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
            const padU = outsidePadOff / Math.max(1, offW * borderScale);
            const padV = outsidePadOff / Math.max(1, offH * borderScale);
            const mapUvToQuad = (u, v) => ({
              x: (1 - u) * (1 - v) * tl.x + u * (1 - v) * tr.x + (1 - u) * v * bl.x + u * v * br.x,
              y: (1 - u) * (1 - v) * tl.y + u * (1 - v) * tr.y + (1 - u) * v * bl.y + u * v * br.y,
            });
            this._drawSourceCanvasBilinearMeshToQuad(
              warpedBorderCtx,
              borderOff,
              mapUvToQuad(-padU, -padV),
              mapUvToQuad(1 + padU, -padV),
              mapUvToQuad(-padU, 1 + padV),
              mapUvToQuad(1 + padU, 1 + padV),
              meshQuality,
              isExportRender ? 0.25 : 1.25,
            );
            warpedBorderCtx.restore();
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            ctx.globalAlpha *= borderAlpha;
            ctx.drawImage(warpedBorder, 0, 0, w, h);
            ctx.restore();
            return;
          }
          ctx.save();
          ctx.globalCompositeOperation = 'source-over';
          ctx.globalAlpha *= borderAlpha;
          ctx.lineWidth = borderWidthPx;
          ctx.lineJoin = 'round';
          ctx.lineCap = 'round';
          const mapUvToQuad = (u, v) => ({
            x: (1 - u) * (1 - v) * tl.x + u * (1 - v) * tr.x + (1 - u) * v * bl.x + u * v * br.x,
            y: (1 - u) * (1 - v) * tl.y + u * (1 - v) * tr.y + (1 - u) * v * bl.y + u * v * br.y,
          });
          for (const line of borderLines) {
            const expandU = Math.min(0.45, line.outsideOffsetPx / Math.max(1, bbW));
            const expandV = Math.min(0.45, line.outsideOffsetPx / Math.max(1, bbH));
            const qtl = mapUvToQuad(-expandU, -expandV);
            const qtr = mapUvToQuad(1 + expandU, -expandV);
            const qbr = mapUvToQuad(1 + expandU, 1 + expandV);
            const qbl = mapUvToQuad(-expandU, 1 + expandV);
            ctx.strokeStyle = line.color;
            ctx.lineWidth = line.widthPx;
            ctx.beginPath();
            ctx.moveTo(qtl.x, qtl.y);
            ctx.lineTo(qtr.x, qtr.y);
            ctx.lineTo(qbr.x, qbr.y);
            ctx.lineTo(qbl.x, qbl.y);
            ctx.closePath();
            ctx.stroke();
          }
          ctx.restore();
        };
        if (hasEdgeFeather) {
          const warped = document.createElement('canvas');
          warped.width = Math.max(1, Math.ceil(w * renderScale));
          warped.height = Math.max(1, Math.ceil(h * renderScale));
          const wctx = warped.getContext('2d');
          if (wctx) {
            wctx.imageSmoothingEnabled = true;
            wctx.imageSmoothingQuality = 'high';
            drawWarpMesh(wctx);
            const mask = document.createElement('canvas');
            mask.width = warped.width;
            mask.height = warped.height;
            const maskCtx = mask.getContext('2d');
            if (maskCtx) {
              this._drawFeatheredQuadMask(
                maskCtx,
                scalePoint(tl),
                scalePoint(tr),
                scalePoint(br),
                scalePoint(bl),
                featherPx * renderScale,
              );
              wctx.globalCompositeOperation = 'destination-in';
              wctx.drawImage(mask, 0, 0);
              wctx.globalCompositeOperation = 'source-over';
            }
            if (bgColor) {
              ctx.save();
              ctx.globalAlpha *= alpha;
              ctx.fillStyle = bgColor;
              ctx.beginPath();
              ctx.moveTo(tl.x, tl.y);
              ctx.lineTo(tr.x, tr.y);
              ctx.lineTo(br.x, br.y);
              ctx.lineTo(bl.x, bl.y);
              ctx.closePath();
              ctx.fill();
              ctx.restore();
            }
            ctx.save();
            ctx.globalCompositeOperation = blendMode;
            ctx.globalAlpha *= alpha;
            drawWarpedToMain(warped);
            ctx.restore();
          }
        } else {
          if (alpha < 1 || usesDomBlendBackdrop || isExportRender) {
            const warped = document.createElement('canvas');
            warped.width = Math.max(1, Math.ceil(w * renderScale));
            warped.height = Math.max(1, Math.ceil(h * renderScale));
            const wctx = warped.getContext('2d');
            if (wctx) {
              wctx.imageSmoothingEnabled = true;
              wctx.imageSmoothingQuality = 'high';
              drawWarpMesh(wctx);
              ctx.save();
              ctx.globalCompositeOperation = blendMode;
              ctx.globalAlpha *= alpha;
              drawWarpedToMain(warped);
              ctx.restore();
            }
          } else {
            ctx.save();
            ctx.globalCompositeOperation = blendMode;
            this._drawSourceCanvasBilinearMeshToQuad(ctx, sourceForMesh, tl, tr, bl, br, meshQuality);
            ctx.restore();
          }
        }

        drawImageBorder();
        ctx.restore();
      }

      /** Strokes `backgroundImage.corners` in canvas px (debug; config “Show Image Upload Debug Lines”). */
      _drawImageUploadDebugLines(ctx, w, h, isExport = false) {
        const cfg = this.config;
        if (!cfg?.showImageUploadDebugLines || !this.liveDebug || isExport) return;
        const bg = cfg?.backgroundImage;
        if (!bg) return;
        const c = bg.corners || {};
        const tl = { x: (Number(c.topLeft?.x) || 0) * w, y: (Number(c.topLeft?.y) || 0) * h };
        const tr = { x: (Number(c.topRight?.x) || 1) * w, y: (Number(c.topRight?.y) || 0) * h };
        const br = { x: (Number(c.bottomRight?.x) || 1) * w, y: (Number(c.bottomRight?.y) || 1) * h };
        const bl = { x: (Number(c.bottomLeft?.x) || 0) * w, y: (Number(c.bottomLeft?.y) || 1) * h };
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 220, 255, 0.95)';
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 5]);
        ctx.beginPath();
        ctx.moveTo(tl.x, tl.y);
        ctx.lineTo(tr.x, tr.y);
        ctx.lineTo(br.x, br.y);
        ctx.lineTo(bl.x, bl.y);
        ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(0, 140, 180, 0.95)';
        const r = 6;
        for (const p of [tl, tr, br, bl]) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.restore();
      }

      // ---------------------------------------------------------------
      // Artwork
      // ---------------------------------------------------------------

      /**
       * Layout debug outlines (art + text) on the **preview canvas only** — callers must not use this for export
       * renders (`isExport` gates strokes separately).
       * Triggers only in live debug mode (`?debug=1` / `?live-debug=1`) when `showArtworkDebugLines` is not false.
       * When enabled: yellow art bounds, purple = configured artwork max slot, yellow (+ optional cyan) text band,
       * blue/magenta = secondary/primary `text.maxWidth` / `text.primaryMaxWidth` caps (straight & curved stacks only).
       */
      _wantsArtworkDebugOutline(cfg) {
        if (this.liveDebug) return cfg?.showArtworkDebugLines !== false;
        return false;
      }

      /** Yellow dashed rect around the artwork allocation box (same coords as `_drawArtwork`). */
      _strokeArtworkDebugRect(ctx, x, y, w, h) {
        if (
          !Number.isFinite(x) ||
          !Number.isFinite(y) ||
          !Number.isFinite(w) ||
          !Number.isFinite(h) ||
          w <= 0 ||
          h <= 0
        )
          return;
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = 'rgba(255, 230, 0, 1)';
        ctx.setLineDash([6, 4]);
        ctx.lineWidth = 3;
        const sw = Math.max(1, w - 1);
        const sh = Math.max(1, h - 1);
        ctx.strokeRect(x + 0.5, y + 0.5, sw, sh);
        ctx.setLineDash([]);
        ctx.restore();
      }

      /**
       * Text layout debug on main canvas: yellow = band passed to `_drawText` (measured stack for straight text);
       * orange (`textBlockH + curvePad` vs draw band) is commented out — re-enable in the block below if needed;
       * cyan = measured stack when it still differs from the draw band.
       * When `showArtworkDebugLines` / layout debug is on, also stroke blue/magenta max-width caps (see
       * `_strokeTextMaxWidthConstraintGuides`) and the artwork max slot (see `_strokeArtworkMaxSlotDebugRect`).
       */
      _strokeTextLayoutDebugRects(ctx, x, y, w, drawBandH, textBlockForFit, layoutStackH) {
        if (!this.liveDebug) return;
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || w <= 0 || !Number.isFinite(drawBandH))
          return;
        if (drawBandH <= 0) return;
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.lineWidth = 2;
        const sw = Math.max(1, w - 1);
        const shBand = Math.max(1, drawBandH - 1);
        ctx.strokeStyle = 'rgba(255, 220, 0, 0.98)';
        ctx.setLineDash([10, 5]);
        ctx.strokeRect(x + 0.5, y + 0.5, sw, shBand);
        // Orange: textBlockForFit (`textBlockH + curvePad`) vs draw band — disabled per product request.
        // if (Number.isFinite(textBlockForFit) && textBlockForFit > 0 && Math.abs(textBlockForFit - drawBandH) > 0.5) {
        //   ctx.strokeStyle = 'rgba(255, 130, 0, 0.98)';
        //   ctx.setLineDash([4, 4]);
        //   const ins = 3;
        //   const shFit = Math.max(1, textBlockForFit - 1);
        //   ctx.strokeRect(x + ins + 0.5, y + 0.5, Math.max(1, w - 2 * ins - 1), shFit);
        // }
        if (Number.isFinite(layoutStackH) && layoutStackH > 0 && Math.abs(layoutStackH - drawBandH) > 0.5) {
          ctx.strokeStyle = 'rgba(0, 220, 255, 0.92)';
          ctx.setLineDash([2, 3]);
          const ins = 6;
          const shLay = Math.max(1, layoutStackH - 1);
          ctx.strokeRect(x + ins + 0.5, y + 0.5, Math.max(1, w - 2 * ins - 1), shLay);
        }
        ctx.setLineDash([]);
        ctx.restore();
      }

      /**
       * Configured artwork max slot (fractions of full canvas W×H), aligned like the art layout: side = flush to
       * zone edge + vertically centered; top/bottom stack = horizontally centered with top at `artY`; art-only =
       * centered in zone. Distinct from yellow `_strokeArtworkDebugRect` (actual drawn image bounds).
       */
      _strokeArtworkMaxSlotDebugRect(ctx, x, y, w, h) {
        if (!this.liveDebug) return;
        if (
          !Number.isFinite(x) ||
          !Number.isFinite(y) ||
          !Number.isFinite(w) ||
          !Number.isFinite(h) ||
          w <= 0 ||
          h <= 0
        )
          return;
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = 'rgba(186, 85, 255, 0.88)';
        ctx.setLineDash([4, 6]);
        ctx.lineWidth = 2;
        const sw = Math.max(1, w - 1);
        const sh = Math.max(1, h - 1);
        ctx.strokeRect(x + 0.5, y + 0.5, sw, sh);
        ctx.setLineDash([]);
        ctx.restore();
      }

      /**
       * Horizontal caps from `text.primaryMaxWidth` / `text.maxWidth` (same formula as `_computeTextLineSizesForLayout`),
       * centered in the text band. Skipped for vertical columns (those use band height, not these widths).
       */
      _strokeTextMaxWidthConstraintGuides(ctx, zoneX, startY, zoneW, bandH, textCfg, imageW) {
        if (!this.liveDebug) return;
        if (!textCfg || zoneW <= 1 || bandH <= 0) return;
        if (textCfg.vertical === true) return;
        const fullImageW = imageW ?? zoneW;
        let defaultMaxW = textCfg.maxWidth ?? 0.7;
        if (typeof defaultMaxW === 'number' && defaultMaxW > 1) defaultMaxW = defaultMaxW / 1000;
        let primaryFrac = textCfg.primaryMaxWidth ?? defaultMaxW;
        if (typeof primaryFrac === 'number' && primaryFrac > 1) primaryFrac = primaryFrac / 1000;
        const maxLinePrimary = Math.max(1, Math.min(fullImageW * primaryFrac, zoneW));
        const maxLineSecondary = Math.max(1, Math.min(fullImageW * defaultMaxW, zoneW));
        const cx = zoneX + zoneW / 2;
        const strokeCap = (tw, color, dash, inset) => {
          const half = tw / 2;
          const left = cx - half;
          const top = startY + inset;
          const bh = Math.max(1, bandH - 2 * inset);
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.strokeStyle = color;
          ctx.setLineDash(dash);
          ctx.lineWidth = 1.5;
          ctx.strokeRect(left + 0.5, top + 0.5, Math.max(1, tw - 1), Math.max(1, bh - 1));
          ctx.setLineDash([]);
          ctx.restore();
        };
        if (Math.abs(maxLineSecondary - maxLinePrimary) > 1) {
          strokeCap(maxLineSecondary, 'rgba(90, 170, 255, 0.92)', [8, 4], 2);
          strokeCap(maxLinePrimary, 'rgba(255, 90, 200, 0.92)', [3, 4], 5);
        } else {
          strokeCap(maxLinePrimary, 'rgba(90, 170, 255, 0.92)', [8, 4], 2);
        }
      }

      _drawArtwork(ctx, url, x, y, w, h, dpr = 1) {
        const img = this._getArtworkImage(url);
        if (!img) return;
        const scale = Math.min(w / img.naturalWidth, h / img.naturalHeight, 1);
        let dw = img.naturalWidth * scale;
        let dh = img.naturalHeight * scale;
        let dx = x + (w - dw) / 2;
        let dy = y + (h - dh) / 2;
        if (w < 200 || h < 200) {
          dx = Math.round(dx);
          dy = Math.round(dy);
          dw = Math.max(1, Math.round(dw));
          dh = Math.max(1, Math.round(dh));
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
        }
        const cfg = this.config;
        const tw = Math.ceil(dw);
        const th = Math.ceil(dh);
        const scalePx = Math.max(1, Math.round(dpr));
        const maskW = tw * scalePx;
        const maskH = th * scalePx;
        const source = cfg.artworkBgRemoval.enabled
          ? this._stripWhiteBackground(img, maskW, maskH, cfg.artworkBgRemoval)
          : img;
        const curve = cfg.artworkCurve ?? 0;
        const curveZoneW = Math.max(1, maskW * (cfg.artworkCurveZoneWidth ?? 1));
        const maxShift = curve !== 0 ? Math.abs(curve) * curveZoneW * 0.04 : 0;
        const tMax = curve !== 0 ? maskW / curveZoneW : 0;
        const t2 = tMax * tMax;
        const cOff = curve !== 0 ? (2 / 3) * maxShift : 0;
        let actualMinYShift = 0;
        let actualMaxYShift = 0;
        if (curve > 0) {
          actualMinYShift = -maxShift / 3;
          actualMaxYShift = maxShift * (t2 - 1) + cOff;
        } else if (curve < 0) {
          actualMinYShift = maxShift * (1 - t2) - cOff;
          actualMaxYShift = maxShift / 3;
        }
        const contentHeight = curve !== 0 ? actualMaxYShift - actualMinYShift + maskH : maskH;
        const maskHCurve = curve !== 0 ? Math.ceil(contentHeight + 2) : maskH;
        const mask = document.createElement('canvas');
        mask.width = maskW;
        mask.height = maskHCurve;
        const mctx = mask.getContext('2d');
        if (!mctx) return;
        mctx.imageSmoothingEnabled = true;
        mctx.imageSmoothingQuality = 'high';
        if (curve !== 0) {
          const zoneCenterX = maskW / 2;
          const drawOffsetY = -actualMinYShift;
          this._drawImageCurve(mctx, source, 0, drawOffsetY, maskW, maskH, curve, curveZoneW, zoneCenterX);
        } else {
          const iw = source.naturalWidth ?? source.width;
          const ih = source.naturalHeight ?? source.height;
          if (iw && ih) mctx.drawImage(source, 0, 0, iw, ih, 0, 0, maskW, maskH);
        }
        const data = mctx.getImageData(0, 0, maskW, maskHCurve);
        const d = data.data;
        const fillColor = cfg.text?.fillColor ?? '#1a1a1a';
        const [r, g, b] = this._parseColorToRgb(fillColor, 1.08);
        for (let i = 0; i < d.length; i += 4) {
          d[i] = Math.min(255, r);
          d[i + 1] = Math.min(255, g);
          d[i + 2] = Math.min(255, b);
        }
        mctx.putImageData(data, 0, 0);
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        if (curve !== 0) {
          ctx.drawImage(mask, 0, 0, maskW, contentHeight, dx, dy, dw, dh);
        } else {
          ctx.drawImage(mask, 0, 0, maskW, maskH, dx, dy, dw, dh);
        }
        ctx.restore();
      }

      /**
       * Parse any CSS color to [r,g,b], optionally darkened. Works with hex, rgb(), and named colors.
       * @param {string} color - CSS color
       * @param {number} [factor=1] - multiply each channel (e.g. 0.5 = darker)
       * @returns {[number,number,number]} [r,g,b] 0–255
       */
      _parseColorToRgb(color, factor) {
        if (!color || typeof color !== 'string') return [26, 26, 26];
        const hex6 = color.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
        if (hex6) {
          const r = Math.round(parseInt(hex6[1], 16) * (factor ?? 1));
          const g = Math.round(parseInt(hex6[2], 16) * (factor ?? 1));
          const b = Math.round(parseInt(hex6[3], 16) * (factor ?? 1));
          return [r, g, b];
        }
        const hex3 = color.match(/^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i);
        if (hex3) {
          const r = Math.round(parseInt(hex3[1] + hex3[1], 16) * (factor ?? 1));
          const g = Math.round(parseInt(hex3[2] + hex3[2], 16) * (factor ?? 1));
          const b = Math.round(parseInt(hex3[3] + hex3[3], 16) * (factor ?? 1));
          return [r, g, b];
        }
        const rgb = color.match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
        if (rgb) {
          const r = Math.round(Number(rgb[1]) * (factor ?? 1));
          const g = Math.round(Number(rgb[2]) * (factor ?? 1));
          const b = Math.round(Number(rgb[3]) * (factor ?? 1));
          return [Math.min(255, r), Math.min(255, g), Math.min(255, b)];
        }
        const c = document.createElement('canvas');
        c.width = 1;
        c.height = 1;
        const ctx = c.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#1a1a1a';
          ctx.fillStyle = color;
          ctx.fillRect(0, 0, 1, 1);
          const p = ctx.getImageData(0, 0, 1, 1).data;
          const f = factor ?? 1;
          return [Math.round(p[0] * f), Math.round(p[1] * f), Math.round(p[2] * f)];
        }
        return [26, 26, 26];
      }

      _stripWhiteBackground(img, dw, dh, bgr) {
        const lineArt = bgr.lineArtMode;
        const w = lineArt ? Math.round(img.naturalWidth) : Math.round(dw);
        const h = lineArt ? Math.round(img.naturalHeight) : Math.round(dh);
        const key = `${img.src}_${w}_${h}_${lineArt ? 'line' : 'fade'}`;
        if (this._artworkNoWhiteCache && this._artworkNoWhiteCache.key === key) return this._artworkNoWhiteCache.canvas;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const c = canvas.getContext('2d');
        if (!c) return img;
        c.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, w, h);
        const data = c.getImageData(0, 0, w, h);
        const d = data.data;
        if (lineArt) {
          const t = bgr.threshold ?? 230;
          for (let i = 0; i < d.length; i += 4) {
            if (d[i] > t && d[i + 1] > t && d[i + 2] > t) d[i + 3] = 0;
          }
        } else {
          for (let i = 0; i < d.length; i += 4) {
            const r = d[i];
            const g = d[i + 1];
            const b = d[i + 2];
            const avg = (r + g + b) / 3;
            const maxCh = Math.max(r, g, b);
            if (avg >= bgr.removeThreshold || maxCh >= bgr.maxChannelRemove) {
              d[i + 3] = 0;
            } else if (avg >= bgr.fadeStart || maxCh >= bgr.maxChannelFade) {
              const tAvg =
                bgr.removeThreshold - bgr.fadeStart ? (avg - bgr.fadeStart) / (bgr.removeThreshold - bgr.fadeStart) : 1;
              const tMax =
                bgr.maxChannelRemove - bgr.maxChannelFade
                  ? (maxCh - bgr.maxChannelFade) / (bgr.maxChannelRemove - bgr.maxChannelFade)
                  : 1;
              const t = Math.min(1, Math.max(tAvg, tMax));
              d[i + 3] = Math.round(d[i + 3] * (1 - t));
            }
          }
        }
        c.putImageData(data, 0, 0);
        this._artworkNoWhiteCache = { key, canvas };
        return canvas;
      }

      /**
       * @param {number} curveZoneW - width of the curve zone (reference for amplitude and t).
       * @param {number} zoneCenterX - center X of the curve zone (for t calculation).
       */
      _drawImageCurve(ctx, img, dx, dy, dw, dh, curve, curveZoneW, zoneCenterX) {
        const iw = img.naturalWidth ?? img.width;
        const ih = img.naturalHeight ?? img.height;
        if (!iw || !ih) return;

        const strips = Math.max(30, Math.ceil(dw / 2));
        const maxShift = Math.abs(curve) * curveZoneW * 0.04;
        const halfRef = curveZoneW / 2;
        const centerOffset = (2 / 3) * maxShift;

        for (let j = 0; j < strips; j++) {
          const srcX = Math.round((j * iw) / strips);
          const srcXNext = Math.round(((j + 1) * iw) / strips);
          const destX = Math.round(dx + (j * dw) / strips);
          const destXNext = Math.round(dx + ((j + 1) * dw) / strips);
          const stripCenterX = (destX + destXNext) / 2;
          const t = (stripCenterX - zoneCenterX) / halfRef;
          let yShift = curve > 0 ? maxShift * (t * t - 1) : maxShift * (1 - t * t);
          yShift += curve > 0 ? centerOffset : -centerOffset;
          ctx.drawImage(img, srcX, 0, srcXNext - srcX, ih, destX, dy + yShift, destXNext - destX, dh);
        }
      }

      // ---------------------------------------------------------------
      // Text
      // ---------------------------------------------------------------

      /**
       * Effective line width at font size fs: straight = measureText; curved = prefixW + letterSpacing (matches _drawTextArc).
       */
      _measureLineWidthAtFontSize(ctx, line, fs, fontFamily, cfg, textCurve) {
        ctx.font = `${fs}px ${fontFamily}`;
        if (textCurve === 0 || !Number.isFinite(textCurve)) {
          return ctx.measureText(line).width;
        }
        let glyphs;
        if (typeof Intl !== 'undefined' && Intl.Segmenter) {
          glyphs = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(line)].map((x) => x.segment);
        } else {
          glyphs = [];
          for (let i = 0; i < line.length; ) {
            const cp = line.codePointAt(i);
            const ch = String.fromCodePoint(cp);
            glyphs.push(ch);
            i += ch.length;
          }
        }
        const nG = glyphs.length;
        if (nG === 0) return 0;
        const prefixW = [0];
        let accG = '';
        for (let g = 0; g < nG; g++) {
          accG += glyphs[g];
          prefixW.push(ctx.measureText(accG).width);
        }
        const ls = Number(cfg.letterSpacing) || 0;
        return prefixW[nG] + Math.max(0, nG - 1) * ls;
      }

      _segmentGlyphs(line) {
        if (!line) return [];
        if (typeof Intl !== 'undefined' && Intl.Segmenter) {
          return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(line)].map((x) => x.segment);
        }
        const out = [];
        for (let i = 0; i < line.length; ) {
          const cp = line.codePointAt(i);
          const ch = String.fromCodePoint(cp);
          out.push(ch);
          i += ch.length;
        }
        return out;
      }

      /**
       * Vertical column: height = grapheme count × (fontSize × lineHeight) + gaps between graphemes;
       * width = max glyph advance. For shrink-to-fit height, callers pass lineHeight 1 so font size does not
       * shrink when the user only increases display line height (spacing); draw uses the real lineHeight.
       */
      _measureVerticalColumnSpan(ctx, glyphs, fs, fontFamily, ls, lineHeight = 1) {
        ctx.font = `${fs}px ${fontFamily}`;
        const nG = glyphs.length;
        if (nG === 0) return { height: 0, width: 0 };
        const lh = Math.max(0.5, Number(lineHeight) || 1);
        const rowStep = fs * lh;
        const gap = Number(ls) || 0;
        let maxW = 0;
        for (let g = 0; g < nG; g++) {
          maxW = Math.max(maxW, ctx.measureText(glyphs[g]).width);
        }
        const totalH = nG * rowStep + Math.max(0, nG - 1) * gap;
        return { height: totalH, width: maxW };
      }

      /**
       * Same sizing as _drawText (shrink-to-fit per line). textBandH matches zoneH passed into _drawText for smallZone rounding.
       * textCurve: when non-zero, effective width uses arc metric (prefixW + letterSpacing) for exact fit.
       * nominalTextCfg: when provided (unscaled text config), shrink decision uses nominal max font size so primary shrinks even after fitScale.
       */
      _computeTextLineSizesForLayout(
        ctx,
        lines,
        primaryIndex,
        fontName,
        zoneW,
        textBandH,
        cfg,
        imageW,
        textCurve,
        nominalTextCfg,
      ) {
        const vertical = cfg?.vertical === true;
        const curve = vertical ? 0 : (textCurve ?? this.config.textCurve ?? 0);
        const fontFamily =
          fontName && this._fontCache.has(fontName) ? `"${fontName}", ${cfg.fallbackFont}` : cfg.fallbackFont;
        let defaultMaxW = cfg.maxWidth ?? 0.7;
        if (typeof defaultMaxW === 'number' && defaultMaxW > 1) defaultMaxW = defaultMaxW / 1000;
        const fullImageW = imageW ?? zoneW;
        const smallZone = zoneW < 200 || textBandH < 200;
        const roundFs = (n) => (smallZone ? Math.max(1, Math.round(n)) : n);
        const lsPx = Number(cfg.letterSpacing) || 0;
        const verticalLetterGap = vertical ? Number(cfg.verticalLetterSpacing) || 0 : 0;
        const verticalLh = Math.max(0.5, Number(cfg.lineHeight) || 1);
        /** Height fit uses lh=1 so line height only adds spacing at draw time, not smaller fonts by default. */
        const verticalFitLh = 1;
        ctx.save();
        ctx.textBaseline = 'alphabetic';
        let lineSizes = lines.map((line, i) => {
          const isPrimary = i === primaryIndex;
          let frac = isPrimary ? (cfg.primaryMaxWidth ?? defaultMaxW) : defaultMaxW;
          if (typeof frac === 'number' && frac > 1) frac = frac / 1000;
          const maxLineW = Math.max(1, Math.min(fullImageW * frac, zoneW));
          /** Vertical: height budget is the text band only (maxWidth fractions are for horizontal line width, not column height). */
          const maxColumnH = Math.max(1, textBandH);
          const maxExtent = vertical ? maxColumnH : maxLineW;
          const maxFs = isPrimary ? cfg.primaryMaxFontSize : cfg.secondaryMaxFontSize;
          const minFs = isPrimary ? cfg.primaryMinFontSize : cfg.secondaryMinFontSize;
          const glyphs = vertical ? this._segmentGlyphs(line) : null;
          const measure = vertical
            ? (fs) =>
                glyphs.length === 0
                  ? 0
                  : this._measureVerticalColumnSpan(ctx, glyphs, fs, fontFamily, verticalLetterGap, verticalFitLh)
                      .height
            : (fs) => this._measureLineWidthAtFontSize(ctx, line, fs, fontFamily, cfg, curve);
          const nominalMaxFs = nominalTextCfg
            ? isPrimary
              ? nominalTextCfg.primaryMaxFontSize
              : nominalTextCfg.secondaryMaxFontSize
            : maxFs;
          const nominalFs = Number.isFinite(nominalMaxFs) ? nominalMaxFs : maxFs;
          const overAtNominal = measure(nominalFs) > maxExtent;
          const overAtMax = measure(maxFs) > maxExtent;
          let fs;
          if (!overAtNominal) {
            fs = Math.max(minFs, Math.min(maxFs, maxFs));
          } else if (!overAtMax) {
            fs = maxFs;
          } else if (measure(minFs) > maxExtent) {
            fs = minFs;
          } else {
            let lo = minFs;
            let hi = maxFs;
            for (let k = 0; k < 40; k++) {
              const mid = (lo + hi) / 2;
              if (measure(mid) <= maxExtent) lo = mid;
              else hi = mid;
            }
            fs = lo;
            if (!smallZone) {
              for (let s = 0; s < 8 && fs > minFs && measure(fs) > maxExtent; s++) fs *= 0.9995;
            }
            fs = Math.max(minFs, Math.min(maxFs, fs));
          }
          if (smallZone) {
            if (overAtNominal && overAtMax && measure(minFs) <= maxExtent) {
              let out = Math.max(minFs, Math.min(maxFs, Math.round(fs * 4) / 4));
              for (let s = 0; s < 20 && out > minFs && measure(out) > maxExtent; s++) out = Math.max(minFs, out - 0.25);
              return out;
            }
            return Math.max(minFs, Math.min(maxFs, roundFs(fs)));
          }
          return fs;
        });
        if (vertical && lines.length > 0) {
          const gapPx = cfg.gap ?? 0;
          const primaryGapPx = cfg.primaryGap ?? 0;
          const baseMargin = 2 * gapPx;
          const primaryMargin = gapPx + primaryGapPx;
          let columnWidths = lines.map((line, i) => {
            const g = this._segmentGlyphs(line);
            const fs = lineSizes[i];
            return this._measureVerticalColumnSpan(ctx, g, fs, fontFamily, verticalLetterGap, verticalLh).width;
          });
          const totalBlockW = (() => {
            const { offsets: xOff, getSlotH: getColW } = this._computeLineYOffsets(
              lines,
              primaryIndex,
              columnWidths,
              primaryMargin,
              baseMargin,
            );
            if (lines.length === 0) return 0;
            const w0 = getColW(0);
            const wLast = getColW(lines.length - 1);
            return xOff[xOff.length - 1] + w0 / 2 + wLast / 2;
          })();
          if (totalBlockW > zoneW && totalBlockW > 0) {
            let factor = zoneW / totalBlockW;
            for (let iter = 0; iter < 28 && factor < 0.999; iter++) {
              lineSizes = lineSizes.map((fs, i) => {
                const isPrimary = i === primaryIndex;
                const minFs = isPrimary ? cfg.primaryMinFontSize : cfg.secondaryMinFontSize;
                return Math.max(minFs, fs * factor);
              });
              for (let i = 0; i < lines.length; i++) {
                columnWidths[i] = this._measureVerticalColumnSpan(
                  ctx,
                  this._segmentGlyphs(lines[i]),
                  lineSizes[i],
                  fontFamily,
                  verticalLetterGap,
                  verticalLh,
                ).width;
              }
              const { offsets: xOff2, getSlotH: getColW2 } = this._computeLineYOffsets(
                lines,
                primaryIndex,
                columnWidths,
                primaryMargin,
                baseMargin,
              );
              const w0 = getColW2(0);
              const wLast = getColW2(lines.length - 1);
              const tw = xOff2[xOff2.length - 1] + w0 / 2 + wLast / 2;
              if (tw <= zoneW) break;
              factor = Math.max(0.88, zoneW / tw);
            }
          }
          let maxColH = 0;
          for (let vi = 0; vi < lines.length; vi++) {
            const h = this._measureVerticalColumnSpan(
              ctx,
              this._segmentGlyphs(lines[vi]),
              lineSizes[vi],
              fontFamily,
              verticalLetterGap,
              verticalLh,
            ).height;
            maxColH = Math.max(maxColH, h);
          }
          if (maxColH > textBandH + 0.5 && maxColH > 0) {
            const s = Math.min(1, textBandH / maxColH);
            lineSizes = lineSizes.map((fs, i) => {
              const isPrimary = i === primaryIndex;
              const minFs = isPrimary ? cfg.primaryMinFontSize : cfg.secondaryMinFontSize;
              return Math.max(minFs, fs * s);
            });
          }
        }
        ctx.restore();
        return lineSizes;
      }

      _textStackHeightFromLineSizes(lines, primaryIndex, lineSizes, textCfg, ctx, fontName) {
        if (textCfg?.vertical === true && ctx) {
          const fontFamily =
            fontName && this._fontCache.has(fontName) ? `"${fontName}", ${textCfg.fallbackFont}` : textCfg.fallbackFont;
          const verticalLetterGap = Number(textCfg.verticalLetterSpacing) || 0;
          const verticalLh = Math.max(0.5, Number(textCfg.lineHeight) || 1);
          let maxColH = 0;
          ctx.save();
          ctx.textBaseline = 'alphabetic';
          for (let i = 0; i < lines.length; i++) {
            const glyphs = this._segmentGlyphs(lines[i]);
            const fs = lineSizes[i];
            const { height } = this._measureVerticalColumnSpan(
              ctx,
              glyphs,
              fs,
              fontFamily,
              verticalLetterGap,
              verticalLh,
            );
            maxColH = Math.max(maxColH, height);
          }
          ctx.restore();
          return maxColH;
        }
        const gapPx = textCfg.gap ?? 0;
        const primaryGapPx = textCfg.primaryGap ?? 0;
        const baseMargin = 2 * gapPx;
        const primaryMargin = gapPx + primaryGapPx;
        const { offsets: yOffsets, getSlotH } = this._computeLineYOffsets(
          lines,
          primaryIndex,
          lineSizes,
          primaryMargin,
          baseMargin,
        );
        const slot0 = getSlotH(0);
        const slotLast = getSlotH(lines.length - 1);
        return yOffsets[yOffsets.length - 1] + slot0 / 2 + slotLast / 2;
      }

      /**
       * Compute per-line font sizes: primary line uses primaryMaxFontSize,
       * other lines use secondaryMaxFontSize. Each shrinks to fit maxWidth (never exceed zone).
       * @param {Object} [textCfgOverride] - Scaled text config (e.g. effectiveConfig.text); when provided, used instead of this.config.text.
       * @param {number[]|null} [lineSizesPre] - When set (same length as lines), skips recomputing sizes so draw band matches main layout pass.
       */
      _drawText(
        ctx,
        lines,
        primaryIndex,
        fontName,
        zoneX,
        startY,
        zoneW,
        zoneH,
        textCfgOverride,
        imageW,
        nominalTextCfg,
        lineSizesPre,
      ) {
        const cfg = textCfgOverride ?? this.config.text;
        const fontFamily =
          fontName && this._fontCache.has(fontName) ? `"${fontName}", ${cfg.fallbackFont}` : cfg.fallbackFont;
        const fullImageW = imageW ?? zoneW;

        ctx.save();
        ctx.textBaseline = 'middle';
        ctx.fillStyle = cfg.fillColor;
        ctx.strokeStyle = cfg.strokeColor;
        const blur = cfg.shadowBlur ?? 0;
        if (blur > 0) {
          ctx.shadowColor = cfg.shadowColor ?? 'rgba(0,0,0,0.3)';
          ctx.shadowOffsetX = cfg.shadowOffsetX ?? 0;
          ctx.shadowOffsetY = cfg.shadowOffsetY ?? 0;
          ctx.shadowBlur = blur;
        }

        const lineSizes =
          Array.isArray(lineSizesPre) && lineSizesPre.length === lines.length
            ? lineSizesPre
            : this._computeTextLineSizesForLayout(
                ctx,
                lines,
                primaryIndex,
                fontName,
                zoneW,
                zoneH,
                cfg,
                fullImageW,
                this.config.textCurve,
                nominalTextCfg,
              );

        const gapPx = cfg.gap ?? 0;
        const primaryGapPx = cfg.primaryGap ?? 0;
        const baseMargin = 2 * gapPx;
        const primaryMargin = gapPx + primaryGapPx;
        const curve = this.config.textCurve ?? 0;
        const useArc = curve !== 0 && cfg?.vertical !== true;

        if (useArc) {
          this._drawTextArc(
            ctx,
            lines,
            lineSizes,
            primaryIndex,
            fontFamily,
            zoneX,
            startY,
            zoneW,
            zoneH,
            curve,
            cfg,
            baseMargin,
            primaryMargin,
            fullImageW,
          );
        } else {
          this._drawTextStraight(
            ctx,
            lines,
            lineSizes,
            primaryIndex,
            fontFamily,
            zoneX,
            startY,
            zoneW,
            zoneH,
            cfg,
            baseMargin,
            primaryMargin,
          );
        }
        ctx.restore();
      }

      /** Unitless line-height multiplier for stack layout (not CSS line-height on ctx). */
      _normalizeTextLineHeight(v) {
        let lh = Number(v);
        if (!Number.isFinite(lh) || lh <= 0) return 1;
        return Math.min(4, Math.max(0.05, lh));
      }

      /** primaryMargin = gap + primaryGap (space next to primary); baseMargin = 2*gap (space between secondaries). */
      _computeLineYOffsets(lines, primaryIndex, lineSizes, primaryMargin, baseMargin) {
        const getSlotH = (i) => lineSizes[i] ?? 0;
        const offsets = [0];
        for (let i = 1; i < lines.length; i++) {
          const prevSlot = getSlotH(i - 1);
          const currSlot = getSlotH(i);
          const margin = i === primaryIndex || i - 1 === primaryIndex ? primaryMargin : baseMargin;
          const gap = prevSlot / 2 + margin + currSlot / 2;
          offsets.push(offsets[i - 1] + gap);
        }
        return { offsets, getSlotH };
      }

      /**
       * Vertical slack so curved + baseline-skewed text is not clipped at zone/offscreen top (ends sit higher than center).
       */
      _estimateTextArcLayoutPad(zoneW, curve, fontSizePx) {
        let c = Number(curve);
        if (!Number.isFinite(c) || Math.abs(c) < 1e-6) return 0;
        if (Math.abs(c) > 1) c /= 1000;
        const absC = Math.max(0.02, Math.min(1, Math.abs(c)));
        const R = Math.max(8, zoneW / (2 * absC));
        const halfW = Math.min(zoneW * 0.49, R * 0.95);
        const th = Math.asin(Math.max(-1, Math.min(1, halfW / R)));
        const lift = R * (1 - Math.cos(th));
        const fs = Math.max(8, Number(fontSizePx) || 16);
        const skewExtra = Math.min(3.5, Math.abs(Math.tan(th))) * fs * 0.2;
        return Math.min(Math.ceil(zoneW * 0.055), Math.ceil(lift + skewExtra + 5));
      }

      _drawTextVerticalColumns(
        ctx,
        lines,
        lineSizes,
        primaryIndex,
        fontFamily,
        zoneX,
        startY,
        zoneW,
        zoneH,
        textCfg,
        baseMargin,
        primaryMargin,
      ) {
        const cfg = textCfg ?? this.config.text;
        const verticalLetterGap = Number(cfg.verticalLetterSpacing) || 0;
        const verticalLh = Math.max(0.5, Number(cfg.lineHeight) || 1);
        const columnWidths = lines.map((line, i) => {
          const g = this._segmentGlyphs(line);
          const fs = lineSizes[i];
          return this._measureVerticalColumnSpan(ctx, g, fs, fontFamily, verticalLetterGap, verticalLh).width;
        });
        const columnHeights = lines.map((line, i) => {
          const g = this._segmentGlyphs(line);
          const fs = lineSizes[i];
          return this._measureVerticalColumnSpan(ctx, g, fs, fontFamily, verticalLetterGap, verticalLh).height;
        });
        const { offsets: xOffsets } = this._computeLineYOffsets(
          lines,
          primaryIndex,
          columnWidths,
          primaryMargin,
          baseMargin,
        );
        const w0 = columnWidths[0] ?? 0;
        const wLast = columnWidths[lines.length - 1] ?? 0;
        const totalW = lines.length === 0 ? 0 : xOffsets[lines.length - 1] + w0 / 2 + wLast / 2;
        const maxH = columnHeights.length ? Math.max(...columnHeights) : 0;
        const smallZone = zoneW < 200 || zoneH < 200;
        const roundPx = (n) => (smallZone ? Math.round(n) : n);
        const blockLeft = zoneX + (zoneW - totalW) / 2;
        const baseY = startY + (zoneH - maxH) / 2;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const strokeW = cfg.strokeWidth ?? 0;
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const fs = lineSizes[i];
          const glyphs = this._segmentGlyphs(line);
          if (glyphs.length === 0) continue;
          ctx.font = `${fs}px ${fontFamily}`;
          const cx = blockLeft + w0 / 2 + xOffsets[i];
          const rowStep = fs * verticalLh;
          for (let g = 0; g < glyphs.length; g++) {
            const ch = glyphs[g];
            const cy = baseY + g * (rowStep + verticalLetterGap) + rowStep / 2;
            if (strokeW > 0) {
              ctx.lineWidth = Math.max(1, fs * strokeW);
              ctx.strokeText(ch, roundPx(cx), roundPx(cy));
            }
            ctx.fillText(ch, roundPx(cx), roundPx(cy));
          }
        }
      }

      _drawTextStraight(
        ctx,
        lines,
        lineSizes,
        primaryIndex,
        fontFamily,
        zoneX,
        startY,
        zoneW,
        zoneH,
        textCfg,
        baseMargin,
        primaryMargin,
      ) {
        const cfg = textCfg ?? this.config.text;
        if (cfg?.vertical === true) {
          this._drawTextVerticalColumns(
            ctx,
            lines,
            lineSizes,
            primaryIndex,
            fontFamily,
            zoneX,
            startY,
            zoneW,
            zoneH,
            cfg,
            baseMargin,
            primaryMargin,
          );
          return;
        }
        const { offsets: yOffsets, getSlotH } = this._computeLineYOffsets(
          lines,
          primaryIndex,
          lineSizes,
          primaryMargin,
          baseMargin,
        );
        const slot0 = getSlotH(0);
        let y = startY + slot0 / 2;
        const x = zoneX + zoneW / 2;
        const smallZone = zoneW < 200 || zoneH < 200;
        const roundPx = (n) => (smallZone ? Math.round(n) : n);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        for (let i = 0; i < lines.length; i++) {
          const fs = lineSizes[i];
          ctx.font = `${fs}px ${fontFamily}`;
          const slotCenter = y + yOffsets[i];
          const m = ctx.measureText(lines[i]);
          const isPrimary = i === primaryIndex;
          const asc = isPrimary
            ? (m.actualBoundingBoxAscent ?? fs * 0.8)
            : (m.fontBoundingBoxAscent ?? m.actualBoundingBoxAscent ?? fs * 0.8);
          const desc = isPrimary
            ? (m.actualBoundingBoxDescent ?? fs * 0.2)
            : (m.fontBoundingBoxDescent ?? m.actualBoundingBoxDescent ?? fs * 0.2);
          const ly = slotCenter + (asc - desc) / 2;
          const strokeW = cfg.strokeWidth ?? 0;
          if (strokeW > 0) {
            ctx.lineWidth = Math.max(1, fs * strokeW);
            ctx.strokeText(lines[i], roundPx(x), roundPx(ly));
          }
          ctx.fillText(lines[i], roundPx(x), roundPx(ly));
        }
      }

      _drawTextArc(
        ctx,
        lines,
        lineSizes,
        primaryIndex,
        fontFamily,
        zoneX,
        startY,
        zoneW,
        zoneH,
        curve,
        textCfg,
        baseMargin,
        primaryMargin,
        imageW,
      ) {
        const cfg = textCfg ?? this.config.text;
        let c = Number(curve);
        if (!Number.isFinite(c)) c = 0;
        if (Math.abs(c) > 1) c = c / 1000;
        const cx = zoneX + zoneW / 2;
        const sign = c > 0 ? 1 : -1;
        const { offsets: yOffsets, getSlotH } = this._computeLineYOffsets(
          lines,
          primaryIndex,
          lineSizes,
          primaryMargin,
          baseMargin,
        );
        const slot0 = getSlotH(0);
        const cyBase = startY + slot0 / 2;
        const smallZone = zoneW < 200 || zoneH < 200;
        const roundPx = (n) => (smallZone ? Math.round(n) : n);
        ctx.textAlign = 'center';
        const debugCurve = this.liveDebug && this.config.debugCurveLines === true;

        const strokeW = cfg.strokeWidth ?? 0;
        ctx.textBaseline = 'alphabetic';
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const fs = lineSizes[i];
          ctx.font = `${fs}px ${fontFamily}`;
          const slotCenter = cyBase + yOffsets[i];
          const mLine = ctx.measureText(line);
          const isPrimary = i === primaryIndex;
          const asc = isPrimary
            ? (mLine.actualBoundingBoxAscent ?? fs * 0.8)
            : (mLine.fontBoundingBoxAscent ?? mLine.actualBoundingBoxAscent ?? fs * 0.8);
          const desc = isPrimary
            ? (mLine.actualBoundingBoxDescent ?? fs * 0.2)
            : (mLine.fontBoundingBoxDescent ?? mLine.actualBoundingBoxDescent ?? fs * 0.2);
          /* Alphabetic baseline Y (same as _drawTextStraight ly). Arc lowest point θ=0 sits on this baseline. */
          const cy = slotCenter + (asc - desc) / 2;
          let glyphs;
          if (typeof Intl !== 'undefined' && Intl.Segmenter) {
            glyphs = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(line)].map(
              (x) => x.segment,
            );
          } else {
            glyphs = [];
            for (let i = 0; i < line.length; ) {
              const cp = line.codePointAt(i);
              const ch = String.fromCodePoint(cp);
              glyphs.push(ch);
              i += ch.length;
            }
          }
          const nG = glyphs.length;
          if (nG === 0) continue;

          const ls = Number(cfg.letterSpacing) || 0;
          const prefixW = [0];
          let accG = '';
          for (let g = 0; g < nG; g++) {
            accG += glyphs[g];
            prefixW.push(ctx.measureText(accG).width);
          }
          const totalW = prefixW[nG] + Math.max(0, nG - 1) * ls;
          if (totalW <= 0) continue;
          const halfW = totalW / 2;
          const absC = Math.max(0.02, Math.min(1, Math.abs(c)));
          const R = Math.max(8, zoneW / (2 * absC));
          const lim = Math.PI / 2 - 1e-4;
          const maxOff = R * Math.sin(lim);
          /** Horizontal offset d from arc center ⇒ angle θ with d = R·sin(θ); θ = asin(d/R). Same as old offset/R only for small angles. */
          const angFromOff = (offset) => {
            const d = Math.max(-maxOff, Math.min(maxOff, offset));
            return Math.asin(d / R);
          };
          const pxAt = (a) => cx + R * Math.sin(a);
          const pyAt = (a) => cy + sign * R * (1 - Math.cos(a));
          /** Arc midpoint angle between two horizontal offsets. */
          const midAngle = (offL, offR) => {
            const tL = angFromOff(offL);
            const tR = angFromOff(offR);
            return (tL + tR) / 2;
          };
          let skewM = Number(this.config.textCurveBaselineSkew);
          if (!Number.isFinite(skewM)) skewM = 1;
          skewM = Math.max(0, Math.min(2.5, skewM));
          /** Vertical skew dy/dx; stems upright. Strength × textCurveBaselineSkew. */
          const baselineSkewK = (a) => {
            const k = sign * Math.tan(a) * skewM;
            return Math.max(-3.5, Math.min(3.5, k));
          };

          if (debugCurve) {
            ctx.save();
            ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            const steps = 48;
            for (let s = 0; s <= steps; s++) {
              const offset = -halfW + (2 * halfW * s) / steps;
              const angle = angFromOff(offset);
              const px = pxAt(angle);
              const py = pyAt(angle);
              if (s === 0) ctx.moveTo(roundPx(px), roundPx(py));
              else ctx.lineTo(roundPx(px), roundPx(py));
            }
            ctx.stroke();
            ctx.restore();
          }

          for (let g = 0; g < nG; g++) {
            const leftFromStart = prefixW[g] + g * ls;
            const rightFromStart = prefixW[g + 1] + g * ls;
            const offsetL = leftFromStart - halfW;
            const offsetR = rightFromStart - halfW;
            const angle = midAngle(offsetL, offsetR);
            const px = pxAt(angle);
            const py = pyAt(angle);
            const k = baselineSkewK(angle);
            ctx.save();
            ctx.translate(px, py);
            ctx.transform(1, k, 0, 1, 0, 0);
            if (strokeW > 0) {
              ctx.lineWidth = Math.max(1, fs * strokeW);
              ctx.strokeText(glyphs[g], 0, 0);
            }
            ctx.fillText(glyphs[g], 0, 0);
            ctx.restore();
          }
        }
      }

      /** Same units as zone `skewX` / `skewY` (per-1000 in editor when |v|>1). */
      _readFieldPlacementSlotSkew(slot) {
        let skX = Number(slot?.skewX);
        let skY = Number(slot?.skewY);
        if (!Number.isFinite(skX)) skX = 0;
        if (!Number.isFinite(skY)) skY = 0;
        if (Math.abs(skX) > 1) skX /= 1000;
        if (Math.abs(skY) > 1) skY /= 1000;
        return { skX, skY };
      }

      _strokeFieldPlacementsDebug(ctx, w, h, dpr, fpCfg, lineTextsBySlot) {
        const slots = fpCfg?.slots;
        if (!Array.isArray(slots) || !lineTextsBySlot.length) return;
        const n = Math.min(lineTextsBySlot.length, slots.length);
        const smallCanvas = w < 200 || h < 200;
        const roundPx = (v) => (smallCanvas ? Math.round(v) : v);
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 160, 120, 0.9)';
        ctx.fillStyle = 'transparent';
        ctx.setLineDash([]);
        ctx.lineWidth = Math.max(1, (dpr || 1) * 1.1);
        for (let i = 0; i < n; i++) {
          const slot = slots[i] || {};
          let sx = Number(slot.x);
          let sy = Number(slot.y);
          let smw = Number(slot.maxWidth);
          if (!Number.isFinite(sx)) sx = 0.5;
          if (!Number.isFinite(sy)) sy = 0.5;
          if (!Number.isFinite(smw)) smw = 0.4;
          if (Math.abs(sx) > 1) sx /= 1000;
          if (Math.abs(sy) > 1) sy /= 1000;
          if (Math.abs(smw) > 1) smw /= 1000;
          const cx = sx * w;
          const cy = sy * h;
          const mw = Math.max(12, smw * w);
          const { skX, skY } = this._readFieldPlacementSlotSkew(slot);
          ctx.save();
          if (skX !== 0 || skY !== 0) {
            ctx.translate(roundPx(cx), roundPx(cy));
            if (skY !== 0 && skX !== 0) {
              ctx.transform(1, skY, skX, 1, 0, 0);
            } else {
              if (skY !== 0) ctx.transform(1, skY, 0, 1, 0, 0);
              if (skX !== 0) ctx.transform(1, 0, skX, 1, 0, 0);
            }
            ctx.translate(-roundPx(cx), -roundPx(cy));
          }
          ctx.beginPath();
          ctx.moveTo(roundPx(cx - mw / 2), roundPx(cy));
          ctx.lineTo(roundPx(cx + mw / 2), roundPx(cy));
          ctx.stroke();
          ctx.restore();
        }
        ctx.restore();
      }

      /**
       * Per-slot text: straight, centered at (x,y), shrink-to-fit maxWidth. Slot index matches Line 1…N inputs.
       */
      _drawFieldPlacementLines(
        ctx,
        w,
        h,
        lineTextsBySlot,
        primarySlotIndex,
        fontName,
        fpCfg,
        textCfgForDraw,
        nominalTextCfg,
        roundTxt,
        offsetX = 0,
        offsetY = 0,
      ) {
        const slots = fpCfg?.slots;
        if (!Array.isArray(slots) || !lineTextsBySlot.length) return;
        const defF = YO_ENGRAVING_PREVIEW_DEFAULTS.fieldPlacements;
        const hasPL = this._hasEngravingPrimaryLine();
        const secondaryFrac = Number.isFinite(Number(fpCfg?.fontSize)) ? Number(fpCfg.fontSize) : defF.fontSize;
        const primaryFrac = Number.isFinite(Number(fpCfg?.primaryFontSize))
          ? Number(fpCfg.primaryFontSize)
          : defF.primaryFontSize;
        const secondaryCapPx = roundTxt(h * secondaryFrac);
        const primaryCapPx = roundTxt(h * primaryFrac);
        const fontFamily =
          fontName && this._fontCache.has(fontName)
            ? `"${fontName}", ${textCfgForDraw.fallbackFont}`
            : textCfgForDraw.fallbackFont;
        const nSlot = Math.min(lineTextsBySlot.length, slots.length);
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = textCfgForDraw.fillColor;
        ctx.strokeStyle = textCfgForDraw.strokeColor;
        const blur = textCfgForDraw.shadowBlur ?? 0;
        if (blur > 0) {
          ctx.shadowColor = textCfgForDraw.shadowColor ?? 'rgba(0,0,0,0.3)';
          ctx.shadowOffsetX = textCfgForDraw.shadowOffsetX ?? 0;
          ctx.shadowOffsetY = textCfgForDraw.shadowOffsetY ?? 0;
          ctx.shadowBlur = blur;
        }
        const smallCanvas = w < 200 || h < 200;
        const roundPx = (v) => (smallCanvas ? Math.round(v) : v);
        const bandH = Math.max((hasPL ? Math.max(primaryCapPx, secondaryCapPx) : secondaryCapPx) * 2.8, h * 0.12);
        for (let i = 0; i < nSlot; i++) {
          const line = lineTextsBySlot[i];
          if (!String(line || '').trim()) continue;
          const slot = slots[i] || {};
          let sx = Number(slot.x);
          let sy = Number(slot.y);
          let smw = Number(slot.maxWidth);
          if (!Number.isFinite(sx)) sx = 0.5;
          if (!Number.isFinite(sy)) sy = 0.5;
          if (!Number.isFinite(smw)) smw = 0.4;
          if (Math.abs(sx) > 1) sx /= 1000;
          if (Math.abs(sy) > 1) sy /= 1000;
          if (Math.abs(smw) > 1) smw /= 1000;
          const cx = sx * w - offsetX;
          const cy = sy * h - offsetY;
          const maxW = Math.max(8, smw * w);
          const isPrimarySlot = this._isPrimarySlot(primarySlotIndex, i);
          const slotFontCap = hasPL && isPrimarySlot ? primaryCapPx : secondaryCapPx;
          const secCapPx = textCfgForDraw.secondaryMaxFontSize ?? slotFontCap;
          const lineCap = hasPL && isPrimarySlot ? slotFontCap : Math.min(slotFontCap, secCapPx);
          const slotCfg = {
            ...textCfgForDraw,
            primaryMaxFontSize: lineCap,
            secondaryMaxFontSize: lineCap,
            primaryMinFontSize: Math.max(2, lineCap * 0.12),
            secondaryMinFontSize: Math.max(2, lineCap * 0.12),
            primaryMaxWidth: 1,
            maxWidth: 1,
          };
          const nominalLine =
            nominalTextCfg && typeof nominalTextCfg === 'object'
              ? {
                  ...nominalTextCfg,
                  primaryMaxFontSize: lineCap,
                  secondaryMaxFontSize: lineCap,
                }
              : null;
          const lineSizes = this._computeTextLineSizesForLayout(
            ctx,
            [line],
            0,
            fontName,
            maxW,
            bandH,
            slotCfg,
            maxW,
            0,
            nominalLine,
          );
          const fs = lineSizes[0];
          ctx.font = `${fs}px ${fontFamily}`;
          const m = ctx.measureText(line);
          const asc = m.actualBoundingBoxAscent ?? fs * 0.8;
          const desc = m.actualBoundingBoxDescent ?? fs * 0.2;
          const ly = cy + (asc - desc) / 2;
          const { skX, skY } = this._readFieldPlacementSlotSkew(slot);
          const strokeW = textCfgForDraw.strokeWidth ?? 0;
          ctx.save();
          if (skX !== 0 || skY !== 0) {
            const px = roundPx(cx);
            const py = roundPx(ly);
            ctx.translate(px, py);
            if (skY !== 0 && skX !== 0) {
              ctx.transform(1, skY, skX, 1, 0, 0);
            } else {
              if (skY !== 0) ctx.transform(1, skY, 0, 1, 0, 0);
              if (skX !== 0) ctx.transform(1, 0, skX, 1, 0, 0);
            }
            ctx.translate(-px, -py);
          }
          if (strokeW > 0) {
            ctx.lineWidth = Math.max(1, fs * strokeW);
            ctx.strokeText(line, roundPx(cx), roundPx(ly));
          }
          ctx.fillText(line, roundPx(cx), roundPx(ly));
          ctx.restore();
        }
        ctx.restore();
      }

      /** @returns {{ ax: number, ay: number }} allocation box top-left (same as `_drawCompanionLayout` center art). */
      _companionArtAllocationRect(w, offsetX, comp, artDrawW, artDrawH, anchorCy) {
        const c = comp?.center;
        let cxA = Number(c?.x);
        if (!Number.isFinite(cxA)) cxA = 0.5;
        if (Math.abs(cxA) > 1) cxA /= 1000;
        const ax = cxA * w - offsetX - artDrawW / 2;
        const ay = anchorCy - artDrawH / 2;
        return { ax, ay };
      }

      /**
       * Yellow bands = each column's `maxWidth` × zone height (layout debug when companion is on).
       * Coordinates match `_drawCompanionLayout` (`offsetX` = `zoneX` offscreen, `0` on main canvas).
       */
      _strokeCompanionTextColumnBandsDebug(ctx, w, h, comp, zoneW, zoneH, anchorCy, offsetX) {
        if (!comp || typeof comp !== 'object') return;
        if (!Number.isFinite(anchorCy)) return;
        const widthScale = Number(zoneW) > 0 ? zoneW : w;
        const zh = Number(zoneH) > 0 ? zoneH : h;
        const bandH = Math.max(40, Math.min(zh, h) * 0.92);
        const top = anchorCy - bandH / 2;
        if (!Number.isFinite(top) || bandH <= 0) return;
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(255, 220, 0, 0.98)';
        ctx.setLineDash([10, 5]);
        for (const loc of ['left', 'center', 'right']) {
          const slot = comp[loc];
          if (!slot) continue;
          let sx = Number(slot.x);
          let smw = Number(slot.maxWidth);
          if (!Number.isFinite(sx)) sx = 0.5;
          if (!Number.isFinite(smw)) smw = 0.3;
          if (Math.abs(sx) > 1) sx /= 1000;
          if (Math.abs(smw) > 1) smw /= 1000;
          const cx = sx * w - offsetX;
          const maxW = Math.max(8, smw * widthScale);
          const left = cx - maxW / 2;
          if (!Number.isFinite(left)) continue;
          const sw = Math.max(1, maxW - 1);
          const sh = Math.max(1, bandH - 1);
          ctx.strokeRect(left + 0.5, top + 0.5, sw, sh);
        }
        ctx.setLineDash([]);
        ctx.restore();
      }

      /**
       * Text-lines companion: Left/Right columns — X + maxWidth, stack vertically centered on `anchorCy`.
       * Primary caps: `companion.primaryFontSizeMax` → left/right primary only; `companion.center.primaryFontSizeMax` → center primary (0 = theme default primary height, not the L/R cap).
       * Center column with artwork — the first `Math.floor(n/2)` center lines above the art, the rest below; gaps + art +
       * text stack centered on `anchorCy`. Center text-only (no art) uses the same vertical centering as side columns.
       * @param {number} offsetX subtract when drawing into zone-relative offscreen (e.g. zoneX).
       * @param {number} zoneW zone width in px (column maxWidth uses this; pass canvas `w` if not in zone space).
       * @param {number} anchorCy Y of zone center in current ctx coords (zone-relative: `zoneH/2`, canvas: `zoneY+zoneH/2`).
       */
      _drawCompanionLayout(
        ctx,
        w,
        h,
        dpr,
        cfg,
        lineTextsBySlot,
        lineLocationsBySlot,
        primarySlotIndex,
        fontName,
        textCfgForDraw,
        nominalTextCfg,
        roundTxt,
        artworkImageUrl,
        artDrawW,
        artDrawH,
        offsetX,
        offsetY,
        zoneW,
        anchorCy,
      ) {
        this._companionLayoutArtBounds = null;
        const comp = cfg.companion;
        if (!comp || typeof comp !== 'object') return;
        const defF = YO_ENGRAVING_PREVIEW_DEFAULTS.fieldPlacements;
        let primarySideFrac = Number(comp.primaryFontSizeMax);
        let secondaryFrac = Number(comp.secondaryFontSizeMax);
        if (!Number.isFinite(primarySideFrac) || primarySideFrac <= 0) primarySideFrac = defF.primaryFontSize;
        if (!Number.isFinite(secondaryFrac) || secondaryFrac <= 0) secondaryFrac = defF.fontSize;
        if (Math.abs(primarySideFrac) > 1) primarySideFrac /= 1000;
        const primarySideCapPx = roundTxt(h * primarySideFrac);
        const secondaryCapPx = roundTxt(h * secondaryFrac);
        let centerPrimaryFrac = Number(comp.center?.primaryFontSizeMax);
        if (!Number.isFinite(centerPrimaryFrac)) centerPrimaryFrac = 0;
        if (Math.abs(centerPrimaryFrac) > 1) centerPrimaryFrac /= 1000;
        const centerPrimaryCapPx =
          centerPrimaryFrac > 0 ? roundTxt(h * centerPrimaryFrac) : roundTxt(h * defF.primaryFontSize);
        const hasPL = this._hasEngravingPrimaryLine();
        const artImg = artworkImageUrl ? this._getArtworkImage(artworkImageUrl) : null;
        const hasCenterArt = Boolean(artImg && artDrawW > 0 && artDrawH > 0);

        const fontFamily =
          fontName && this._fontCache.has(fontName)
            ? `"${fontName}", ${textCfgForDraw.fallbackFont}`
            : textCfgForDraw.fallbackFont;

        const smallCanvas = w < 200 || h < 200;
        const roundPx = (v) => (smallCanvas ? Math.round(v) : v);
        const widthScale = Number(zoneW) > 0 ? zoneW : w;
        const readSlotX = (loc) => {
          const slot = comp[loc];
          if (!slot || typeof slot !== 'object') return { cx: 0.5 * w - offsetX, maxW: Math.max(8, 0.3 * widthScale) };
          let sx = Number(slot.x);
          let smw = Number(slot.maxWidth);
          if (!Number.isFinite(sx)) sx = 0.5;
          if (!Number.isFinite(smw)) smw = 0.3;
          if (Math.abs(sx) > 1) sx /= 1000;
          if (Math.abs(smw) > 1) smw /= 1000;
          return { cx: sx * w - offsetX, maxW: Math.max(8, smw * widthScale) };
        };
        let lineGapFrac = Number(comp.lineGap);
        let artworkGapFrac = Number(comp.artworkGap);
        if (!Number.isFinite(lineGapFrac)) lineGapFrac = 0.008;
        if (!Number.isFinite(artworkGapFrac)) artworkGapFrac = 0.014;
        if (Math.abs(lineGapFrac) > 1) lineGapFrac /= 1000;
        if (Math.abs(artworkGapFrac) > 1) artworkGapFrac /= 1000;
        const lineGap = roundTxt(Math.max(0, h * lineGapFrac));
        const midGap = roundTxt(Math.max(0, h * artworkGapFrac));

        const layoutLineMetrics = (line, origIdx, maxWCol, columnLoc) => {
          const isPrimarySlot = this._isPrimarySlot(primarySlotIndex, origIdx);
          const primaryCapHere = columnLoc === 'center' ? centerPrimaryCapPx : primarySideCapPx;
          const slotFontCap = hasPL && isPrimarySlot ? primaryCapHere : secondaryCapPx;
          const lineCap = slotFontCap;
          const minLineCap = Math.max(2, lineCap * 0.12);
          const slotCfg = {
            ...textCfgForDraw,
            primaryMaxFontSize: lineCap,
            secondaryMaxFontSize: lineCap,
            primaryMinFontSize: minLineCap,
            secondaryMinFontSize: minLineCap,
            primaryMaxWidth: 1,
            maxWidth: 1,
          };
          const nominalLine =
            nominalTextCfg && typeof nominalTextCfg === 'object'
              ? {
                  ...nominalTextCfg,
                  primaryMaxFontSize: lineCap,
                  secondaryMaxFontSize: lineCap,
                }
              : null;
          const bandH = Math.max(lineCap * 2.8, h * 0.12);
          const lineSizes = this._computeTextLineSizesForLayout(
            ctx,
            [line],
            0,
            fontName,
            maxWCol,
            bandH,
            slotCfg,
            maxWCol,
            0,
            nominalLine,
          );
          const fs = lineSizes[0];
          ctx.font = `${fs}px ${fontFamily}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'alphabetic';
          const m = ctx.measureText(line);
          const asc = m.actualBoundingBoxAscent ?? fs * 0.8;
          const desc = m.actualBoundingBoxDescent ?? fs * 0.2;
          return { fs, asc, desc };
        };

        const drawLineAtBaseline = (line, ly, fs, cxCol) => {
          ctx.save();
          ctx.font = `${fs}px ${fontFamily}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'alphabetic';
          ctx.fillStyle = textCfgForDraw.fillColor;
          ctx.strokeStyle = textCfgForDraw.strokeColor;
          const strokeW = textCfgForDraw.strokeWidth ?? 0;
          if (strokeW > 0) {
            ctx.lineWidth = Math.max(1, fs * strokeW);
            ctx.strokeText(line, roundPx(cxCol), roundPx(ly));
          }
          ctx.fillText(line, roundPx(cxCol), roundPx(ly));
          ctx.restore();
        };

        const inkStackHeight = (metricsArr) => {
          if (!metricsArr.length) return 0;
          const rel = [];
          rel[0] = metricsArr[0].asc;
          for (let j = 1; j < metricsArr.length; j++) {
            rel[j] = rel[j - 1] + metricsArr[j - 1].desc + lineGap + metricsArr[j].asc;
          }
          return rel[metricsArr.length - 1] + metricsArr[metricsArr.length - 1].desc;
        };

        const collectColumnLines = (loc) => {
          const linesHere = [];
          const indicesHere = [];
          for (let i = 0; i < lineTextsBySlot.length; i++) {
            const locSlot = lineLocationsBySlot[i] || 'center';
            if (locSlot !== loc) continue;
            const t = lineTextsBySlot[i];
            if (!String(t || '').trim()) continue;
            linesHere.push(t);
            indicesHere.push(i);
          }
          return { linesHere, indicesHere };
        };

        const drawLinesInColumn = (loc, linesHere, indicesHere) => {
          const { cx, maxW } = readSlotX(loc);
          if (!linesHere.length) return;

          const metrics = linesHere.map((line, j) => layoutLineMetrics(line, indicesHere[j], maxW, loc));
          const nL = metrics.length;
          const relBaseline = [];
          relBaseline[0] = metrics[0].asc;
          for (let j = 1; j < nL; j++) {
            relBaseline[j] = relBaseline[j - 1] + metrics[j - 1].desc + lineGap + metrics[j].asc;
          }
          const bottomRel = relBaseline[nL - 1] + metrics[nL - 1].desc;
          const midRel = bottomRel / 2;
          const yOff = anchorCy - midRel;
          for (let j = 0; j < nL; j++) {
            const line = linesHere[j];
            const { fs } = metrics[j];
            const ly = relBaseline[j] + yOff;
            drawLineAtBaseline(line, ly, fs, cx);
          }
        };

        const drawColumn = (loc) => {
          const { linesHere, indicesHere } = collectColumnLines(loc);
          drawLinesInColumn(loc, linesHere, indicesHere);
        };

        const drawCenterSandwich = (linesHere, indicesHere, splitIndex) => {
          const { cx, maxW } = readSlotX('center');
          const metricsAll = linesHere.map((line, j) => layoutLineMetrics(line, indicesHere[j], maxW, 'center'));
          const n = metricsAll.length;
          const nAbove = Number.isFinite(Number(splitIndex))
            ? Math.min(n, Math.max(0, Math.floor(Number(splitIndex))))
            : Math.floor(n / 2);
          const mAbove = metricsAll.slice(0, nAbove);
          const mBelow = metricsAll.slice(nAbove);
          const linesAbove = linesHere.slice(0, nAbove);
          const linesBelow = linesHere.slice(nAbove);
          const H_a = inkStackHeight(mAbove);
          const H_b = inkStackHeight(mBelow);
          const T = H_a + midGap + artDrawH + midGap + H_b;
          const Y_top = anchorCy - T / 2;

          if (mAbove.length) {
            const rel = [];
            rel[0] = mAbove[0].asc;
            for (let j = 1; j < mAbove.length; j++) {
              rel[j] = rel[j - 1] + mAbove[j - 1].desc + lineGap + mAbove[j].asc;
            }
            for (let j = 0; j < mAbove.length; j++) {
              drawLineAtBaseline(linesAbove[j], Y_top + rel[j], mAbove[j].fs, cx);
            }
          }

          const artTop = Y_top + H_a + midGap;
          let cxA = Number(comp.center?.x);
          if (!Number.isFinite(cxA)) cxA = 0.5;
          if (Math.abs(cxA) > 1) cxA /= 1000;
          const ax = cxA * w - offsetX - artDrawW / 2;
          this._companionLayoutArtBounds = { ax, ay: artTop, w: artDrawW, h: artDrawH };
          this._drawArtwork(ctx, artworkImageUrl, ax, artTop, artDrawW, artDrawH, dpr);

          if (mBelow.length) {
            const belowBase = artTop + artDrawH + midGap;
            const rel = [];
            rel[0] = mBelow[0].asc;
            for (let j = 1; j < mBelow.length; j++) {
              rel[j] = rel[j - 1] + mBelow[j - 1].desc + lineGap + mBelow[j].asc;
            }
            for (let j = 0; j < mBelow.length; j++) {
              drawLineAtBaseline(linesBelow[j], belowBase + rel[j], mBelow[j].fs, cx);
            }
          }
        };

        const { linesHere: topLines, indicesHere: topIdx } = collectColumnLines('top');
        const { linesHere: centerLines, indicesHere: centerIdx } = collectColumnLines('center');
        const { linesHere: bottomLines, indicesHere: bottomIdx } = collectColumnLines('bottom');
        const hasTopBottomText = topLines.length > 0 || bottomLines.length > 0;
        const centerSplitIndex = topLines.length + Math.floor(centerLines.length / 2);
        const centerStackLines = hasTopBottomText ? [...topLines, ...centerLines, ...bottomLines] : centerLines;
        const centerStackIdx = hasTopBottomText ? [...topIdx, ...centerIdx, ...bottomIdx] : centerIdx;
        const hasCenterText = centerStackLines.length > 0;

        const blur = textCfgForDraw.shadowBlur ?? 0;
        if (blur > 0) {
          ctx.shadowColor = textCfgForDraw.shadowColor ?? 'rgba(0,0,0,0.3)';
          ctx.shadowOffsetX = textCfgForDraw.shadowOffsetX ?? 0;
          ctx.shadowOffsetY = textCfgForDraw.shadowOffsetY ?? 0;
          ctx.shadowBlur = blur;
        }
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        drawColumn('left');
        drawColumn('right');
        if (hasCenterArt && hasCenterText) {
          drawCenterSandwich(centerStackLines, centerStackIdx, hasTopBottomText ? centerSplitIndex : undefined);
        } else {
          if (hasCenterArt) {
            let cxA = Number(comp.center?.x);
            if (!Number.isFinite(cxA)) cxA = 0.5;
            if (Math.abs(cxA) > 1) cxA /= 1000;
            const ax = cxA * w - offsetX - artDrawW / 2;
            const ay = anchorCy - artDrawH / 2;
            this._companionLayoutArtBounds = { ax, ay, w: artDrawW, h: artDrawH };
            this._drawArtwork(ctx, artworkImageUrl, ax, ay, artDrawW, artDrawH, dpr);
          }
          if (!hasCenterArt && hasCenterText) drawLinesInColumn('center', centerStackLines, centerStackIdx);
        }
        ctx.restore();
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      }

      _strokeCompanionDebug(ctx, w, h, dpr, comp, offsetX, offsetY, zoneW, anchorCy) {
        if (!comp || typeof comp !== 'object') return;
        const smallCanvas = w < 200 || h < 200;
        const roundPx = (v) => (smallCanvas ? Math.round(v) : v);
        const widthScale = Number(zoneW) > 0 ? zoneW : w;
        const tick = Math.max(18, Math.min(widthScale, h) * 0.06);
        ctx.save();
        ctx.strokeStyle = 'rgba(180, 90, 220, 0.95)';
        ctx.setLineDash([6, 4]);
        ctx.lineWidth = Math.max(1, (dpr || 1) * 1);
        const cy = Number.isFinite(Number(anchorCy)) ? Number(anchorCy) : offsetY + (h - offsetY) / 2;
        for (const loc of ['left', 'center', 'right']) {
          const slot = comp[loc];
          if (!slot) continue;
          let sx = Number(slot.x);
          let smw = Number(slot.maxWidth);
          if (!Number.isFinite(sx)) sx = 0.5;
          if (!Number.isFinite(smw)) smw = 0.3;
          if (Math.abs(sx) > 1) sx /= 1000;
          if (Math.abs(smw) > 1) smw /= 1000;
          const cx = sx * w - offsetX;
          const half = Math.max(8, smw * widthScale) / 2;
          ctx.beginPath();
          ctx.moveTo(roundPx(cx - half), roundPx(cy));
          ctx.lineTo(roundPx(cx + half), roundPx(cy));
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(roundPx(cx), roundPx(cy - tick));
          ctx.lineTo(roundPx(cx), roundPx(cy + tick));
          ctx.stroke();
        }
        ctx.restore();
      }

      /** Full-opacity guide; runs whenever live debug + checkbox (does not require circular text enabled). */
      _strokeCircularTextDebugCircle(ctx, w, h, dpr, circularCfg) {
        const { cx, cy, R } = this._normalizeCircularTextPlacement(circularCfg, w, h);
        const smallCanvas = w < 200 || h < 200;
        const roundPx = (n) => (smallCanvas ? Math.round(n) : n);
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 0, 90, 0.95)';
        ctx.fillStyle = 'transparent';
        ctx.setLineDash([8, 5]);
        ctx.lineWidth = Math.max(1.5, (dpr || 1) * 1.25);
        ctx.beginPath();
        ctx.arc(roundPx(cx), roundPx(cy), Math.max(2, roundPx(R)), 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }

      /** @returns {{ cx: number, cy: number, R: number }} Canvas-space circle (logical w×h). */
      _normalizeCircularTextPlacement(ct, w, h) {
        const minDim = Math.max(1, Math.min(w, h));
        let cxN = Number(ct?.centerX);
        let cyN = Number(ct?.centerY);
        let rN = Number(ct?.radius);
        if (!Number.isFinite(cxN)) cxN = 0.5;
        if (!Number.isFinite(cyN)) cyN = 0.5;
        if (!Number.isFinite(rN)) rN = 0.18;
        if (Math.abs(cxN) > 1) cxN /= 1000;
        if (Math.abs(cyN) > 1) cyN /= 1000;
        if (Math.abs(rN) > 1) rN /= 1000;
        cxN = Math.min(1, Math.max(0, cxN));
        cyN = Math.min(1, Math.max(0, cyN));
        rN = Math.min(0.49, Math.max(0.02, rN));
        const R = rN * minDim;
        return { cx: cxN * w, cy: cyN * h, R: Math.max(8, R) };
      }

      /**
       * Line 0 along top semicircle, line 1 along bottom (left→right). Replaces in-zone text when enabled.
       * One font size for both lines; line 1 rotated +π so glyphs stay upright like line 0.
       */
      _drawCircularZoneText(ctx, lines, fontName, w, h, textCfg, circularCfg, circularFontPx) {
        const ct = circularCfg || {};
        const { cx, cy, R } = this._normalizeCircularTextPlacement(ct, w, h);
        const smallCanvas = w < 200 || h < 200;
        const roundPx = (n) => (smallCanvas ? Math.round(n) : n);

        const fontFamily =
          fontName && this._fontCache.has(fontName) ? `"${fontName}", ${textCfg.fallbackFont}` : textCfg.fallbackFont;
        const lineIndices = lines.length > 1 ? [0, 1] : [0];
        const arcBudget = Math.PI * R * 0.92;
        const strokeW = textCfg.strokeWidth ?? 0;
        const minFsCircular = Math.max(4, textCfg.primaryMinFontSize ?? h * 0.015);

        const segmentGlyphs = (line) => {
          if (typeof Intl !== 'undefined' && Intl.Segmenter) {
            return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(line)].map((x) => x.segment);
          }
          const out = [];
          for (let i = 0; i < line.length; ) {
            const cp = line.codePointAt(i);
            out.push(String.fromCodePoint(cp));
            i += String.fromCodePoint(cp).length;
          }
          return out;
        };

        ctx.save();
        ctx.textAlign = 'center';
        /** Middle: vertical center of each glyph sits on the circle (not the alphabetic baseline). */
        ctx.textBaseline = 'middle';
        ctx.fillStyle = textCfg.fillColor;
        ctx.strokeStyle = textCfg.strokeColor;

        for (const lineIdx of lineIndices) {
          const line = lines[lineIdx] || '';
          if (!line) continue;
          let fs = Math.max(minFsCircular, Number(circularFontPx) || 0);
          const minFs = minFsCircular;
          const glyphs = segmentGlyphs(line);
          const nG = glyphs.length;
          if (nG === 0) continue;
          const ls = Number(textCfg.letterSpacing) || 0;

          const measureTotalW = (size) => {
            ctx.font = `${size}px ${fontFamily}`;
            let accG = '';
            const prefixW = [0];
            for (let g = 0; g < nG; g++) {
              accG += glyphs[g];
              prefixW.push(ctx.measureText(accG).width);
            }
            return prefixW[nG] + Math.max(0, nG - 1) * ls;
          };

          let totalW = measureTotalW(fs);
          while (fs > minFs && totalW > arcBudget) {
            fs *= 0.97;
            totalW = measureTotalW(fs);
          }
          fs = Math.max(minFs, fs);
          ctx.font = `${fs}px ${fontFamily}`;

          let accG = '';
          const prefixW = [0];
          for (let g = 0; g < nG; g++) {
            accG += glyphs[g];
            prefixW.push(ctx.measureText(accG).width);
          }
          totalW = prefixW[nG] + Math.max(0, nG - 1) * ls;
          const halfW = totalW / 2;

          const phiCenter = lineIdx === 0 ? (3 * Math.PI) / 2 : Math.PI / 2;
          const phiSign = lineIdx === 0 ? 1 : -1;

          for (let g = 0; g < nG; g++) {
            const leftFromStart = prefixW[g] + g * ls;
            const rightFromStart = prefixW[g + 1] + g * ls;
            const s = (leftFromStart + rightFromStart) / 2 - halfW;
            const phi = phiCenter + (phiSign * s) / R;
            const px = cx + R * Math.cos(phi);
            const py = cy + R * Math.sin(phi);
            const tnx = -Math.sin(phi);
            const tny = Math.cos(phi);
            let angle = Math.atan2(tny, tnx);
            if (lineIdx === 1) angle += Math.PI;
            ctx.save();
            ctx.translate(roundPx(px), roundPx(py));
            ctx.rotate(angle);
            if (strokeW > 0) {
              ctx.lineWidth = Math.max(1, fs * strokeW);
              ctx.strokeText(glyphs[g], 0, 0);
            }
            ctx.fillText(glyphs[g], 0, 0);
            ctx.restore();
          }
        }
        ctx.restore();
      }

      /**
       * Build the exported image (canvas + optional background) as a PNG Blob.
       * Renders engraving at export resolution to avoid blur from scaling up the preview.
       * @returns {Promise<Blob|null>}
       */
      async _getExportBlob() {
        const canvas = this.canvas;
        if (!canvas) return null;
        this._setupCanvas();
        this.config = this._buildConfig();
        const activeItem = canvas.closest('.yo-engraving-preview__item');
        const activeImg = activeItem?.querySelector('img');
        const exportW = this.config.export.width;
        const aspect = canvas.height / canvas.width;
        const exportH = Math.round(exportW * aspect);

        const { lines, primaryIndex, font, artworkImageUrl } = this._getFormState();
        if (font) await this._loadFont(font);
        const bgSrc = this._getBackgroundImageSourceFromConfig(this.config);
        if (bgSrc) await this._ensureBackgroundImage(bgSrc);

        const bgImg = await new Promise((resolve) => {
          if (!activeImg) {
            resolve(null);
            return;
          }
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = activeImg.currentSrc || activeImg.src;
        });

        const offscreen = document.createElement('canvas');
        offscreen.width = exportW;
        offscreen.height = exportH;
        const octx = offscreen.getContext('2d');
        if (!octx) return null;
        if (bgImg) {
          const imgW = bgImg.naturalWidth;
          const imgH = bgImg.naturalHeight;
          const scale = Math.min(exportW / imgW, exportH / imgH);
          const dw = imgW * scale;
          const dh = imgH * scale;
          octx.drawImage(bgImg, (exportW - dw) / 2, (exportH - dh) / 2, dw, dh);
        }
        // Match the live-preview coordinate space first, then scale to export resolution:
        // - preview uses w/h in logical (CSS) pixels and then applies ctx.setTransform(this._dpr)
        // - export uses an offscreen canvas with pixel dimensions exportW/exportH
        // We keep the same logical w/h as preview, and scale by exportW/previewW so geometry
        // (including skew/shear about zone center) lands in the same place.
        const previewDpr = this._dpr ?? 1;
        const previewW = this._canvasW ?? canvas.width / previewDpr;
        const previewH = this._canvasH ?? canvas.height / previewDpr;
        const exportDpr = exportW / previewW;
        this._draw(lines, primaryIndex, font, artworkImageUrl, {
          ctx: octx,
          w: previewW,
          h: previewH,
          dpr: exportDpr,
          noClear: true,
        });

        return new Promise((resolve) => {
          offscreen.toBlob((blob) => resolve(blob), 'image/png', 1);
        });
      }

      async _testUpload() {
        const parent = this.closest('yo-engraving');
        const uploadUrl = ((parent && parent.dataset.uploadUrl) || '').trim();
        if (!uploadUrl) {
          console.warn(
            `${this.ns} Test upload: no data-upload-url on parent <yo-engraving>. Set "Engraving image upload API" in section settings.`,
          );
          return;
        }
        try {
          const blob = await this._getExportBlob();
          if (!blob) {
            console.warn(`${this.ns} Test upload: no export blob`);
            return;
          }
          const fd = new FormData();
          fd.append('file', blob, 'engraving-preview.png');
          const res = await fetch(uploadUrl, { method: 'POST', body: fd });
          const data = await (res.ok ? res.json() : Promise.reject(new Error(res.statusText)));
          const url = data && (data.url ?? data.image_url ?? data.file_url);
          console.log(`${this.ns} Test upload – Shopify file URL:`, url);
          if (data) console.log(`${this.ns} Test upload – full response:`, data);
        } catch (err) {
          console.warn(`${this.ns} Test upload failed`, err);
        }
      }

      /**
       * Export image: download or upload depending on config.export.action.
       * For upload, POSTs blob to config.export.uploadUrl (FormData key 'file'),
       * expects JSON with URL at config.export.uploadResponseUrlKey.
       */
      _downloadImage() {
        const exp = this.config.export;
        this._getExportBlob().then((blob) => {
          if (!blob) return;
          if (exp.action === 'download') {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = 'engraving-preview.png';
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
            return;
          }
          if (exp.action === 'upload' && exp.uploadUrl) {
            const form = new FormData();
            form.append('file', blob, 'engraving-preview.png');
            fetch(exp.uploadUrl, {
              method: 'POST',
              body: form,
            })
              .then((res) => (res.ok ? res.json() : Promise.reject(new Error(res.statusText))))
              .then((data) => {
                const url = data && data[exp.uploadResponseUrlKey];
                if (url) this.dispatchEvent(new CustomEvent('yo-engraving-export-uploaded', { detail: { url } }));
              })
              .catch((err) => {
                this.dispatchEvent(new CustomEvent('yo-engraving-export-error', { detail: { error: err } }));
              });
          }
        });
      }

      updatePreview(options = {}) {
        const hasCanvas = !!this.querySelector('.yo-engraving-preview__canvas');
        if (!hasCanvas) return;
        this.config = this._buildConfig();
        const bgSrc = this._getBackgroundImageSourceFromConfig(this.config);
        const allowDraw = this._livePreviewEnabled || Boolean(bgSrc);
        if (!allowDraw) return;
        if (!this._resizeObserver) this._initCanvases();

        if (this._drawScheduled) cancelAnimationFrame(this._drawScheduled);
        if (this._loadingShowTimer) clearTimeout(this._loadingShowTimer);
        const quiet = Boolean(options?.quiet || options?.immediate);
        const requestId = ++this._previewRequestId;
        if (quiet) this._setLoadingState(false);
        else this._setLoadingState(true);
        this._drawScheduled = requestAnimationFrame(async () => {
          this._drawScheduled = null;
          try {
            const { lines, primaryIndex, font, artworkImageUrl } = this._getFormState();
            const cfg = this._buildConfig();
            const bgUrl = this._getBackgroundImageSourceFromConfig(cfg);
            await this._preparePreviewAssets(font, artworkImageUrl, bgUrl);
            if (requestId !== this._previewRequestId) return;
            this._draw(lines, primaryIndex, font, artworkImageUrl);
            if (window.matchMedia('(max-width: 1023px)').matches && this.classList.contains('is-text-focused')) {
              this.setTextFocusState(true);
              this.closest('yo-engraving')?._centerPreviewForTypingZoom?.();
            }
          } catch (err) {
            console.warn(`${this.ns} updatePreview draw failed`, err);
          } finally {
            if (!quiet && requestId === this._previewRequestId) this._setLoadingState(false);
          }
        });
      }

      getExportBlob() {
        return this._getExportBlob();
      }
    },
  );
